import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useSearchParams } from 'react-router-dom';
import { TbEyeglass2 } from 'react-icons/tb';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import MoveToInboxRoundedIcon from '@mui/icons-material/MoveToInboxRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import JoinInnerRoundedIcon from '@mui/icons-material/JoinInnerRounded';
import FolderCopyRoundedIcon from '@mui/icons-material/FolderCopyRounded';
import LensBlurRoundedIcon from '@mui/icons-material/LensBlurRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded';
import AddAPhotoRoundedIcon from '@mui/icons-material/AddAPhotoRounded';
import BuildCircleRoundedIcon from '@mui/icons-material/BuildCircleRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Fragment, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import {
  getCollection,
  getCustomerLabel,
  getId,
  getLabel,
  getSiteLabel,
  getStockItemLabel,
  getUserLabel,
  requestBlob,
  requestJson,
  stripEmptyValues,
  userHasSite,
} from '../lib/api';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { useSiteSession } from '../context/SiteSessionContext';
import { getSocket } from '../lib/socket';
import { playActionSound } from '../lib/sound';
import { useMenuPage } from '../hooks/useMenuPage';
import { useCoalescedRefresh } from '../hooks/useCoalescedRefresh';

function CardSectionTitle({ icon, children, color = 'primary.main' }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: 1.5,
          display: 'grid',
          placeItems: 'center',
          color,
          bgcolor: 'rgba(102, 178, 255, 0.12)',
          flex: '0 0 auto',
          '& .MuiSvgIcon-root': { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
        {children}
      </Typography>
    </Stack>
  );
}

const CODE_39_PATTERNS = {
  '0': 'nnnwwnwnn', '1': 'wnnwnnnnw', '2': 'nnwwnnnnw', '3': 'wnwwnnnnn',
  '4': 'nnnwwnnnw', '5': 'wnnwwnnnn', '6': 'nnwwwnnnn', '7': 'nnnwnnwnw',
  '8': 'wnnwnnwnn', '9': 'nnwwnnwnn', A: 'wnnnnwnnw', B: 'nnwnnwnnw',
  C: 'wnwnnwnnn', D: 'nnnnwwnnw', E: 'wnnnwwnnn', F: 'nnwnwwnnn',
  G: 'nnnnnwwnw', H: 'wnnnnwwnn', I: 'nnwnnwwnn', J: 'nnnnwwwnn',
  K: 'wnnnnnnww', L: 'nnwnnnnww', M: 'wnwnnnnwn', N: 'nnnnwnnww',
  O: 'wnnnwnnwn', P: 'nnwnwnnwn', Q: 'nnnnnnwww', R: 'wnnnnnwwn',
  S: 'nnwnnnwwn', T: 'nnnnwnwwn', U: 'wwnnnnnnw', V: 'nwwnnnnnw',
  W: 'wwwnnnnnn', X: 'nwnnwnnnw', Y: 'wwnnwnnnn', Z: 'nwwnwnnnn',
  '-': 'nwnnnnwnw', '*': 'nwnnwnwnn',
};

function getDispatchBarcodeSvg(value) {
  const normalized = String(value || '').toUpperCase().replace(/[^0-9A-Z-]/g, '');
  const payload = `*${normalized}*`;
  const narrow = 2;
  const wide = 5;
  const quietZone = 12;
  let x = quietZone;
  const bars = [];

  [...payload].forEach((character) => {
    const pattern = CODE_39_PATTERNS[character];
    if (!pattern) return;
    [...pattern].forEach((widthType, index) => {
      const width = widthType === 'w' ? wide : narrow;
      if (index % 2 === 0) bars.push(`<rect x="${x}" y="4" width="${width}" height="44" fill="#111827"/>`);
      x += width;
    });
    x += narrow;
  });
  const width = x + quietZone;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} 64" role="img" aria-label="Code barre ${normalized}" preserveAspectRatio="none"><rect width="100%" height="100%" fill="#ffffff"/>${bars.join('')}<text x="${width / 2}" y="60" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" font-weight="700" fill="#111827">${normalized}</text></svg>`;
}

function NonConformityMarker({ type, count = 1 }) {
  const isStore = type === 'NCM';
  return (
    <Tooltip title={`${count} ${type} active${count > 1 ? 's' : ''} • ${isStore ? 'déclarée magasin' : 'déclarée atelier'}`}>
      <Box
        component="span"
        sx={{
          position: 'relative',
          width: 24,
          height: 24,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isStore ? 'primary.main' : 'secondary.main',
        }}
      >
        {isStore ? <StorefrontRoundedIcon sx={{ fontSize: 21 }} /> : <BuildCircleRoundedIcon sx={{ fontSize: 21 }} />}
        <Box
          component="span"
          sx={{
            position: 'absolute',
            right: -4,
            bottom: -3,
            width: 14,
            height: 14,
            borderRadius: 0.75,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'background.paper',
            color: isStore ? 'primary.main' : 'secondary.main',
            border: '1px solid',
            borderColor: 'divider',
            '& .MuiSvgIcon-root': { fontSize: 10 },
          }}
        >
          <WarningAmberIcon />
        </Box>
      </Box>
    </Tooltip>
  );
}

function csvEscape(value) {
  const text = value == null ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadCsv(filename, rows) {
  const content = rows.map((row) => row.map(csvEscape).join(';')).join('\n');
  const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const emptyForm = {
  jobNumber: '',
  workshopSheetNumber: '',
  process: 'STOCK_LENS_STORE',
  storeSiteId: '',
  workshopSiteId: '',
  customerId: '',
  customerFullName: '',
  advisorId: '',
  advisorFullName: '',
  frameDisplayLabel: '',
  mountingType: '',
  frameSentToWorkshop: '',
  stockItemId: '',
  lensSupplier: 'ESSILOR',
  lensQuantity: 1,
  orderedAt: '',
  preparedAt: '',
  shippedAt: '',
  printLabel: '',
  isPrinted: false,
  lensMode: 'stock',
  frameType: 'new',
  mountingLocation: 'store',
};

const emptyTraceabilityForm = {
  checks: {
    differences: false,
    heights: false,
    damagedFrame: false,
    insufficientLensDiameters: false,
    lensFrameMismatch: false,
    mountingImpossible: false,
    other: false,
  },
  pupillaryDifferences: {
    orderRight: '',
    orderLeft: '',
    realizationRight: '',
    realizationLeft: '',
  },
  heights: {
    orderRight: '',
    orderLeft: '',
    realizationRight: '',
    realizationLeft: '',
  },
  shapeProblem: '',
  lensFrameMismatchDetails: {
    material: '',
    base: '',
    thickness: '',
  },
  otherComment: '',
  comment: '',
  mountingCompleted: '',
};

const traceabilityCheckLabels = {
  differences: 'Écarts à vérifier',
  heights: 'Hauteurs à vérifier',
  damagedFrame: 'Monture abîmée',
  insufficientLensDiameters: 'Diamètres verres insuffisants',
  lensFrameMismatch: 'Inadéquation verres / monture',
  mountingImpossible: 'Montage impossible',
  other: 'Autres',
};

const STOCK_PROCESS_CODES = ['STOCK_LENS_STORE', 'STOCK_LENS_WORKSHOP', 'STOCK_LENS_RAW_BL'];
const WORKSHOP_SEND_STEP_CODES = ['ATELIER_PRET_ENVOI_MAGASIN', 'ATELIER_PRET_ENVOI_VERRE_MAGASIN'];
const WORKSHOP_TO_SEND_STEP_CODES = ['ATELIER_ENVOI_LUNETTE_MAGASIN', 'ATELIER_ENVOI_VERRE_MAGASIN'];
const WORKSHOP_MOUNTING_TYPE_ACTION_CODES = ['WORKSHOP_SEND', 'WORKSHOP_SEND_STOCK_LENS'];
const STORE_PHYSICAL_SEND_TO_WORKSHOP_STEP_CODES = [
  'MAGASIN_ENVOI_DOSSIER_MONTURE',
  'MAGASIN_DOSSIER_INFORMATION',
  'MAGASIN_ENVOI_DOSSIER_MONTURE_VERRE',
  'MAGASIN_ENVOI_MONTURE',
];
const STORE_TO_RECEIVE_STEP_CODES = [
  'MAGASIN_RECEPTION_DOSSIER_COMPLET',
  'MAGASIN_RECEPTION_VERRE',
  // Compatibilité des JOB existants: la réception de leur BL final les clôture.
  'MAGASIN_MONTAGE',
];
// Types de dossier pour lesquels l'origine de la monture peut être renseignée.
const FRAME_ORIGIN_TYPES = ['new', 'retailing', 'daily'];
const NON_STOCK_STORE_MOUNT_PROCESS_CODES = [
  'NEW_FRAME_NON_STOCK_LENS_STORE_MOUNT',
  'EXISTING_FRAME_NON_STOCK_LENS_STORE_MOUNT',
];
const DEFAULT_MOUNTING_TYPE_OPTIONS = [
  'T.D.',
  'Classique',
  'Easy-Digifit',
  'Percées',
  'V.S.',
  '+Gravure',
  'Clip',
  'Traçabilité',
];
const HIDDEN_MOUNTING_TYPE_OPTIONS = new Set(['N.C.A']);

const DEFAULT_NON_CONFORMITY_SCOPE_OPTIONS = [
  { value: 'right_lens', label: 'Verre droit' },
  { value: 'left_lens', label: 'Verre gauche' },
  { value: 'frame', label: 'Monture' },
  { value: 'other', label: 'Autres' },
];

const DEFAULT_NON_CONFORMITY_REASON_OPTIONS = [
  { value: 'scratch', label: 'Rayure(s)', scopes: ['right_lens', 'left_lens'] },
  { value: 'chip', label: 'Éclat(s)', scopes: ['right_lens', 'left_lens'] },
  { value: 'defect', label: 'Défaut(s)', scopes: ['right_lens', 'left_lens', 'frame', 'other'] },
  { value: 'mounting', label: 'Montage', scopes: ['right_lens', 'left_lens', 'frame'] },
  { value: 'other', label: 'Autres', scopes: ['right_lens', 'left_lens', 'frame', 'other'] },
];

const WORKSHOP_NON_CONFORMITY_FILTER_OPTIONS = [
  'NCA',
  'NCM',
  'Traçabilité (montage impossible)',
  'Traçabilité (montage non)',
  'Traçabilité (montage fait)',
];

function getOptionLabelByValue(options, value) {
  return options.find((option) => option.value === value)?.label || value || '—';
}

function hasNonConformity(row) {
  return (row?.nonConformities || []).some((item) => item.status !== 'cancelled');
}

function getActiveNonConformitiesByType(row, type) {
  return (row?.nonConformities || []).filter(
    (item) => item.status !== 'cancelled' && (item.type || 'NCA') === type
  );
}

function hasMountingImpossibleAlert(row) {
  return (row?.workflowAlerts || []).some((item) => item.type === 'mounting_impossible');
}

function hasTraceabilityReport(row) {
  return (row?.traceabilityReports || []).length > 0;
}

function isUpdatedAtInRange(value, from, to) {
  if (!from && !to) return true;
  const timestamp = new Date(value || 0).getTime();
  if (!Number.isFinite(timestamp) || timestamp <= 0) return false;
  const fromTimestamp = from ? new Date(`${from}T00:00:00`).getTime() : -Infinity;
  const toTimestamp = to ? new Date(`${to}T23:59:59.999`).getTime() : Infinity;
  return timestamp >= fromTimestamp && timestamp <= toTimestamp;
}

function matchesWorkshopNonConformityFilter(row, filter) {
  const normalizedFilter = String(filter || '').toLocaleLowerCase('fr-FR');
  if (normalizedFilter === 'nca') return getActiveNonConformitiesByType(row, 'NCA').length > 0;
  if (normalizedFilter === 'ncm') return getActiveNonConformitiesByType(row, 'NCM').length > 0;

  const reports = row?.traceabilityReports || [];
  if (normalizedFilter.includes('montage impossible')) {
    return hasMountingImpossibleAlert(row) || reports.some((report) => report.checks?.mountingImpossible === true);
  }
  if (normalizedFilter.includes('montage non')) {
    return reports.some((report) => report.mountingCompleted === false);
  }
  if (normalizedFilter.includes('montage fait')) {
    return reports.some((report) => report.mountingCompleted === true);
  }
  return false;
}

function hasMissingInitialFile(row) {
  return Boolean(row?.missingInitialFile?.detectedAt);
}

function hasPendingMissingInitialFile(row) {
  return hasMissingInitialFile(row) && !row?.missingInitialFile?.resolvedAt;
}

function isStockCommande(row) {
  return row?.lens?.mode === 'stock' || STOCK_PROCESS_CODES.includes(row?.process);
}

function isWorkshopSendStep(step) {
  return WORKSHOP_SEND_STEP_CODES.includes(step?.code);
}

function getCommandesViewConfig(viewMode) {
  if (viewMode === 'nonStock') {
    return {
      title: 'Suivi atelier',
      descriptionAdmin: 'Suivi des dossiers verre fournisseur et verre de stock.',
      descriptionWorkshop: (site) => `Process atelier affectés à ${site?.name || 'cet atelier'}.`,
      descriptionStore: (site) => `Suivi des dossiers créés par ${site?.name || 'ce magasin'}.`,
      addLabel: 'Ajouter un suivi',
      defaultLensMode: 'supplier',
    };
  }

  return {
    title: 'Suivi - Verres de stock',
    descriptionAdmin: 'Suivi des dossiers utilisant des verres de stock.',
    descriptionWorkshop: (site) => `Suivi des verres de stock affectés à ${site?.name || 'cet atelier'}.`,
    descriptionStore: (site) => `Suivi des verres de stock créés par ${site?.name || 'ce magasin'}.`,
    addLabel: 'Ajouter un suivi',
    defaultLensMode: 'stock',
  };
}

const workflowOptions = {
  NEW_FRAME_NON_STOCK_LENS: {
    label: 'Simple Boucle - verre non stock',
    status: 'À envoyer',
    steps: [
      { code: 'MAGASIN_ENVOI_DOSSIER_MONTURE', label: 'Envoi dossier', ownerType: 'store' },
      { code: 'ATELIER_RECEPTION_DOSSIER', label: 'Réception dossier', ownerType: 'workshop' },
      { code: 'ATELIER_RECEPTION_VERRE', label: 'Réception verre', ownerType: 'workshop' },
      { code: 'ATELIER_RAPPROCHEMENT_MONTAGE', label: 'Rapprochement / montage', ownerType: 'workshop' },
      { code: 'ATELIER_PRET_ENVOI_MAGASIN', label: 'À envoyer magasin', ownerType: 'workshop' },
      { code: 'ATELIER_ENVOI_LUNETTE_MAGASIN', label: 'Envoi magasin', ownerType: 'workshop' },
      { code: 'MAGASIN_RECEPTION_DOSSIER_COMPLET', label: 'Réception finale', ownerType: 'store' },
    ],
  },
  NEW_FRAME_NON_STOCK_LENS_STORE_MOUNT: {
    label: 'Simple Boucle - verre non stock - montage magasin',
    status: 'À envoyer',
    steps: [
      { code: 'MAGASIN_ENVOI_DOSSIER_MONTURE', label: 'Envoi dossier', ownerType: 'store' },
      { code: 'ATELIER_RECEPTION_DOSSIER', label: 'Réception dossier', ownerType: 'workshop' },
      { code: 'ATELIER_RECEPTION_VERRE', label: 'Réception verre', ownerType: 'workshop' },
      { code: 'ATELIER_ENVOI_VERRE_MAGASIN', label: 'Envoi verre', ownerType: 'workshop' },
      { code: 'MAGASIN_RECEPTION_VERRE', label: 'Réception finale', ownerType: 'store' },
    ],
  },
  EXISTING_FRAME_NON_STOCK_LENS: {
    label: 'Deuxième Boucle - verre non stock',
    status: 'Commande',
    steps: [
      { code: 'MAGASIN_DOSSIER_INFORMATION', label: 'Commande', ownerType: 'store' },
      { code: 'ATELIER_RECEPTION_COMMANDE', label: 'Réception commande', ownerType: 'workshop' },
      { code: 'ATELIER_RECEPTION_VERRE', label: 'Réception verre', ownerType: 'workshop' },
      { code: 'ATELIER_RAPPROCHEMENT_MONTAGE', label: 'Rapprochement / montage', ownerType: 'workshop' },
      { code: 'ATELIER_PRET_ENVOI_VERRE_MAGASIN', label: 'À envoyer magasin', ownerType: 'workshop' },
      { code: 'ATELIER_ENVOI_VERRE_MAGASIN', label: 'Envoi verre', ownerType: 'workshop' },
      { code: 'MAGASIN_RECEPTION_VERRE', label: 'Réception verre', ownerType: 'store' },
      { code: 'MAGASIN_ENVOI_DOSSIER_MONTURE_VERRE', label: 'Envoi monture/verre', ownerType: 'store' },
      { code: 'ATELIER_RECEPTION_DOSSIER', label: 'Réception dossier', ownerType: 'workshop' },
      { code: 'ATELIER_MONTAGE_RAPPROCHEMENT', label: 'Rapprochement', ownerType: 'workshop' },
      { code: 'ATELIER_PRET_ENVOI_MAGASIN', label: 'À envoyer magasin', ownerType: 'workshop' },
      { code: 'ATELIER_ENVOI_LUNETTE_MAGASIN', label: 'Envoi magasin', ownerType: 'workshop' },
      { code: 'MAGASIN_RECEPTION_DOSSIER_COMPLET', label: 'Réception finale', ownerType: 'store' },
    ],
  },
  EXISTING_FRAME_NON_STOCK_LENS_STORE_MOUNT: {
    label: 'Deuxième Boucle - verre non stock - montage magasin (historique)',
    status: 'Commande',
    steps: [
      { code: 'MAGASIN_DOSSIER_INFORMATION', label: 'Commande', ownerType: 'store' },
      { code: 'ATELIER_RECEPTION_COMMANDE', label: 'Réception commande', ownerType: 'workshop' },
      { code: 'ATELIER_RECEPTION_VERRE', label: 'Réception verre', ownerType: 'workshop' },
      { code: 'ATELIER_ENVOI_VERRE_MAGASIN', label: 'Envoi verre', ownerType: 'workshop' },
      { code: 'MAGASIN_RECEPTION_VERRE', label: 'Réception verre', ownerType: 'store' },
    ],
  },
  RETAILLAGE: {
    label: 'Retaillage - montage atelier',
    status: 'À envoyer',
    steps: [
      { code: 'MAGASIN_ENVOI_DOSSIER_MONTURE', label: 'Envoi dossier', ownerType: 'store' },
      { code: 'ATELIER_RECEPTION_DOSSIER', label: 'Réception dossier', ownerType: 'workshop' },
      { code: 'ATELIER_PRET_ENVOI_MAGASIN', label: 'À envoyer magasin', ownerType: 'workshop' },
      { code: 'ATELIER_ENVOI_LUNETTE_MAGASIN', label: 'Envoi magasin', ownerType: 'workshop' },
      { code: 'MAGASIN_RECEPTION_DOSSIER_COMPLET', label: 'Réception finale', ownerType: 'store' },
    ],
  },
  CLIP: {
    label: 'Clip - montage atelier',
    status: 'À envoyer',
    steps: [
      { code: 'MAGASIN_ENVOI_DOSSIER_MONTURE', label: 'Envoi dossier', ownerType: 'store' },
      { code: 'ATELIER_RECEPTION_DOSSIER', label: 'Réception dossier', ownerType: 'workshop' },
      { code: 'ATELIER_PRET_ENVOI_MAGASIN', label: 'À envoyer magasin', ownerType: 'workshop' },
      { code: 'ATELIER_ENVOI_LUNETTE_MAGASIN', label: 'Envoi magasin', ownerType: 'workshop' },
      { code: 'MAGASIN_RECEPTION_DOSSIER_COMPLET', label: 'Réception finale', ownerType: 'store' },
    ],
  },
  STOCK_LENS_STORE: {
    label: 'Verre de stock - process magasin',
    status: 'À envoyer',
    steps: [
      { code: 'MAGASIN_CONSULTE_COMMANDE_STOCK', label: 'Commande', ownerType: 'store' },
      { code: 'ATELIER_RECUPERE_VERRE_STOCK', label: 'Picking verre', ownerType: 'workshop' },
      { code: 'ATELIER_PRET_ENVOI_VERRE_MAGASIN', label: 'Envoi verre', ownerType: 'workshop' },
      { code: 'ATELIER_ENVOI_VERRE_MAGASIN', label: 'Envoi verre au magasin', ownerType: 'workshop' },
      { code: 'MAGASIN_RECEPTION_VERRE', label: 'Réception finale', ownerType: 'store' },
    ],
  },
  STOCK_LENS_WORKSHOP: {
    label: 'Verre de stock - process atelier',
    status: 'À envoyer',
    steps: [
      { code: 'MAGASIN_CONSULTE_COMMANDE_STOCK', label: 'Commande', ownerType: 'store' },
      { code: 'MAGASIN_ENVOI_MONTURE', label: 'Envoi monture', ownerType: 'store' },
      { code: 'ATELIER_RECEPTION_MONTURE', label: 'Réception monture', ownerType: 'workshop' },
      { code: 'ATELIER_RECUPERE_VERRE_STOCK', label: 'Picking verre', ownerType: 'workshop' },
      { code: 'ATELIER_MONTAGE_RAPPROCHEMENT', label: 'Rapprochement', ownerType: 'workshop' },
      { code: 'ATELIER_PRET_ENVOI_MAGASIN', label: 'À envoyer magasin', ownerType: 'workshop' },
      { code: 'ATELIER_ENVOI_LUNETTE_MAGASIN', label: 'Envoi magasin', ownerType: 'workshop' },
      { code: 'MAGASIN_RECEPTION_DOSSIER_COMPLET', label: 'Réception finale', ownerType: 'store' },
    ],
  },
  STOCK_LENS_RAW_BL: {
    label: 'Verre de stock - Envoi Palets Bruts',
    status: 'À envoyer',
    steps: [
      { code: 'MAGASIN_CONSULTE_COMMANDE_STOCK', label: 'Commande', ownerType: 'store' },
      { code: 'ATELIER_RECUPERE_VERRE_STOCK', label: 'Picking verre', ownerType: 'workshop' },
      { code: 'ATELIER_ENVOI_VERRE_MAGASIN', label: 'À mettre en BL retour', ownerType: 'workshop' },
      { code: 'MAGASIN_RECEPTION_VERRE', label: 'Réception finale', ownerType: 'store' },
    ],
  },
};

const workflowDocumentation = [
  {
    category: 'Prestations atelier',
    items: [
      { key: 'RETAILLAGE', frame: 'Tous magasins', mounting: 'Montage atelier' },
      { key: 'CLIP', frame: 'Magasins AA uniquement', mounting: 'Montage atelier · Type Clip forcé' },
    ],
  },
  {
    category: 'Verre fournisseur',
    items: [
      { key: 'NEW_FRAME_NON_STOCK_LENS', frame: 'Simple Boucle', mounting: 'Montage atelier' },
      { key: 'NEW_FRAME_NON_STOCK_LENS_STORE_MOUNT', frame: 'Simple Boucle', mounting: 'Montage magasin' },
      { key: 'EXISTING_FRAME_NON_STOCK_LENS', frame: 'Deuxième Boucle', mounting: 'Montage atelier' },
      { key: 'EXISTING_FRAME_NON_STOCK_LENS_STORE_MOUNT', frame: 'Deuxième Boucle', mounting: 'Montage magasin (historique)' },
    ],
  },
  {
    category: 'Verre de stock',
    items: [
      { key: 'STOCK_LENS_STORE', frame: 'Télédétourage', mounting: 'Montage magasin' },
      { key: 'STOCK_LENS_WORKSHOP', frame: 'Envoi monture Atelier AMOI', mounting: 'Montage atelier' },
      { key: 'STOCK_LENS_RAW_BL', frame: 'Envoi Palets Bruts', mounting: 'Sans montage atelier' },
    ],
  },
];

function WorkflowDocumentationStepper({ steps }) {
  return (
    <Box sx={{ maxWidth: 720 }}>
      <Stepper
        orientation="vertical"
        activeStep={-1}
        sx={{
          '& .MuiStepConnector-line': { borderColor: 'divider' },
          '& .MuiStepLabel-root': { py: 0.35 },
          '& .MuiStepLabel-iconContainer': { pr: 1.25 },
          '& .MuiStepLabel-label': { mt: 0 },
        }}
      >
        {steps.map((step, index) => {
          const isWorkshop = step.ownerType === 'workshop';
          const OwnerIcon = isWorkshop ? BuildCircleRoundedIcon : StorefrontRoundedIcon;
          return (
            <Step key={step.code}>
              <StepLabel
                StepIconComponent={() => (
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: isWorkshop ? 'success.main' : 'primary.main',
                      color: 'common.black',
                    }}
                  >
                    <OwnerIcon sx={{ fontSize: 17 }} />
                  </Avatar>
                )}
                optional={
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.1 }}>
                    <Chip
                      size="small"
                      color={isWorkshop ? 'success' : 'primary'}
                      variant="outlined"
                      label={isWorkshop ? 'Atelier' : 'Magasin'}
                      sx={{ height: 20, fontWeight: 800 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Étape {index + 1}
                    </Typography>
                  </Stack>
                }
              >
                <Typography variant="body2" sx={{ display: 'block', fontWeight: 900, lineHeight: 1.2 }}>
                  {step.label}
                </Typography>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
}

function WorkflowDocumentationDialog({ open, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <AppBar position="static" color="transparent" elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" onClick={onClose} aria-label="Fermer la documentation">
            <CloseIcon />
          </IconButton>
          <InfoOutlinedIcon color="primary" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 950 }}>
              Documentation des workflows atelier
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              Parcours des dossiers verre fournisseur et verre de stock.
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <DialogContent sx={{ bgcolor: 'background.default', py: { xs: 2, md: 3 } }}>
        <Box sx={{ width: 'min(1120px, 100%)', mx: 'auto' }}>
          <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2.5 }}>
            Le workflow est défini à la création du JOB. Les jalons sont à bipper dans l’ordre affiché.
          </Alert>
          <Stack spacing={3}>
            {workflowDocumentation.map((group) => (
              <Box key={group.category}>
                <Typography variant="overline" color="primary" sx={{ fontWeight: 950 }}>
                  {group.category}
                </Typography>
                <Stack spacing={1} sx={{ mt: 0.75 }}>
                  {group.items.map((item) => {
                    const workflow = workflowOptions[item.key];
                    return (
                      <Accordion key={item.key} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', overflow: 'hidden', '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: { xs: 1.5, md: 2 }, '& .MuiAccordionSummary-content': { my: 1.25 } }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ width: '100%', pr: 1 }}>
                            <Typography sx={{ fontWeight: 900, flex: 1 }}>{workflow.label}</Typography>
                            <Chip size="small" color="info" variant="outlined" label={item.frame} sx={{ fontWeight: 800 }} />
                            <Chip size="small" color="success" variant="outlined" label={item.mounting} sx={{ fontWeight: 800 }} />
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: { xs: 1.5, md: 2 }, pt: 0, pb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            {item.key === 'STOCK_LENS_STORE'
                              ? 'Le magasin transmet la forme; l’atelier prépare et renvoie uniquement le ou les verres.'
                              : item.key === 'STOCK_LENS_WORKSHOP'
                                ? 'Le magasin envoie la monture à AMOI; l’atelier réalise le montage puis retourne l’équipement.'
                                : 'Le dossier suit les jalons ci-dessous jusqu’à sa réception finale au magasin.'}
                          </Typography>
                          <WorkflowDocumentationStepper steps={workflow.steps} />
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

const bulkWorkshopActions = [
  { code: 'WORKSHOP_RECEIVE_FILE', label: 'Réceptionner dossier', icon: <FolderCopyRoundedIcon /> },
  { code: 'WORKSHOP_RECEIVE_LENS', label: 'Réceptionner verre', icon: <LensBlurRoundedIcon /> },
  { code: 'WORKSHOP_MATCH', label: 'Rapprocher', icon: <JoinInnerRoundedIcon /> },
  { code: 'WORKSHOP_SEND', label: 'Envoyer vers le magasin', icon: <LocalShippingRoundedIcon /> },
];

const stockLensBulkAction = {
  code: 'WORKSHOP_SEND_STOCK_LENS',
  label: 'Envoi verre',
  icon: <LocalShippingRoundedIcon />,
};

const bulkStoreActions = [
  { code: 'STORE_CREATE', label: 'Créer Suivi', icon: <AddIcon /> },
];

function BulkScanInlineForm({
  bulkJobInput,
  setBulkJobInput,
  bulkDisplayResults,
  onSubmit,
  onEnsureAction,
  inputKind = 'JOB',
}) {
  return (
    <Box component="form" onSubmit={onSubmit}>
      <Stack spacing={2}>
        <TextField
          autoFocus
          label={inputKind === 'FACTURE' ? 'Numéro de facture' : 'Scanner un JOB'}
          placeholder={inputKind === 'FACTURE' ? 'Ex : 121032021' : 'Scanne le code-barres du JOB'}
          value={bulkJobInput}
          onFocus={onEnsureAction}
          onChange={(event) => {
            onEnsureAction();
            setBulkJobInput(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onEnsureAction();
              onSubmit(event);
            }
          }}
          helperText={inputKind === 'FACTURE'
            ? 'Saisis ou scanne directement le numéro de facture, sans préfixe supplémentaire.'
            : 'Chaque Entrée valide immédiatement le JOB scanné.'}
          fullWidth
        />
        <Stack direction="row" spacing={1}>
          <Chip color="success" label={`${bulkDisplayResults.successCount} validé(s)`} />
          <Chip
            color={bulkDisplayResults.errorCount ? 'error' : 'default'}
            label={`${bulkDisplayResults.errorCount} rejeté(s)`}
          />
        </Stack>
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxHeight: 260 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{inputKind === 'FACTURE' ? 'Facture' : 'JOB'}</TableCell>
                <TableCell>Résultat</TableCell>
                <TableCell>Détail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bulkDisplayResults.results.map((result) => (
                <TableRow key={`${result.jobNumber}-${result.scannedAt || result.message}`}>
                  <TableCell sx={{ fontWeight: 700 }}>{result.jobNumber}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={result.success ? 'success' : 'error'}
                      label={result.success ? 'Validé' : 'Rejeté'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{result.message}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Box>
  );
}

function SecondLoopIcon({ width = 124, height = 54 }) {
  return (
    <Box aria-hidden="true" sx={{
      width,
      height,
      bgcolor: 'currentColor',
      mask: 'url(/assets/workflow/deuxieme-boucle-bold.png) center / contain no-repeat',
      WebkitMask: 'url(/assets/workflow/deuxieme-boucle-bold.png) center / contain no-repeat',
    }} />
  );
}

function ChoiceTile({ selected, icon, label, subtitle, onClick, disabled = false }) {
  return (
    <Button
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        width: 156,
        height: 156,
        flexShrink: 0,
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.85,
        px: 1.2,
        py: 1,
        borderRadius: 2,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'primary.main' : 'background.paper',
        color: selected ? 'primary.contrastText' : 'text.primary',
        boxShadow: selected ? '0 8px 18px rgba(25, 118, 210, 0.20)' : 'none',
        textAlign: 'center',
        textTransform: 'none',
        fontWeight: 900,
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: selected ? 'primary.dark' : 'action.hover',
        },
      }}
    >
      <Box sx={{ fontSize: 54, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 950, lineHeight: 1.12 }}>
          {label}
        </Typography>
        {subtitle && (
          <Typography component="span" variant="caption" sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Button>
  );
}

const GLASSES_PATH = 'M5 31c0-5 3-7.5 9-7.5s9 2.5 9 7.5-3 10-9 10-9-5-9-10Zm27 0c0-5 3-7.5 9-7.5s9 2.5 9 7.5-3 10-9 10-9-5-9-10Z';

// Simple Boucle : monture et ses deux origines possibles (magasin, client).
function SimpleLoopIcon({ height = 36 }) {
  return (
    <Box component="svg" viewBox="0 0 56 44" aria-hidden="true" sx={{ height, width: 'auto' }}>
      <path d={GLASSES_PATH} stroke="currentColor" strokeWidth="4.5" fill="none" strokeLinejoin="round" />
      <path d="M23 29.5c2.4-2 6.6-2 9 0" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" />
      <g transform="translate(15 10)">
        <circle r="9" fill="none" stroke="currentColor" strokeWidth="2.6" />
        <path d="M-5-2.6h10l-1.2-3.2h-7.6Zm.7 1.8v5.6h8.6v-5.6" fill="currentColor" stroke="currentColor" strokeWidth=".8" strokeLinejoin="round" />
      </g>
      <g transform="translate(41 10)">
        <circle r="9" fill="none" stroke="currentColor" strokeWidth="2.6" />
        <circle cy="-2.3" r="2.8" fill="currentColor" />
        <path d="M-5 5.4C-5 2.2-2.8.6 0 .6S5 2.2 5 5.4Z" fill="currentColor" />
      </g>
    </Box>
  );
}

// Retaillage : meuleuse de taillage (capot, fenêtre avec le verre, écran et boutons).
function RetailingIcon({ height = 36 }) {
  return (
    <Box component="svg" viewBox="0 0 56 44" aria-hidden="true" sx={{ height, width: 'auto' }}>
      <path d="M9 13 13 5h16l4 8" stroke="currentColor" strokeWidth="3.6" fill="none" strokeLinejoin="round" />
      <rect x="4" y="13" width="48" height="27" rx="5" stroke="currentColor" strokeWidth="4" fill="none" />
      <rect x="9" y="18" width="24" height="17" rx="4" stroke="currentColor" strokeWidth="3" fill="none" />
      <path d="M14.5 24.5c0-2.5 3-3.5 6.5-3.5s6.5 1 6.5 3.5-2.5 6-6.5 6-6.5-3.5-6.5-6Z" stroke="currentColor" strokeWidth="2.8" fill="none" strokeLinejoin="round" />
      <rect x="38" y="18" width="10" height="8" rx="1.5" fill="currentColor" />
      <circle cx="40.5" cy="31" r="1.9" fill="currentColor" />
      <circle cx="45.5" cy="31" r="1.9" fill="currentColor" />
    </Box>
  );
}

// Clip : face solaire teintée clipsée par-dessus la monture.
function ClipIcon({ height = 36 }) {
  return (
    <Box component="svg" viewBox="0 0 60 44" aria-hidden="true" sx={{ height, width: 'auto' }}>
      <rect x="3.5" y="18" width="22" height="19" rx="6" stroke="currentColor" strokeWidth="3.6" fill="none" />
      <rect x="34.5" y="18" width="22" height="19" rx="6" stroke="currentColor" strokeWidth="3.6" fill="none" />
      <path d="M25.5 23c3-2.5 6-2.5 9 0" stroke="currentColor" strokeWidth="3.4" fill="none" strokeLinecap="round" />
      <rect x="5" y="7" width="20" height="17" rx="5" fill="currentColor" fillOpacity=".5" stroke="currentColor" strokeWidth="3" />
      <rect x="35" y="7" width="20" height="17" rx="5" fill="currentColor" fillOpacity=".5" stroke="currentColor" strokeWidth="3" />
      <path d="M25 11.5h10" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
    </Box>
  );
}

function DailyOrderIcon({ height = 36 }) {
  return (
    <Box component="svg" viewBox="0 0 48 44" aria-hidden="true" sx={{ height, width: 'auto' }}>
      <rect x="5" y="7" width="38" height="33" rx="6" stroke="currentColor" strokeWidth="4" fill="none" />
      <path d="M5 17h38M15 3v8M33 3v8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
      <path d="m16 29 6 5 11-11" stroke="currentColor" strokeWidth="4.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </Box>
  );
}

function TypeTile({ selected, icon, label, subtitle, onClick }) {
  return (
    <Button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      sx={{
        minHeight: 106,
        flexDirection: 'column',
        gap: 0.5,
        px: 1,
        py: 1.25,
        borderRadius: '16px',
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'primary.main' : 'background.paper',
        color: selected ? 'primary.contrastText' : 'text.primary',
        boxShadow: selected ? '0 10px 22px -12px rgba(102, 178, 255, 0.9)' : 'none',
        textTransform: 'none',
        '&:hover': { borderColor: 'primary.main', bgcolor: selected ? 'primary.main' : 'action.hover' },
      }}
    >
      <Box sx={{ height: 38, display: 'grid', placeItems: 'center' }}>{icon}</Box>
      <Typography variant="body2" sx={{ fontWeight: 800, fontSize: 12, lineHeight: 1.2 }}>{label}</Typography>
      {subtitle && (
        <Typography component="span" sx={{ fontSize: 10.5, fontStyle: 'italic', opacity: 0.85 }}>{subtitle}</Typography>
      )}
    </Button>
  );
}

function SegmentedChoice({ value, options, onChange, ariaLabel }) {
  return (
    <Box
      role="radiogroup"
      aria-label={ariaLabel}
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
        maxWidth: 520,
        p: 0.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '14px',
        bgcolor: 'action.hover',
      }}
    >
      {options.map((option) => {
        const checked = value === option.value;
        return (
          <Button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={checked}
            disabled={option.disabled}
            startIcon={option.icon}
            onClick={() => onChange(option.value)}
            sx={{
              borderRadius: '10px',
              py: 1.1,
              fontWeight: 800,
              textTransform: 'none',
              color: checked ? 'primary.contrastText' : 'text.primary',
              bgcolor: checked ? 'primary.main' : 'transparent',
              boxShadow: checked ? '0 6px 14px -8px rgba(102, 178, 255, 0.9)' : 'none',
              '&:hover': { bgcolor: checked ? 'primary.main' : 'action.selected' },
            }}
          >
            {option.label}
          </Button>
        );
      })}
    </Box>
  );
}

function StepTitle({ number, children, optional = false, action = null }) {
  return (
    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ flexWrap: 'wrap' }}>
      <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
        <Box component="span" sx={{ color: 'primary.dark', fontWeight: 900, mr: 1 }}>{number}.</Box>
        {children}
      </Typography>
      {optional && (
        <Typography component="span" sx={{ fontSize: 11.5, fontStyle: 'italic', color: 'text.secondary' }}>Facultatif</Typography>
      )}
      {action && <Box sx={{ ml: 'auto !important' }}>{action}</Box>}
    </Stack>
  );
}

const NC_PHOTO_MAX = 3;

// Réduit une photo (≈ 1 600 px, JPEG) avant l'envoi : une photo de téléphone de
// plusieurs Mo passe à quelques centaines de Ko.
async function compressPhoto(file, maxSize = 1600, quality = 0.82) {
  const loadImage = () => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Format non lisible par ce navigateur : ${file.name}. Utilise une photo JPEG ou PNG (sur iPhone : Réglages › Appareil photo › Formats › « Le plus compatible »).`));
    };
    image.src = url;
  });
  let source;
  try {
    source = await createImageBitmap(file);
  } catch (_error) {
    source = await loadImage();
  }
  const scale = Math.min(1, maxSize / Math.max(source.width, source.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(source.width * scale);
  canvas.height = Math.round(source.height * scale);
  canvas.getContext('2d').drawImage(source, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  if (!blob) throw new Error(`Compression impossible pour ${file.name}.`);
  return new File([blob], `${String(file.name || 'photo').replace(/\.[^.]+$/, '')}.jpg`, { type: 'image/jpeg' });
}

function NonConformityPhotos({ commandeId, nonConformity, canAdd, onUpdated, onError }) {
  const photos = nonConformity?.attachments || [];
  const [urls, setUrls] = useState({});
  const [viewer, setViewer] = useState(null);
  const [uploading, setUploading] = useState(false);
  const photoKey = photos.map((photo) => photo._id).join(',');

  useEffect(() => {
    let cancelled = false;
    const created = [];
    Promise.all(photos.map(async (photo) => {
      try {
        const { blob } = await requestBlob(`/commandes/${commandeId}/non-conformities/${nonConformity._id}/photos/${photo._id}`);
        const url = URL.createObjectURL(blob);
        created.push(url);
        return [photo._id, url];
      } catch (_error) {
        return [photo._id, ''];
      }
    })).then((entries) => {
      if (!cancelled) setUrls(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
      created.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commandeId, nonConformity?._id, photoKey]);

  const handleAdd = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    if (photos.length + files.length > NC_PHOTO_MAX) {
      onError(`${NC_PHOTO_MAX} photos maximum par non-conformité.`);
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      for (const file of files) formData.append('photos', await compressPhoto(file));
      const updated = await requestJson(`/commandes/${commandeId}/non-conformities/${nonConformity._id}/photos`, {
        method: 'POST',
        body: formData,
      });
      onUpdated(updated);
    } catch (error) {
      onError(error.message);
    } finally {
      setUploading(false);
    }
  };

  if (!photos.length && !canAdd) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
      {photos.map((photo, index) => (
        <Box
          key={photo._id}
          component="button"
          type="button"
          onClick={() => urls[photo._id] && setViewer(urls[photo._id])}
          aria-label={`Agrandir la photo ${index + 1}`}
          sx={{
            width: 64,
            height: 64,
            p: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            cursor: 'zoom-in',
            bgcolor: 'background.paper',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {urls[photo._id]
            ? <Box component="img" src={urls[photo._id]} alt={`Photo ${index + 1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <CircularProgress size={18} />}
        </Box>
      ))}
      {canAdd && photos.length < NC_PHOTO_MAX && (
        <Button
          component="label"
          size="small"
          variant="outlined"
          color="warning"
          startIcon={uploading ? <CircularProgress size={14} /> : <AddAPhotoRoundedIcon fontSize="small" />}
          disabled={uploading}
          sx={{ fontWeight: 800 }}
        >
          {photos.length ? 'Ajouter' : 'Ajouter une photo'}
          <input hidden type="file" accept="image/*" multiple onChange={handleAdd} />
        </Button>
      )}
      <Dialog open={Boolean(viewer)} onClose={() => setViewer(null)} maxWidth="lg">
        {viewer && (
          <Box component="img" src={viewer} alt="Photo de la non-conformité" onClick={() => setViewer(null)} sx={{ display: 'block', maxWidth: '100%', maxHeight: '85vh', cursor: 'zoom-out' }} />
        )}
      </Dialog>
    </Stack>
  );
}

function StoreSendInlineForm({
  frameType,
  setFrameType,
  mountingLocation,
  setMountingLocation,
  teleEdging,
  setTeleEdging,
  frameOrigin,
  setFrameOrigin,
  frameOriginCode,
  setFrameOriginCode,
  customerLastName,
  setCustomerLastName,
  customerFirstName,
  setCustomerFirstName,
  isAdmin,
  selectedBulkOperator,
  bulkOperatorOptions,
  setBulkOperatorId,
  bulkJobInput,
  setBulkJobInput,
  bulkDisplayResults,
  onSubmit,
  onEnsureAction,
  isAaStore,
}) {
  const isSimpleLoop = frameType === 'new';
  const showTeleEdging = isSimpleLoop && mountingLocation === 'store';
  const showOrigin = FRAME_ORIGIN_TYPES.includes(frameType);
  const usesInvoiceNumber = ['retailing', 'clip', 'daily'].includes(frameType);
  const selectFrameType = (value) => {
    setFrameType(value);
    if (value !== 'new') {
      setMountingLocation('workshop');
      setTeleEdging(false);
    }
    if (!FRAME_ORIGIN_TYPES.includes(value)) {
      setFrameOrigin('');
      setFrameOriginCode('');
    }
  };
  const selectOrigin = (value) => {
    setFrameOrigin(value);
    setFrameOriginCode('');
  };
  const types = [
    { value: 'new', label: 'Simple Boucle', subtitle: 'Monture stock ou client', icon: <SimpleLoopIcon /> },
    { value: 'existing', label: 'Deuxième Boucle', subtitle: 'Obligatoire', icon: <SecondLoopIcon width={82} height={36} /> },
    { value: 'retailing', label: 'Retaillage', icon: <RetailingIcon /> },
    isAaStore && { value: 'clip', label: 'Clip', icon: <ClipIcon /> },
    { value: 'daily', label: 'Commande journalière', icon: <DailyOrderIcon /> },
  ].filter(Boolean);
  let stepNumber = 0;
  const nextStep = () => {
    stepNumber += 1;
    return stepNumber;
  };
  const scanTitle = usesInvoiceNumber ? 'Numéro de facture' : 'Scanner les JOB';
  const scanHelp = frameType === 'daily'
    ? 'Le numéro de facture sert de numéro de JOB. Entrée crée la commande et son BL, puis ouvre la fiche à imprimer.'
    : usesInvoiceNumber
      ? 'Saisis ou scanne directement le numéro de facture, sans préfixe supplémentaire.'
      : '';

  return (
    <Box component="form" onSubmit={onSubmit}>
      <Stack spacing={2.75}>
        {isAdmin && (
          <Autocomplete
            options={bulkOperatorOptions}
            value={selectedBulkOperator}
            onChange={(_event, value) => setBulkOperatorId(value?._id || '')}
            getOptionLabel={getUserLabel}
            renderInput={(params) => <TextField {...params} label="Opérateur magasin" required />}
            fullWidth
          />
        )}

        <Stack spacing={1}>
          <StepTitle number={nextStep()}>Type de dossier</StepTitle>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))', gap: 1.25 }}>
            {types.map((type) => (
              <TypeTile
                key={type.value}
                selected={frameType === type.value}
                icon={type.icon}
                label={type.label}
                subtitle={type.subtitle}
                onClick={() => selectFrameType(type.value)}
              />
            ))}
          </Box>
        </Stack>

        <Stack spacing={1}>
          <StepTitle number={nextStep()}>Assemblage fait par</StepTitle>
          <SegmentedChoice
            ariaLabel="Assemblage fait par"
            value={mountingLocation}
            onChange={setMountingLocation}
            options={[
              { value: 'workshop', label: 'Atelier', icon: <BuildCircleRoundedIcon /> },
              { value: 'store', label: 'Magasin', icon: <StorefrontRoundedIcon />, disabled: !isSimpleLoop },
            ]}
          />
          {!isSimpleLoop && (
            <Typography variant="caption" color="text.secondary">
              {frameType === 'existing'
                ? 'Atelier obligatoire en Deuxième Boucle.'
                : frameType === 'daily'
                  ? 'Atelier obligatoire pour une commande journalière.'
                  : 'Atelier obligatoire pour ce choix.'}
            </Typography>
          )}
        </Stack>

        {showTeleEdging && (
          <Stack spacing={1}>
            <StepTitle number={nextStep()}>Taillage des verres par AMOI</StepTitle>
            <SegmentedChoice
              ariaLabel="Taillage des verres par AMOI"
              value={teleEdging ? 'yes' : 'no'}
              onChange={(value) => setTeleEdging(value === 'yes')}
              options={[
                { value: 'yes', label: 'Oui', icon: <CheckRoundedIcon /> },
                { value: 'no', label: 'Non', icon: <CloseRoundedIcon /> },
              ]}
            />
          </Stack>
        )}

        {showOrigin && (
          <Stack spacing={1}>
            <StepTitle
              number={nextStep()}
              optional
              action={(
                <Button size="small" onClick={() => selectOrigin('')} disabled={!frameOrigin} sx={{ textDecoration: 'underline', fontWeight: 700 }}>
                  Effacer
                </Button>
              )}
            >
              Origine de la monture
            </StepTitle>
            <SegmentedChoice
              ariaLabel="Origine de la monture"
              value={frameOrigin}
              onChange={selectOrigin}
              options={[
                { value: 'stock', label: 'Monture du stock', icon: <StorefrontRoundedIcon /> },
                { value: 'customer', label: 'Monture du client', icon: <PersonRoundedIcon /> },
              ]}
            />
            {frameOrigin && (
              <TextField
                size="small"
                value={frameOriginCode}
                onChange={(event) => setFrameOriginCode(event.target.value.replace(/\s+/g, '').toUpperCase())}
                placeholder={frameOrigin === 'stock' ? 'Code article (facultatif)' : 'Référence de la monture (facultatif)'}
                inputProps={{ 'aria-label': frameOrigin === 'stock' ? 'Code article' : 'Référence de la monture' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><QrCodeScannerRoundedIcon fontSize="small" /></InputAdornment>
                  ),
                  sx: { fontFamily: 'monospace', fontWeight: 700 },
                }}
                sx={{ maxWidth: 520 }}
              />
            )}
          </Stack>
        )}

        <Stack spacing={1}>
          <StepTitle number={nextStep()} optional>Client</StepTitle>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <TextField
              size="small"
              label="Nom"
              value={customerLastName}
              onChange={(event) => setCustomerLastName(event.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="Prénom"
              value={customerFirstName}
              onChange={(event) => setCustomerFirstName(event.target.value)}
              fullWidth
            />
          </Stack>
        </Stack>

        <Stack spacing={1}>
          <StepTitle number={nextStep()}>{scanTitle}</StepTitle>
          <TextField
            autoFocus
            value={bulkJobInput}
            placeholder={usesInvoiceNumber ? 'Scanne ou saisis le numéro de facture' : 'Scanne le code-barres du JOB'}
            onFocus={onEnsureAction}
            onChange={(event) => {
              onEnsureAction();
              setBulkJobInput(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onEnsureAction();
                onSubmit(event);
              }
            }}
            helperText={scanHelp || undefined}
            inputProps={{ 'aria-label': scanTitle }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <QrCodeScannerRoundedIcon sx={{ color: 'primary.main', fontSize: 30 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Chip size="small" variant="outlined" label="Entrée ↵" sx={{ fontWeight: 700 }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: '14px',
                bgcolor: 'rgba(102, 178, 255, 0.08)',
                fontSize: 22,
                fontWeight: 700,
                fontFamily: 'monospace',
                '& input': { py: 1.75 },
                '& fieldset': { borderWidth: 2, borderColor: 'primary.main' },
              },
            }}
            fullWidth
          />
        </Stack>

        <Stack spacing={1.25} sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontWeight: 800, fontSize: 13.5, mr: 'auto' }}>Résultats de la session</Typography>
            <Chip size="small" color="success" variant="outlined" label={`${bulkDisplayResults.successCount} validé(s)`} />
            <Chip
              size="small"
              color={bulkDisplayResults.errorCount ? 'error' : 'default'}
              variant="outlined"
              label={`${bulkDisplayResults.errorCount} rejeté(s)`}
            />
          </Stack>
          {bulkDisplayResults.results.length ? (
            <Stack spacing={1} sx={{ maxHeight: 280, overflowY: 'auto' }}>
              {bulkDisplayResults.results.map((result) => (
                <Box
                  key={`${result.jobNumber}-${result.scannedAt || result.message}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr auto', sm: '130px 1fr auto' },
                    gap: 1,
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderLeft: '4px solid',
                    borderLeftColor: result.success ? 'success.main' : 'error.main',
                    borderRadius: '12px',
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{result.jobNumber}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, gridRow: { xs: 2, sm: 'auto' } }}>
                    {result.message}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: result.success ? 'success.main' : 'error.main' }}>
                    {result.success ? 'Validé' : 'Rejeté'}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">Aucun scan pour l’instant.</Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

function ProcessSelectionCards({ frameType, mountingLocation, onFrameTypeChange, onMountingLocationChange }) {
  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
          Monture
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', gap: 1.5 }}>
          <ChoiceTile
            selected={frameType === 'new'}
            icon={<SimpleLoopIcon height={54} />}
            label="Simple Boucle"
            subtitle="Monture stock ou client"
            onClick={() => onFrameTypeChange('new')}
          />
          <ChoiceTile
            selected={frameType === 'existing'}
            icon={<SecondLoopIcon />}
            label="Deuxième Boucle"
            onClick={() => onFrameTypeChange('existing')}
          />
        </Box>
      </Stack>

      <Stack spacing={1}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
          Assemblage fait par
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', gap: 1.5 }}>
          <ChoiceTile
            selected={mountingLocation === 'workshop'}
            icon={<BuildCircleRoundedIcon fontSize="inherit" />}
            label="Atelier"
            onClick={() => onMountingLocationChange('workshop')}
          />
          <ChoiceTile
            selected={mountingLocation === 'store'}
            icon={<StorefrontRoundedIcon fontSize="inherit" />}
            label="Magasin"
            onClick={() => onMountingLocationChange('store')}
          />
        </Box>
      </Stack>

      <Alert severity="info">
        <strong>{getFrameTypeLabel(frameType)}</strong> + Montage réalisé par{' '}
        <strong>{mountingLocation === 'store' ? 'magasin' : 'atelier'}</strong>
      </Alert>
    </Stack>
  );
}

const bulkActionStyles = {
  WORKSHOP_RECEIVE_FILE: {
    color: '#ffffff',
    background: 'linear-gradient(135deg, #0284c7, #2563eb)',
    borderColor: '#38bdf8',
  },
  WORKSHOP_RECEIVE_LENS: {
    color: '#ffffff',
    background: 'linear-gradient(135deg, #0891b2, #0d9488)',
    borderColor: '#22d3ee',
  },
  WORKSHOP_MATCH: {
    color: '#ffffff',
    background: 'linear-gradient(135deg, #16a34a, #059669)',
    borderColor: '#4ade80',
  },
  WORKSHOP_SEND: {
    color: '#ffffff',
    background: 'linear-gradient(135deg, #2563eb, #06b6d4)',
    borderColor: '#60a5fa',
  },
  WORKSHOP_SEND_STOCK_LENS: {
    color: '#ffffff',
    background: 'linear-gradient(135deg, #2563eb, #06b6d4)',
    borderColor: '#60a5fa',
  },
  STORE_CREATE: {
    color: '#111827',
    background: 'linear-gradient(135deg, #fde047, #f59e0b)',
    borderColor: '#facc15',
  },
  STORE_SEND: {
    color: '#ffffff',
    background: 'linear-gradient(135deg, #2563eb, #0ea5e9)',
    borderColor: '#60a5fa',
  },
  STORE_RECEIVE: {
    color: '#ffffff',
    background: 'linear-gradient(135deg, #0284c7, #2563eb)',
    borderColor: '#38bdf8',
  },
};

const mountingTypeChipStyles = {
  'T.D.': { bgcolor: '#2563eb', color: '#ffffff' },
  Classique: { bgcolor: '#0891b2', color: '#ffffff' },
  'Easy-Digifit': { bgcolor: '#7c3aed', color: '#ffffff' },
  Percées: { bgcolor: '#dc2626', color: '#ffffff' },
  'V.S.': { bgcolor: '#0d9488', color: '#ffffff' },
  '+Gravure': { bgcolor: '#ea580c', color: '#ffffff' },
  Clip: { bgcolor: '#f59e0b', color: '#111827' },
};

function getMountingTypeChipSx(type) {
  return mountingTypeChipStyles[type] || { bgcolor: '#334155', color: '#ffffff' };
}

const DAY_MS = 24 * 60 * 60 * 1000;

const stepLabelByCode = {
  MAGASIN_ENVOI_DOSSIER_MONTURE: 'Envoi dossier',
  ATELIER_RECEPTION_COMMANDE: 'Réception commande',
  ATELIER_RECEPTION_DOSSIER: 'Réception dossier',
  ATELIER_RECEPTION_MONTURE: 'Réception monture',
  ATELIER_RECEPTION_VERRE: 'Réception verre',
  ATELIER_RAPPROCHEMENT: 'Rapprochement',
  ATELIER_MONTAGE: 'Montage',
  ATELIER_RAPPROCHEMENT_MONTAGE: 'Rapprochement / montage',
  MAGASIN_RECEPTION_DOSSIER_COMPLET: 'Réception finale',
  MAGASIN_DOSSIER_INFORMATION: 'Commande',
  ATELIER_RECEPTION_DOSSIER_COMMANDE_VERRE: 'Réception verre',
  MAGASIN_RECEPTION_VERRE: 'Réception verre',
  MAGASIN_ENVOI_DOSSIER_MONTURE_VERRE: 'Envoi monture/verre',
  MAGASIN_CONSULTE_COMMANDE_STOCK: 'Commande',
  ATELIER_RECUPERE_VERRE_STOCK: 'Picking verre',
  ATELIER_PRET_ENVOI_VERRE_MAGASIN: 'Envoi verre',
  ATELIER_ENVOI_VERRE_MAGASIN: 'Envoi verre au magasin',
  MAGASIN_MONTAGE: 'Montage magasin',
  MAGASIN_ENVOI_MONTURE: 'Envoi monture',
  ATELIER_MONTAGE_RAPPROCHEMENT: 'Rapprochement',
  ATELIER_PRET_ENVOI_MAGASIN: 'À envoyer magasin',
  ATELIER_ENVOI_LUNETTE_MAGASIN: 'Envoi magasin',
};

function getStepLabel(step) {
  return stepLabelByCode[step?.code] || step?.label || '—';
}

function normalizeWorkflowStepsForDisplay(steps = []) {
  const normalized = [];

  for (let index = 0; index < steps.length; index += 1) {
    const step = steps[index];
    const nextStep = steps[index + 1];

    if (step?.code === 'ATELIER_RAPPROCHEMENT' && nextStep?.code === 'ATELIER_MONTAGE') {
      const isFullyCompleted = Boolean(step.completedAt && nextStep.completedAt);
      normalized.push({
        ...step,
        code: 'ATELIER_RAPPROCHEMENT_MONTAGE_LEGACY',
        label: 'Rapprochement / montage',
        completedAt: isFullyCompleted ? nextStep.completedAt : undefined,
        completedBy: isFullyCompleted ? nextStep.completedBy || step.completedBy : undefined,
        completedSiteId: isFullyCompleted ? nextStep.completedSiteId || step.completedSiteId : undefined,
      });
      index += 1;
      continue;
    }

    normalized.push(step);
  }

  return normalized;
}

function resolveProcessFromForm(form) {
  if (form.lensMode === 'stock') {
    if (form.process === 'STOCK_LENS_RAW_BL') return 'STOCK_LENS_RAW_BL';
    return form.mountingLocation === 'workshop' ? 'STOCK_LENS_WORKSHOP' : 'STOCK_LENS_STORE';
  }

  if (form.frameType === 'existing') {
    return form.mountingLocation === 'store'
      ? 'EXISTING_FRAME_NON_STOCK_LENS_STORE_MOUNT'
      : 'EXISTING_FRAME_NON_STOCK_LENS';
  }

  return form.mountingLocation === 'store'
    ? 'NEW_FRAME_NON_STOCK_LENS_STORE_MOUNT'
    : 'NEW_FRAME_NON_STOCK_LENS';
}

function getFrameTypeLabel(frameType, process = '', dailyOrder = false) {
  if (dailyOrder) return 'Commande journalière';
  if (process === 'STOCK_LENS_RAW_BL') return 'Palets bruts';
  if (frameType === 'existing') return 'Deuxième Boucle';
  if (frameType === 'retailing') return 'Retaillage';
  if (frameType === 'clip') return 'Clip';
  return 'Simple Boucle';
}

function getFrameOriginLabel(frame) {
  if (!frame?.origin) return '';
  const label = frame.origin === 'stock' ? 'Monture du stock' : 'Monture du client';
  return frame.originCode ? `${label} · ${frame.originCode}` : label;
}

function getMountingLocationLabel(mountingLocation) {
  return mountingLocation === 'workshop' ? 'atelier' : 'magasin';
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('fr-FR');
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function printSpecialWorkshopSheet(printWindow, { jobNumber, customerName, processLabel, storeLabel, dispatchListNumber = '', originLabel = '' }) {
  if (!printWindow) return;
  // La fiche Retaillage doit rester lisible tout en occupant moins de place
  // à l'impression. Les fiches Clip conservent la mise en page historique.
  const isRetailing = String(processLabel || '').toUpperCase() === 'RETAILLAGE';
  const barcodeWidth = isRetailing ? 190 : 300;
  const barcodeHeight = isRetailing ? 52 : 82;
  const barcodeNumberSize = isRetailing ? 14 : 18;
  const html = `<!doctype html>
    <html lang="fr"><head><meta charset="utf-8"/><title>Fiche atelier ${escapeHtml(jobNumber)}</title>
    <style>
      @page{size:A4;margin:14mm}*{box-sizing:border-box}body{margin:0;font-family:Arial,Helvetica,sans-serif;color:#0f172a}
      header{display:flex;justify-content:space-between;align-items:flex-start;gap:28px;border-bottom:3px solid #0ea5e9;padding-bottom:16px}
      h1{margin:0;font-size:27px}.subtitle{margin-top:5px;color:#64748b}.type{display:inline-block;margin-top:14px;padding:8px 14px;border-radius:999px;background:#dbeafe;color:#1e40af;font-size:16px;font-weight:800}
      .barcode{width:${barcodeWidth}px;height:${barcodeHeight}px}.number{text-align:center;font-size:${barcodeNumberSize}px;font-weight:900;margin-top:4px}
      .details{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:26px}.field{border:1px solid #cbd5e1;border-radius:9px;padding:14px;min-height:72px;font-size:18px;font-weight:800}
      .label{display:block;margin-bottom:7px;color:#64748b;font-size:11px;text-transform:uppercase;font-weight:700}.work{margin-top:26px;height:330px;border:1px solid #cbd5e1;border-radius:9px;padding:14px;color:#64748b;font-size:12px}
      footer{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:26px}.signature{height:90px;border:1px solid #cbd5e1;border-radius:9px;padding:12px;color:#64748b;font-size:12px}
    </style></head><body>
      <header><div><h1>Fiche atelier</h1><div class="subtitle">Optibox · ${escapeHtml(formatDateTime(new Date()))}</div><div class="type">${escapeHtml(processLabel)}</div></div>
      <div><div class="barcode">${getDispatchBarcodeSvg(jobNumber)}</div><div class="number">${escapeHtml(jobNumber)}</div></div></header>
      <div class="details"><div class="field"><span class="label">Client</span>${escapeHtml(customerName)}</div><div class="field"><span class="label">Type</span>${escapeHtml(processLabel)}</div><div class="field"><span class="label">Numéro de facture</span>${escapeHtml(jobNumber)}</div><div class="field"><span class="label">Magasin</span>${escapeHtml(storeLabel || '—')}</div>${originLabel ? `<div class="field"><span class="label">Origine de la monture</span>${escapeHtml(originLabel)}</div>` : ''}${dispatchListNumber ? `<div class="field"><span class="label">Bon de livraison</span><div class="barcode">${getDispatchBarcodeSvg(dispatchListNumber)}</div><div class="number">${escapeHtml(dispatchListNumber)}</div></div>` : ''}</div>
      <div class="work">Observations atelier</div><footer><div class="signature">Technicien</div><div class="signature">Contrôle</div></footer>
      <script>window.addEventListener('load',()=>{window.focus();window.print()})</script></body></html>`;
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.opener = null;
}

function getMailStatusLabel(status) {
  if (status === 'sent') return 'Envoyé';
  if (status === 'skipped') return 'Non envoyé';
  if (status === 'failed') return 'Erreur';
  return status || '—';
}

function getMailStatusSeverity(status) {
  if (status === 'failed') return 'error';
  if (status === 'skipped') return 'warning';
  return 'info';
}

function getOwnerLabel(ownerType) {
  if (ownerType === 'store') return 'Magasin';
  if (ownerType === 'workshop') return 'Atelier';
  return 'Stock';
}

function getOwnerIcon(ownerType) {
  if (ownerType === 'store') return <StorefrontRoundedIcon fontSize="inherit" />;
  if (ownerType === 'workshop') return <BuildCircleRoundedIcon fontSize="inherit" />;
  return <Inventory2RoundedIcon fontSize="inherit" />;
}

function getOwnerIconColor(ownerType) {
  if (ownerType === 'store') return 'primary.main';
  if (ownerType === 'workshop') return 'success.main';
  return 'info.main';
}

function getStoreSupplierAccount(row) {
  const supplier =
    row.lens?.supplier?.toUpperCase?.() || row.lens?.stockItemId?.supplier?.toUpperCase?.() || '';
  if (!supplier || !row.storeSiteId?.supplierAccounts) return '—';
  return row.storeSiteId.supplierAccounts.find((account) => account.supplier === supplier)?.accountNumber || '—';
}

function getCommandeSupplier(row) {
  return row.lens?.supplier || row.lens?.stockItemId?.supplier || '—';
}

function getSiteAvatarText(site) {
  const code = site?.code || '';
  return code.slice(0, 3) || site?.name?.slice(0, 3) || '?';
}

function getWaitingSiteForStep(row, step) {
  if (step?.ownerType === 'workshop') return row.workshopSiteId;
  if (step?.ownerType === 'store') return row.storeSiteId;
  return null;
}

function getCurrentStep(row) {
  return row.workflow?.steps?.find((step) => !step.completedAt) || null;
}

// The immutable activity journal is available on newly updated JOBs. For
// older JOBs, expose the completed workflow milestones as a read-only
// fallback so the detail view still shows the complete history.
function getCommandeActivityHistory(row) {
  const entries = [...(row?.activityHistory || [])];
  const recordedSteps = new Set(
    entries.map((entry) => entry?.metadata?.stepCode).filter(Boolean),
  );
  (row?.workflow?.steps || []).forEach((step) => {
    if (!step?.completedAt || recordedSteps.has(step.code)) return;
    entries.push({
      action: 'workflow_step_completed',
      label: `Étape validée : ${step.label || step.code}`,
      detail: step.label || step.code,
      occurredAt: step.completedAt,
      performedBySiteType: step.ownerType,
      performedBySiteId: step.completedSiteId,
      performedByUserId: step.completedBy,
      metadata: { stepCode: step.code, legacy: true },
    });
  });
  if (!entries.length && row?.createdAt) {
    entries.push({
      action: 'commande_created',
      label: 'JOB créé',
      detail: 'Création du suivi atelier (historique initial).',
      occurredAt: row.createdAt,
      metadata: { legacy: true },
    });
  }
  return entries.sort((left, right) => new Date(right.occurredAt || 0) - new Date(left.occurredAt || 0));
}

function getCurrentStepSince(row) {
  const steps = row?.workflow?.steps || [];
  const currentIndex = steps.findIndex((step) => !step.completedAt);
  if (currentIndex === -1) return '';
  const previousCompleted = [...steps.slice(0, currentIndex)].reverse().find((step) => step.completedAt);
  return previousCompleted?.completedAt || row?.createdAt || row?.updatedAt || '';
}

function getWorkflowStep(row, code) {
  return (row?.workflow?.steps || []).find((step) => step.code === code) || null;
}

function isAwaitingWorkshopLens(row) {
  return getCurrentStep(row)?.code === 'ATELIER_RECEPTION_VERRE';
}

function isAwaitingWorkshopMatching(row) {
  return [
    'ATELIER_RAPPROCHEMENT',
    'ATELIER_MONTAGE',
    'ATELIER_RAPPROCHEMENT_MONTAGE',
    'ATELIER_MONTAGE_RAPPROCHEMENT',
  ].includes(getCurrentStep(row)?.code);
}

function isAwaitingWorkshopDispatchPreparation(row) {
  return [
    'ATELIER_PRET_ENVOI_MAGASIN',
    'ATELIER_PRET_ENVOI_VERRE_MAGASIN',
  ].includes(getCurrentStep(row)?.code);
}

function isLensReceptionLate(row, thresholdDays = 20) {
  const dossierReception = getWorkflowStep(row, 'ATELIER_RECEPTION_DOSSIER');
  return isAwaitingWorkshopLens(row) && dossierReception?.completedAt && getDaysSince(dossierReception.completedAt) >= thresholdDays;
}

function isMatchingLate(row, thresholdDays = 3) {
  const lensReception = getWorkflowStep(row, 'ATELIER_RECEPTION_VERRE');
  return isAwaitingWorkshopMatching(row) && lensReception?.completedAt && getDaysSince(lensReception.completedAt) >= thresholdDays;
}

function isLensWithoutDossierLate(row, thresholdDays = 6) {
  const lensReception = getWorkflowStep(row, 'ATELIER_RECEPTION_VERRE');
  const dossierReception = getWorkflowStep(row, 'ATELIER_RECEPTION_DOSSIER');
  return Boolean(lensReception?.completedAt) && Boolean(dossierReception) && !dossierReception.completedAt && getDaysSince(lensReception.completedAt) >= thresholdDays;
}

function getDaysSince(value) {
  if (!value) return 0;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - date.getTime()) / DAY_MS));
}

function isCommandeBlocked(row, thresholdDays) {
  return Boolean(getCurrentStep(row)) && getDaysSince(getCurrentStepSince(row)) > Number(thresholdDays || 0);
}

function isDispatchListEligible(row) {
  const currentStep = getCurrentStep(row);
  return currentStep?.ownerType === 'store' && STORE_PHYSICAL_SEND_TO_WORKSHOP_STEP_CODES.includes(currentStep.code);
}

function isReturnDispatchListEligible(row) {
  const currentStep = getCurrentStep(row);
  return currentStep?.ownerType === 'workshop' && WORKSHOP_TO_SEND_STEP_CODES.includes(currentStep.code);
}

function isStoreCommandeToReceive(row) {
  const currentStep = getCurrentStep(row);
  return currentStep?.ownerType === 'store' && STORE_TO_RECEIVE_STEP_CODES.includes(currentStep.code);
}

function isStorePendingWorkshopIssue(row) {
  const hasNca = getActiveNonConformitiesByType(row, 'NCA').length > 0;
  const hasTraceabilityNon = (row?.traceabilityReports || []).some((report) => report.mountingCompleted === false);
  if (!hasNca && !hasTraceabilityNon) return false;

  const finalReception = (row?.workflow?.steps || []).find(
    (step) => STORE_TO_RECEIVE_STEP_CODES.includes(step.code) && step.completedAt
  );
  return !finalReception;
}

function getDisplayStep(step, viewMode = 'stock') {
  if (
    viewMode === 'nonStock' &&
    ['ATELIER_RAPPROCHEMENT', 'ATELIER_MONTAGE', 'ATELIER_RAPPROCHEMENT_MONTAGE'].includes(step?.code)
  ) {
    return {
      ...step,
      code: 'ATELIER_RAPPROCHEMENT_MONTAGE',
      label: 'Rapprochement / montage',
    };
  }
  return step;
}

function getCommandeDisplayLabel(row, viewMode = 'stock') {
  const currentStep = getDisplayStep(getCurrentStep(row), viewMode);
  if (currentStep?.ownerType === 'store' && STORE_PHYSICAL_SEND_TO_WORKSHOP_STEP_CODES.includes(currentStep.code)) {
    return 'En attente d’envoi BL';
  }
  if (currentStep?.code === 'MAGASIN_MONTAGE') return 'En attente de réception BL';
  if (currentStep) return getStepLabel(currentStep);
  return row.workflow?.displayStatus || row.frame?.displayLabel || '—';
}

function getSimpleCommandeStatus(row, viewMode = 'stock') {
  const currentStep = getDisplayStep(getCurrentStep(row), viewMode);
  if (!currentStep) return 'Terminé';
  if (currentStep.ownerType === 'store' && STORE_PHYSICAL_SEND_TO_WORKSHOP_STEP_CODES.includes(currentStep.code)) {
    return 'Magasin • En attente d’envoi BL';
  }
  if (currentStep.code === 'MAGASIN_MONTAGE') return 'Magasin • En attente de réception BL';
  if (currentStep.ownerType === 'store' && !row.workflow?.steps?.some((step) => step.completedAt)) {
    return 'Magasin • À envoyer';
  }
  if (currentStep.ownerType === 'store') return `Magasin • ${getStepLabel(currentStep)}`;
  if (currentStep.ownerType === 'workshop') return `Atelier • ${getStepLabel(currentStep)}`;
  return getStepLabel(currentStep);
}

function getCommandeCategoryLabel(row) {
  return isStockCommande(row) ? 'Verre de stock' : 'Verre fournisseur';
}

function getPrimaryActionLabel(row) {
  const step = getCurrentStep(row);
  if (!step) return '';
  const isFirstStep = row.workflow?.steps?.findIndex((item) => item.code === step.code) === 0;

  if (step.code === 'MAGASIN_DOSSIER_INFORMATION') return 'Commander les verres';
  if (step.ownerType === 'store' && isFirstStep) return 'Envoyer la commande';

  if (step.code === 'ATELIER_RECUPERE_VERRE_STOCK') return 'Picking de verre';
  if (step.code.includes('RECEPTION')) return 'Réceptionner';
  if (step.code.includes('ENVOI')) return step.ownerType === 'store' ? 'Envoyer la commande' : 'Envoyer';
  if (['ATELIER_RAPPROCHEMENT_MONTAGE', 'ATELIER_MONTAGE_RAPPROCHEMENT'].includes(step.code)) return 'Rapprocher';
  if (step.code === 'MAGASIN_MONTAGE') return 'Réceptionner le BL';
  if (step.code.includes('MONTAGE')) return 'Valider montage';
  if (step.code.includes('RAPPROCHEMENT')) return 'Valider rapprochement';
  if (step.code.includes('DOSSIER')) return 'Valider dossier';

  return 'Valider étape';
}

function getRowWorkflowActionLabel(row) {
  if (hasPendingMissingInitialFile(row)) return 'Régulariser dossier';
  const step = getCurrentStep(row);
  if (!step) return 'Terminé';

  const labelByStepCode = {
    MAGASIN_ENVOI_DOSSIER_MONTURE: 'Envoyer dossier',
    MAGASIN_DOSSIER_INFORMATION: 'Commander les verres',
    MAGASIN_CONSULTE_COMMANDE_STOCK: 'Envoyer commande',
    MAGASIN_ENVOI_MONTURE: 'Envoyer monture',
    MAGASIN_RECEPTION_VERRE: 'Réceptionner verre',
    MAGASIN_ENVOI_DOSSIER_MONTURE_VERRE: 'Envoyer monture/verre',
    MAGASIN_RECEPTION_DOSSIER_COMPLET: 'Réceptionner',
    MAGASIN_MONTAGE: 'Réceptionner le BL',
    ATELIER_RECEPTION_DOSSIER: 'Réceptionner dossier',
    ATELIER_RECEPTION_MONTURE: 'Réceptionner monture',
    ATELIER_RECEPTION_DOSSIER_COMMANDE_VERRE: 'Réceptionner verre',
    ATELIER_RECEPTION_VERRE: 'Réceptionner verre',
    ATELIER_RECUPERE_VERRE_STOCK: 'Récupérer verre',
    ATELIER_RAPPROCHEMENT: 'Rapprocher',
    ATELIER_MONTAGE: 'Rapprocher',
    ATELIER_RAPPROCHEMENT_MONTAGE: 'Rapprocher',
    ATELIER_MONTAGE_RAPPROCHEMENT: 'Rapprocher',
    ATELIER_PRET_ENVOI_MAGASIN: 'Préparer retour magasin',
    ATELIER_PRET_ENVOI_VERRE_MAGASIN: 'Envoi verre',
    ATELIER_ENVOI_VERRE_MAGASIN: 'Mettre en BL',
    ATELIER_ENVOI_LUNETTE_MAGASIN: 'Envoyer magasin',
  };

  return labelByStepCode[step.code] || getPrimaryActionLabel(row);
}

function getRowWorkflowButtonSx(row) {
  const code = getCurrentStep(row)?.code;

  if (['ATELIER_RECEPTION_DOSSIER', 'ATELIER_RECEPTION_DOSSIER_COMMANDE_VERRE'].includes(code)) {
    return bulkActionStyles.WORKSHOP_RECEIVE_FILE;
  }

  if (['ATELIER_RECEPTION_VERRE', 'ATELIER_RECUPERE_VERRE_STOCK'].includes(code)) {
    return bulkActionStyles.WORKSHOP_RECEIVE_LENS;
  }

  if (
    [
      'ATELIER_RAPPROCHEMENT',
      'ATELIER_MONTAGE',
      'ATELIER_RAPPROCHEMENT_MONTAGE',
      'ATELIER_MONTAGE_RAPPROCHEMENT',
    ].includes(code)
  ) {
    return bulkActionStyles.WORKSHOP_MATCH;
  }

  if (
    [
      'ATELIER_PRET_ENVOI_MAGASIN',
      'ATELIER_PRET_ENVOI_VERRE_MAGASIN',
      'ATELIER_ENVOI_VERRE_MAGASIN',
      'ATELIER_ENVOI_LUNETTE_MAGASIN',
      'MAGASIN_ENVOI_DOSSIER_MONTURE',
      'MAGASIN_DOSSIER_INFORMATION',
      'MAGASIN_CONSULTE_COMMANDE_STOCK',
      'MAGASIN_ENVOI_MONTURE',
      'MAGASIN_ENVOI_DOSSIER_MONTURE_VERRE',
      'MAGASIN_RECEPTION_DOSSIER_COMPLET',
      'MAGASIN_RECEPTION_VERRE',
    ].includes(code)
  ) {
    return bulkActionStyles.WORKSHOP_SEND;
  }

  return {
    color: '#ffffff',
    background: 'linear-gradient(135deg, #2563eb, #0ea5e9)',
    borderColor: '#60a5fa',
  };
}

function formatDiopter(value) {
  if (value === null || value === undefined || value === '') return '—';
  const number = Number(value);
  if (Number.isNaN(number)) return value;
  return `${number > 0 ? '+' : ''}${number.toFixed(2)}`;
}

function formatOpticalCorrection(sphere, cylinder) {
  const hasSphere = sphere !== null && sphere !== undefined && sphere !== '';
  const hasCylinder = cylinder !== null && cylinder !== undefined && cylinder !== '';
  if (!hasSphere && !hasCylinder) return '—';
  return `${hasSphere ? formatDiopter(sphere) : '—'} (${hasCylinder ? formatDiopter(cylinder) : '—'})`;
}

function getStockLensSideLines(row) {
  const lines = row?.lens?.stockReservationDetails?.length
    ? row.lens.stockReservationDetails
    : [{ stockItemId: row?.lens?.stockItemId, quantity: row?.lens?.quantity || 1 }];
  return lines
    .flatMap((line) => {
      const sides = Array.isArray(line.sides) ? line.sides : [];
      return sides.length
        ? sides.map((side) => ({ ...line, side, quantity: 1 }))
        : [{ ...line, side: '' }];
    })
    .sort((left, right) => ({ right: 0, left: 1 }[left.side] ?? 2) - ({ right: 0, left: 1 }[right.side] ?? 2));
}

function toDateInputValue(value) {
  if (!value) return '';
  return new Date(value).toISOString().slice(0, 10);
}

function toDateTimeLocalValue(value = new Date()) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
}

function toApiPayload(form) {
  const process = resolveProcessFromForm(form);
  const isStockLens = form.lensMode === 'stock';
  const mountingLocation = form.mountingLocation || (isStockLens ? 'store' : 'workshop');
  const computedSupplier = form.lensSupplier || undefined;

  return stripEmptyValues({
    jobNumber: form.jobNumber,
    workshopSheetNumber: isStockLens ? '' : form.workshopSheetNumber,
    storeSiteId: form.storeSiteId,
    workshopSiteId: form.workshopSiteId,
    customerId: form.customerId,
    customerSnapshot: {
      fullName: form.customerFullName,
    },
    advisorId: form.advisorId,
    advisorSnapshot: {
      fullName: form.advisorFullName,
    },
    frame: {
      type: form.frameType,
      displayLabel: form.frameDisplayLabel,
      mountingType: form.mountingType,
      sentToWorkshop: mountingLocation === 'workshop',
    },
    lens: {
      stockItemId: isStockLens ? form.stockItemId : '',
      mode: isStockLens ? 'stock' : 'supplier',
      supplier: computedSupplier,
      quantity: Number(form.lensQuantity || 1),
      isSpecificLens: !isStockLens,
    },
    mountingLocation,
    process,
    dates: {
      orderedAt: form.orderedAt || undefined,
      preparedAt: form.preparedAt || undefined,
      shippedAt: form.shippedAt || undefined,
    },
    print: {
      label: form.printLabel,
      isPrinted: form.isPrinted,
    },
  });
}

function fromApiItem(item) {
  const lensMode = item.lens?.mode === 'stock' || item.lens?.stockItemId ? 'stock' : 'supplier';

  return {
    jobNumber: item.jobNumber || '',
    workshopSheetNumber: item.workshopSheetNumber || '',
    process: item.process || 'STOCK_LENS_STORE',
    storeSiteId: getId(item.storeSiteId),
    workshopSiteId: getId(item.workshopSiteId),
    customerId: getId(item.customerId),
    customerFullName: item.customerSnapshot?.fullName || '',
    advisorId: getId(item.advisorId),
    advisorFullName: item.advisorSnapshot?.fullName || '',
    frameDisplayLabel: item.frame?.displayLabel || '',
    mountingType: item.frame?.mountingType || '',
    frameSentToWorkshop: item.frame?.sentToWorkshop ? 'yes' : 'no',
    frameType: item.frame?.type || (item.process === 'EXISTING_FRAME_NON_STOCK_LENS' ? 'existing' : 'new'),
    lensMode,
    mountingLocation:
      item.mountingLocation ||
      (NON_STOCK_STORE_MOUNT_PROCESS_CODES.includes(item.process) ? 'store' : '') ||
      (item.process === 'STOCK_LENS_WORKSHOP' || item.frame?.sentToWorkshop ? 'workshop' : 'store'),
    stockItemId: getId(item.lens?.stockItemId),
    lensSupplier: item.lens?.supplier || item.lens?.stockItemId?.supplier || 'ESSILOR',
    lensQuantity: item.lens?.quantity || 1,
    orderedAt: toDateInputValue(item.dates?.orderedAt),
    preparedAt: toDateInputValue(item.dates?.preparedAt),
    shippedAt: toDateInputValue(item.dates?.shippedAt),
    printLabel: item.print?.label || '',
    isPrinted: Boolean(item.print?.isPrinted),
  };
}

const STORE_MOUNT_PROCESS_CODES = ['NEW_FRAME_NON_STOCK_LENS_STORE_MOUNT', 'EXISTING_FRAME_NON_STOCK_LENS_STORE_MOUNT', 'STOCK_LENS_STORE'];
const WORKSHOP_MOUNT_PROCESS_CODES = ['NEW_FRAME_NON_STOCK_LENS', 'EXISTING_FRAME_NON_STOCK_LENS', 'RETAILLAGE', 'CLIP', 'STOCK_LENS_WORKSHOP'];

// Choix faits à la création du suivi, qui déterminent le workflow du dossier.
function getCommandeWorkflowChoices(commande) {
  if (!commande) return [];
  const process = commande.process || '';
  const assembly = STORE_MOUNT_PROCESS_CODES.includes(process)
    ? 'magasin'
    : WORKSHOP_MOUNT_PROCESS_CODES.includes(process)
      ? 'atelier'
      : commande.mountingLocation === 'store' || commande.mountingLocation === 'workshop'
        ? getMountingLocationLabel(commande.mountingLocation)
        : '';
  const choices = [
    { key: 'frame', label: getFrameTypeLabel(commande.frame?.type, process, commande.dailyOrder) },
    commande.frame?.origin && { key: 'origin', label: getFrameOriginLabel(commande.frame) },
    assembly && { key: 'assembly', label: `Assemblage ${assembly}` },
    NON_STOCK_STORE_MOUNT_PROCESS_CODES.includes(process) && {
      key: 'teleEdging',
      label: `Taillage AMOI : ${commande.frame?.teleEdging ? 'oui' : 'non'}`,
    },
    { key: 'lens', label: getCommandeCategoryLabel(commande) },
  ];
  return choices.filter(Boolean);
}

function getDispatchItemDossierState(commande, viewMode) {
  if (!commande) return '';
  if (commande.active === false) return 'Dossier inactif';
  if (commande.workflow?.dispatchRecovery?.pending) {
    return commande.workflow.dispatchRecovery.direction === 'workshop_to_store'
      ? 'Dossier à renvoyer par l’atelier'
      : 'Dossier à renvoyer par le magasin';
  }
  return getSimpleCommandeStatus(commande, viewMode);
}

const DISPATCH_LIST_STATUS_OPTIONS = [
  ['draft', 'À envoyer'],
  ['sent', 'En attente réception'],
  ['partial', 'Partiel'],
  ['received', 'Réceptionné'],
];

const toDispatchListRow = (list) => ({
  ...list,
  id: list._id,
  directionLabel: (list.direction || 'store_to_workshop') === 'workshop_to_store'
    ? 'Atelier → Magasin'
    : 'Magasin → Atelier',
  statusLabel:
    list.status === 'draft'
      ? 'À envoyer'
      : list.status === 'sent' || list.status === 'pending'
        ? 'En attente réception'
        : list.status === 'received'
          ? 'Réceptionné'
          : list.status === 'partial'
            ? 'Partiel'
            : list.status || '—',
  itemCount: (list.items || []).length,
  storeLabel: list.storeSiteId?.code || getSiteLabel(list.storeSiteId),
  workshopLabel: list.workshopSiteId?.code || getSiteLabel(list.workshopSiteId),
});

export default function CommandesPage({ viewMode = 'stock', dispatchOnly = false, menuPath = '' }) {
  const [searchParams] = useSearchParams();
  const menuPage = useMenuPage(
    dispatchOnly ? menuPath || '/suivi-atelier/bl' : menuPath || (viewMode === 'nonStock' ? '/suivi-atelier' : '/stock/suivi')
  );
  const { isAdmin, site, siteType } = useSiteSession();
  const viewConfig = getCommandesViewConfig(viewMode);
  const isStockFollowupView = viewMode === 'stock';
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [initialReservation, setInitialReservation] = useState({ stockItemId: '', quantity: 0 });
  const [viewingRow, setViewingRow] = useState(null);
  const [workflowCorrectionOpen, setWorkflowCorrectionOpen] = useState(false);
  const [workflowCorrectionSteps, setWorkflowCorrectionSteps] = useState([]);
  const [workflowCorrectionSaving, setWorkflowCorrectionSaving] = useState(false);
  const [workflowCorrectionError, setWorkflowCorrectionError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [workflowOperatorId, setWorkflowOperatorId] = useState('');
  const [bulkActionCode, setBulkActionCode] = useState('WORKSHOP_RECEIVE_FILE');
  const [workshopBulkTab, setWorkshopBulkTab] = useState('WORKSHOP_RECEIVE_FILE');
  const [workshopBulkDialogOpen, setWorkshopBulkDialogOpen] = useState(false);
  const [storeBulkTab, setStoreBulkTab] = useState('STORE_CREATE');
  const [storeBulkDialogOpen, setStoreBulkDialogOpen] = useState(false);
  const [bulkOperatorId, setBulkOperatorId] = useState('');
  const [bulkJobInput, setBulkJobInput] = useState('');
  const [bulkMountingType, setBulkMountingType] = useState('');
  const [storeSendFrameType, setStoreSendFrameType] = useState('new');
  const [storeSendMountingLocation, setStoreSendMountingLocation] = useState('workshop');
  const [storeSendTeleEdging, setStoreSendTeleEdging] = useState(false);
  const [storeSendCustomerLastName, setStoreSendCustomerLastName] = useState('');
  const [storeSendCustomerFirstName, setStoreSendCustomerFirstName] = useState('');
  const [storeSendFrameOrigin, setStoreSendFrameOrigin] = useState('');
  const [storeSendFrameOriginCode, setStoreSendFrameOriginCode] = useState('');
  const [storeSendWorkshopSiteId, setStoreSendWorkshopSiteId] = useState('');
  const [bulkResults, setBulkResults] = useState(null);
  const [, setBulkSaving] = useState(false);
  const [dispatchListNumberInput, setDispatchListNumberInput] = useState('');
  const [dispatchListSaving, setDispatchListSaving] = useState(false);
  const [dispatchLists, setDispatchLists] = useState([]);
  const [, setDispatchListsLoading] = useState(false);
  const [dispatchCreateOpen, setDispatchCreateOpen] = useState(false);
  const [dispatchCreateDirection, setDispatchCreateDirection] = useState('store_to_workshop');
  const [dispatchScanInput, setDispatchScanInput] = useState('');
  const [dispatchScanRows, setDispatchScanRows] = useState([]);
  const [dispatchSendOpen, setDispatchSendOpen] = useState(false);
  const [dispatchSendNumberInput, setDispatchSendNumberInput] = useState('');
  const [dispatchSenderUserId, setDispatchSenderUserId] = useState('');
  const [autoReturnBlOpen, setAutoReturnBlOpen] = useState(false);
  const [autoReturnBlSelection, setAutoReturnBlSelection] = useState([]);
  const [autoReturnBlSaving, setAutoReturnBlSaving] = useState(false);
  const [autoReturnBlPreviewOpen, setAutoReturnBlPreviewOpen] = useState(false);
  const [autoReturnBlPreview, setAutoReturnBlPreview] = useState(null);
  const [dispatchReceiveOpen, setDispatchReceiveOpen] = useState(false);
  const [partialReceiveTarget, setPartialReceiveTarget] = useState(null);
  const [partialReceiveIds, setPartialReceiveIds] = useState([]);
  const [dispatchDetailTarget, setDispatchDetailTarget] = useState(null);
  const [dispatchDetailCommandes, setDispatchDetailCommandes] = useState({ listId: '', loading: false, byId: new Map() });
  const [dispatchEditTarget, setDispatchEditTarget] = useState(null);
  const [dispatchEditCommandeIds, setDispatchEditCommandeIds] = useState([]);
  const [dispatchEditCheckedIds, setDispatchEditCheckedIds] = useState([]);
  const [dispatchListKpiFilter, setDispatchListKpiFilter] = useState('');
  const [serverDispatchListKpis, setServerDispatchListKpis] = useState(null);
  const [cardDialog, setCardDialog] = useState(null);
  const [cardDialogFilters, setCardDialogFilters] = useState({ search: '', store: '', step: '' });
  const [cardDialogSelection, setCardDialogSelection] = useState([]);
  const [cardDialogBulkMountingType, setCardDialogBulkMountingType] = useState('');
  const [dispatchListFilters, setDispatchListFilters] = useState({
    global: '',
    listNumber: '',
    status: '',
    store: '',
    workshop: '',
  });
  const [dispatchListSort, setDispatchListSort] = useState({ field: 'createdAt', direction: 'desc' });
  // Tableau des BL paginé côté serveur, indépendant de `dispatchLists` (100 BL
  // récents) qui alimente les autres outils de la page.
  const [dispatchTable, setDispatchTable] = useState({ rows: [], total: 0, loading: false });
  const [dispatchTablePage, setDispatchTablePage] = useState(0);
  const [dispatchTableRowsPerPage, setDispatchTableRowsPerPage] = useState(50);
  const [dispatchTableSearch, setDispatchTableSearch] = useState({ global: '', listNumber: '' });
  const [workflowDocumentationOpen, setWorkflowDocumentationOpen] = useState(false);
  const [pickingOpen, setPickingOpen] = useState(false);
  const [pickingJobNumber, setPickingJobNumber] = useState('');
  const [pickingRightBarcode, setPickingRightBarcode] = useState('');
  const [pickingLeftBarcode, setPickingLeftBarcode] = useState('');
  const [pickingOperatorId, setPickingOperatorId] = useState('');
  const [pickingSaving, setPickingSaving] = useState(false);
  const [mountingTypeOpen, setMountingTypeOpen] = useState(false);
  const [mountingTypeRow, setMountingTypeRow] = useState(null);
  const [mountingTypeValue, setMountingTypeValue] = useState('');
  const [mountingTypeMode, setMountingTypeMode] = useState('send');
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuRow, setActionMenuRow] = useState(null);
  const [nonConformityOpen, setNonConformityOpen] = useState(false);
  const [nonConformityRow, setNonConformityRow] = useState(null);
  const [nonConformityJobNumber, setNonConformityJobNumber] = useState('');
  const [nonConformityForm, setNonConformityForm] = useState({ type: 'NCA', scope: '', reason: '', comment: '' });
  const [nonConformityPhotos, setNonConformityPhotos] = useState([]);
  const [nonConformityPhotoBusy, setNonConformityPhotoBusy] = useState(false);
  const [nonConformitySaving, setNonConformitySaving] = useState(false);
  const [cancelNonConformityTarget, setCancelNonConformityTarget] = useState(null);
  const [cancelNonConformityReason, setCancelNonConformityReason] = useState('');
  const [cancelNonConformitySaving, setCancelNonConformitySaving] = useState(false);
  const [traceabilityOpen, setTraceabilityOpen] = useState(false);
  const [traceabilityRow, setTraceabilityRow] = useState(null);
  const [traceabilityForm, setTraceabilityForm] = useState(emptyTraceabilityForm);
  const [traceabilityTechnicianId, setTraceabilityTechnicianId] = useState('');
  const [traceabilitySaving, setTraceabilitySaving] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    description: '',
    confirmLabel: 'Confirmer',
    color: 'primary',
    onConfirm: null,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'info',
    message: '',
  });
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState({
    job: '',
    category: '',
    store: '',
    mountingType: '',
    teleEdging: '',
    mountingLocation: '',
    frameType: '',
    status: '',
    nca: [],
  });
  const [updatedAtRange, setUpdatedAtRange] = useState({ from: '', to: '' });
  const [workshopKpiFilter, setWorkshopKpiFilter] = useState('');
  const useServerPagination = !dispatchOnly && (isAdmin || siteType === 'workshop');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [serverRowCount, setServerRowCount] = useState(0);
  const [serverWorkshopKpis, setServerWorkshopKpis] = useState(null);
  const rowsRequestVersionRef = useRef(0);
  const deferredSearch = useDeferredValue(search);
  const deferredColumnFilters = useDeferredValue(columnFilters);
  const [blockedThresholdDays, setBlockedThresholdDays] = useState(3);
  const [mountingTypeOptions, setMountingTypeOptions] = useState(DEFAULT_MOUNTING_TYPE_OPTIONS);
  const [nonConformityScopeOptions, setNonConformityScopeOptions] = useState(DEFAULT_NON_CONFORMITY_SCOPE_OPTIONS);
  const [nonConformityReasonOptions, setNonConformityReasonOptions] = useState(DEFAULT_NON_CONFORMITY_REASON_OPTIONS);
  const [lensSearch, setLensSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const canCreateOrder = false;
  const canWorkshopDispatchTools = (isAdmin || siteType === 'workshop') && (viewMode === 'nonStock' || isStockFollowupView || dispatchOnly);
  const canBulkWorkshopScan = canWorkshopDispatchTools && !dispatchOnly;
  const canPickStockLenses = canWorkshopDispatchTools && !dispatchOnly;

  const loadRows = useCallback(async ({ silent = false } = {}) => {
    const requestVersion = rowsRequestVersionRef.current + 1;
    rowsRequestVersionRef.current = requestVersion;
    try {
      if (!silent) setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (useServerPagination) {
        params.set('page', String(paginationModel.page + 1));
        params.set('limit', String(paginationModel.pageSize));
        params.set('sort', 'updatedAt:desc');
        if (viewMode === 'stock') params.set('view', 'stock');
        if (workshopKpiFilter) params.set('kpi', workshopKpiFilter);
        if (deferredSearch.trim()) params.set('search', deferredSearch.trim());
        Object.entries(deferredColumnFilters).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            const selectedValues = value.map((item) => String(item || '').trim()).filter(Boolean);
            if (selectedValues.length) params.set(key, selectedValues.join(','));
            return;
          }
          if (String(value || '').trim()) params.set(key, String(value).trim());
        });
        if (updatedAtRange.from) params.set('updatedFrom', updatedAtRange.from);
        if (updatedAtRange.to) params.set('updatedTo', updatedAtRange.to);
      } else {
        params.set('limit', '5000');
      }
      const payload = await requestJson(`/commandes?${params.toString()}`);
      if (rowsRequestVersionRef.current !== requestVersion) return;
      setRows(getCollection(payload));
      if (useServerPagination) setServerRowCount(Number(payload.pagination?.total || 0));
    } catch (err) {
      if (rowsRequestVersionRef.current !== requestVersion) return;
      setError(err.message);
      if (!silent) setRows([]);
    } finally {
      if (!silent && rowsRequestVersionRef.current === requestVersion) setLoading(false);
    }
  }, [deferredColumnFilters, deferredSearch, paginationModel, updatedAtRange, useServerPagination, viewMode, workshopKpiFilter]);

  useEffect(() => {
    if (!useServerPagination) return;
    setPaginationModel((current) => (current.page === 0 ? current : { ...current, page: 0 }));
  }, [deferredColumnFilters, deferredSearch, updatedAtRange, useServerPagination, viewMode, workshopKpiFilter]);

  const loadDispatchLists = useCallback(async ({ silent = false } = {}) => {
    if (viewMode !== 'nonStock' && !isStockFollowupView) return;
    try {
      if (!silent) setDispatchListsLoading(true);
      // Les compteurs sont lus après la liste : le GET de la liste peut clôturer
      // automatiquement des BL déjà réceptionnés dossier par dossier.
      const payload = await requestJson('/commandes/workflow/dispatch-lists');
      setDispatchLists(getCollection(payload));
      const kpis = await requestJson('/commandes/workflow/dispatch-lists/kpis').catch(() => null);
      setServerDispatchListKpis(kpis);
    } catch (_err) {
      setDispatchLists([]);
    } finally {
      if (!silent) setDispatchListsLoading(false);
    }
  }, [isStockFollowupView, viewMode]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDispatchTableSearch({ global: dispatchListFilters.global.trim(), listNumber: dispatchListFilters.listNumber.trim() });
    }, 300);
    return () => clearTimeout(timer);
  }, [dispatchListFilters.global, dispatchListFilters.listNumber]);

  useEffect(() => {
    setDispatchTablePage(0);
  }, [
    dispatchTableSearch,
    dispatchListFilters.status,
    dispatchListFilters.store,
    dispatchListFilters.workshop,
    dispatchListKpiFilter,
    dispatchListSort,
    dispatchTableRowsPerPage,
  ]);

  const dispatchTableRequestRef = useRef(0);
  const loadDispatchTable = useCallback(async () => {
    if (!dispatchOnly) return;
    const requestId = dispatchTableRequestRef.current + 1;
    dispatchTableRequestRef.current = requestId;
    const params = new URLSearchParams({
      page: String(dispatchTablePage + 1),
      limit: String(dispatchTableRowsPerPage),
      sort: dispatchListSort.field,
      order: dispatchListSort.direction,
    });
    if (dispatchTableSearch.global) params.set('global', dispatchTableSearch.global);
    if (dispatchTableSearch.listNumber) params.set('listNumber', dispatchTableSearch.listNumber);
    if (dispatchListKpiFilter) params.set('kpi', dispatchListKpiFilter);
    if (dispatchListFilters.status) params.set('status', dispatchListFilters.status);
    if (dispatchListFilters.store) params.set('storeSiteId', dispatchListFilters.store);
    if (dispatchListFilters.workshop) params.set('workshopSiteId', dispatchListFilters.workshop);
    setDispatchTable((current) => ({ ...current, loading: true }));
    try {
      const payload = await requestJson(`/commandes/workflow/dispatch-lists?${params.toString()}`);
      if (dispatchTableRequestRef.current !== requestId) return;
      setDispatchTable({ rows: getCollection(payload), total: payload.pagination?.total || 0, loading: false });
    } catch (_err) {
      if (dispatchTableRequestRef.current !== requestId) return;
      setDispatchTable({ rows: [], total: 0, loading: false });
    }
  }, [
    dispatchListFilters.status,
    dispatchListFilters.store,
    dispatchListFilters.workshop,
    dispatchListKpiFilter,
    dispatchListSort,
    dispatchOnly,
    dispatchTablePage,
    dispatchTableRowsPerPage,
    dispatchTableSearch,
  ]);

  // `dispatchLists` est rechargé après chaque action et chaque événement temps
  // réel : le tableau paginé suit ces rechargements.
  useEffect(() => {
    loadDispatchTable();
  }, [loadDispatchTable, dispatchLists]);

  const dispatchDetailListId = dispatchDetailTarget?._id || '';
  useEffect(() => {
    if (!dispatchDetailListId) return undefined;
    let cancelled = false;
    setDispatchDetailCommandes({ listId: dispatchDetailListId, loading: true, byId: new Map() });
    requestJson(`/commandes/workflow/dispatch-lists/${dispatchDetailListId}/details`)
      .then((payload) => {
        if (cancelled) return;
        const byId = new Map(getCollection(payload)
          .filter((entry) => entry.commande)
          .map((entry) => [String(entry.commandeId), entry.commande]));
        setDispatchDetailCommandes({ listId: dispatchDetailListId, loading: false, byId });
      })
      .catch(() => {
        if (!cancelled) setDispatchDetailCommandes({ listId: dispatchDetailListId, loading: false, byId: new Map() });
      });
    return () => { cancelled = true; };
  }, [dispatchDetailListId, dispatchLists]);

  const loadWorkshopKpis = useCallback(async () => {
    if (!isAdmin && !['workshop', 'store'].includes(siteType)) return;
    try {
      const payload = await requestJson(`/commandes/workflow/workshop-kpis${viewMode === 'stock' ? '?view=stock' : ''}`);
      setServerWorkshopKpis(payload);
    } catch (_err) {
      setServerWorkshopKpis(null);
    }
  }, [isAdmin, siteType, viewMode]);

  const loadLookups = useCallback(async () => {
    try {
      const [sitesPayload, stockPayload, customersPayload, usersPayload, settingsPayload] = await Promise.all([
        requestJson('/sites'),
        requestJson('/stock-items'),
        requestJson('/customers'),
        requestJson('/users?limit=2000'),
        requestJson('/settings/STOCK'),
      ]);
      setSites(getCollection(sitesPayload));
      setStockItems(getCollection(stockPayload));
      setCustomers(getCollection(customersPayload));
      setUsers(getCollection(usersPayload));
      setBlockedThresholdDays(Number(settingsPayload.value?.workshopBlockedDays || 3));
      const configuredMountingTypes = Array.isArray(settingsPayload.value?.mountingTypes)
        ? settingsPayload.value.mountingTypes
            .map((item) => String(item || '').trim())
            .filter((item) => item && !HIDDEN_MOUNTING_TYPE_OPTIONS.has(item))
        : [];
      setMountingTypeOptions(configuredMountingTypes.length ? configuredMountingTypes : DEFAULT_MOUNTING_TYPE_OPTIONS);
      const configuredScopes = Array.isArray(settingsPayload.value?.nonConformityScopes)
        ? settingsPayload.value.nonConformityScopes
            .map((item) => ({
              value: String(item?.value || '').trim(),
              label: String(item?.label || item?.value || '').trim(),
            }))
            .filter((item) => item.value && item.label)
        : [];
      const nextScopes = configuredScopes.length ? configuredScopes : DEFAULT_NON_CONFORMITY_SCOPE_OPTIONS;
      const allowedScopes = new Set(nextScopes.map((item) => item.value));
      const configuredReasons = Array.isArray(settingsPayload.value?.nonConformityReasons)
        ? settingsPayload.value.nonConformityReasons
            .map((item) => ({
              value: String(item?.value || '').trim(),
              label: String(item?.label || item?.value || '').trim(),
              scopes: (Array.isArray(item?.scopes) ? item.scopes : [])
                .map((scope) => String(scope || '').trim())
                .filter((scope) => allowedScopes.has(scope)),
            }))
            .filter((item) => item.value && item.label && item.scopes.length)
        : [];
      setNonConformityScopeOptions(nextScopes);
      setNonConformityReasonOptions(configuredReasons.length ? configuredReasons : DEFAULT_NON_CONFORMITY_REASON_OPTIONS);
    } catch (_err) {
      setSites([]);
      setStockItems([]);
      setCustomers([]);
      setUsers([]);
      setMountingTypeOptions(DEFAULT_MOUNTING_TYPE_OPTIONS);
      setNonConformityScopeOptions(DEFAULT_NON_CONFORMITY_SCOPE_OPTIONS);
      setNonConformityReasonOptions(DEFAULT_NON_CONFORMITY_REASON_OPTIONS);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    loadDispatchLists();
  }, [loadDispatchLists]);

  useEffect(() => {
    loadLookups();
    loadWorkshopKpis();
  }, [loadLookups, loadWorkshopKpis]);

  useEffect(() => {
    if (!error) return;
    setSnackbar({
      open: true,
      severity: 'error',
      message: error,
    });
  }, [error]);

  useEffect(() => {
    if (!snackbar.open || !snackbar.message) return;
    playActionSound(snackbar.severity);
  }, [snackbar.open, snackbar.message, snackbar.severity]);

  useEffect(() => {
    if ((viewMode !== 'nonStock' && !isStockFollowupView) || siteType !== 'store') return;
    if (bulkActionCode.startsWith('STORE_')) return;
    setBulkActionCode(storeBulkTab);
  }, [bulkActionCode, isStockFollowupView, siteType, storeBulkTab, viewMode]);

  useEffect(() => {
    const job = searchParams.get('job') || '';
    const q = searchParams.get('q') || '';
    const nca = searchParams.get('nca') === '1' ? 'Avec non-conformité' : '';
    if (!job && !q && !nca) return;

    setSearch(q);
    setWorkshopKpiFilter('');
    setColumnFilters((current) => ({
      ...current,
      job: job || '',
      nca: nca || current.nca || '',
    }));
  }, [searchParams, viewMode]);

  // La recherche globale transmet l'identifiant de la commande afin d'ouvrir
  // directement sa fiche de suivi, sans obliger l'utilisateur à retrouver la
  // ligne dans le tableau filtré.
  useEffect(() => {
    const commandeId = searchParams.get('commandeId') || '';
    if (!commandeId) return undefined;

    let cancelled = false;
    requestJson(`/commandes/${encodeURIComponent(commandeId)}`)
      .then((payload) => {
        if (cancelled) return;
        setViewingRow(payload?.data || payload);
      })
      .catch(() => {
        // Le filtre JOB reste actif même si le détail n'est momentanément pas
        // accessible (droits, commande supprimée ou réponse réseau).
      });

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const realtimeDirtyRef = useRef({ commandes: false, dispatchLists: false, kpis: false, lookups: false });
  const realtimeEntityVersionsRef = useRef(new Map());
  const scheduleRealtimeRefresh = useCoalescedRefresh(async () => {
    const dirty = realtimeDirtyRef.current;
    realtimeDirtyRef.current = { commandes: false, dispatchLists: false, kpis: false, lookups: false };
    await Promise.all([
      dirty.commandes ? loadRows({ silent: true }) : Promise.resolve(),
      dirty.dispatchLists ? loadDispatchLists({ silent: true }) : Promise.resolve(),
      dirty.kpis ? loadWorkshopKpis() : Promise.resolve(),
      dirty.lookups ? loadLookups() : Promise.resolve(),
    ]);
  }, 350);
  const requestRealtimeRefresh = useCallback((dirty = {}) => {
    const current = realtimeDirtyRef.current;
    realtimeDirtyRef.current = {
      commandes: current.commandes || Boolean(dirty.commandes),
      dispatchLists: current.dispatchLists || Boolean(dirty.dispatchLists),
      kpis: current.kpis || Boolean(dirty.kpis),
      lookups: current.lookups || Boolean(dirty.lookups),
    };
    scheduleRealtimeRefresh();
  }, [scheduleRealtimeRefresh]);

  const applyRealtimeCommande = useCallback((commande) => {
    const commandeId = String(commande?._id || '');
    if (!commandeId) return;

    const belongsToCurrentSession = isAdmin || (
      siteType === 'store'
        ? getId(commande.storeSiteId) === site?._id
        : siteType === 'workshop'
          ? getId(commande.workshopSiteId) === site?._id
          : false
    );

    setRows((current) => {
      const withoutCommande = current.filter((item) => String(item._id) !== commandeId);
      if (!belongsToCurrentSession) return withoutCommande;
      return [commande, ...withoutCommande].sort(
        (left, right) => new Date(right.createdAt || 0) - new Date(left.createdAt || 0)
      );
    });
    setViewingRow((current) => (String(current?._id || '') === commandeId ? commande : current));
  }, [isAdmin, site?._id, siteType]);

  const removeRealtimeCommande = useCallback((commandeId) => {
    const normalizedId = String(commandeId || '');
    if (!normalizedId) return;
    setRows((current) => current.filter((item) => String(item._id) !== normalizedId));
    setViewingRow((current) => (String(current?._id || '') === normalizedId ? null : current));
  }, []);

  useEffect(() => {
    const socket = getSocket();
    const refreshCommandes = (event = {}) => {
      if (event?.hasEntityEvent) return;
      requestRealtimeRefresh({ commandes: true, kpis: true, dispatchLists: true });
    };
    const updateCommandeEntity = async (event = {}) => {
      if (event.entity !== 'commande' || !event.id) return;
      if (useServerPagination) {
        requestRealtimeRefresh({ commandes: true, kpis: true });
        return;
      }
      if (event.operation === 'deleted' || event.operation === 'removed') {
        removeRealtimeCommande(event.id);
        requestRealtimeRefresh({ kpis: true, dispatchLists: true });
        return;
      }

      const version = (realtimeEntityVersionsRef.current.get(event.id) || 0) + 1;
      realtimeEntityVersionsRef.current.set(event.id, version);
      const hasCompleteData = event.data?._id && event.data?.jobNumber !== undefined && event.data?.workflow;
      if (hasCompleteData) {
        applyRealtimeCommande(event.data);
        requestRealtimeRefresh({ kpis: true, dispatchLists: true });
        return;
      }

      try {
        const commande = await requestJson(`/commandes/${event.id}`);
        if (realtimeEntityVersionsRef.current.get(event.id) !== version) return;
        applyRealtimeCommande(commande?.data || commande);
      } catch (requestError) {
        if (realtimeEntityVersionsRef.current.get(event.id) !== version) return;
        if ([403, 404].includes(requestError?.status)) removeRealtimeCommande(event.id);
        else requestRealtimeRefresh({ commandes: true, kpis: true });
      }
    };
    const refreshStockAndCommandes = () => {
      requestRealtimeRefresh({ commandes: true, kpis: true, lookups: true });
    };

    socket.on('commandes:changed', refreshCommandes);
    socket.on('entity:changed', updateCommandeEntity);
    socket.on('stock-items:changed', refreshStockAndCommandes);

    return () => {
      socket.off('commandes:changed', refreshCommandes);
      socket.off('entity:changed', updateCommandeEntity);
      socket.off('stock-items:changed', refreshStockAndCommandes);
    };
  }, [applyRealtimeCommande, removeRealtimeCommande, requestRealtimeRefresh, useServerPagination]);

  const storeSites = useMemo(() => sites.filter((site) => site.type === 'store'), [sites]);
  const workshopSites = useMemo(() => sites.filter((site) => site.type === 'workshop'), [sites]);
  const preferredWorkshopSite = useMemo(
    () =>
      workshopSites.find((workshopSite) => String(workshopSite.code || '').toUpperCase() === 'AMOI') ||
      workshopSites.find((workshopSite) => String(getSiteLabel(workshopSite) || '').toUpperCase().includes('AMOI')) ||
      workshopSites[0] ||
      null,
    [workshopSites]
  );
  const defaultWorkshopSiteId = preferredWorkshopSite?._id || '';
  const workshopUsers = useMemo(
    () => users.filter((user) => user.active !== false && (user.siteId?.type === 'workshop' || (user.siteIds || []).some((siteItem) => siteItem?.type === 'workshop'))),
    [users]
  );
  const storeUsers = useMemo(
    () => users.filter((user) => user.active !== false && (user.siteId?.type === 'store' || (user.siteIds || []).some((siteItem) => siteItem?.type === 'store'))),
    [users]
  );
  const advisorOptions = useMemo(
    () =>
      isAdmin
        ? users
        : users.filter((user) => userHasSite(user, site?._id)),
    [isAdmin, site, users]
  );
  const availableStockItems = useMemo(
    () =>
      stockItems.filter(
        (item) =>
          (!form.workshopSiteId || getId(item.siteId) === form.workshopSiteId) &&
          (Number(item.availableQty || 0) > 0 || getId(item) === form.stockItemId)
      ),
    [stockItems, form.stockItemId, form.workshopSiteId]
  );

  useEffect(() => {
    if (!defaultWorkshopSiteId || form.workshopSiteId) return;
    setForm((current) => ({ ...current, workshopSiteId: current.workshopSiteId || defaultWorkshopSiteId }));
  }, [defaultWorkshopSiteId, form.workshopSiteId]);

  useEffect(() => {
    if (!defaultWorkshopSiteId || storeSendWorkshopSiteId) return;
    setStoreSendWorkshopSiteId(defaultWorkshopSiteId);
  }, [defaultWorkshopSiteId, storeSendWorkshopSiteId]);

  // The workshop follow-up is shared: supplier lenses and stock lenses can be sent in the same BL.
  const rowsForView = useMemo(
    () => (viewMode === 'nonStock' ? rows : rows.filter(isStockCommande)),
    [rows, viewMode]
  );
  const activeDispatchListByCommandeId = useMemo(() => {
    // A partial BL remains available for history, but must not block a new
    // dispatch cycle. Only unsent/unreceived BLs reserve a JOB.
    const openStatuses = new Set(['draft', 'sent', 'pending']);
    const memberships = new Map();

    dispatchLists
      .filter((list) => openStatuses.has(list.status))
      .forEach((list) => {
        (list.items || []).forEach((item) => {
          // A partial/active BL can retain historical lines that were already
          // received or skipped.  Those lines must remain visible in the BL
          // history, but they must not block the JOB or display as an active
          // association in the follow-up table.  Only pending (or legacy
          // items without a status) are still physically in the BL.
          if (item.status && !['pending'].includes(item.status)) return;
          const commandeId = String(item.commandeId?._id || item.commandeId || '');
          if (!commandeId || memberships.has(commandeId)) return;
          memberships.set(commandeId, {
            listNumber: list.listNumber,
            status: list.status,
            direction: list.direction || 'store_to_workshop',
          });
        });
      });

    return memberships;
  }, [dispatchLists]);
  const getActiveDispatchListForCommande = useCallback(
    (row) => activeDispatchListByCommandeId.get(String(row?._id || row?.id || '')) || null,
    [activeDispatchListByCommandeId]
  );
  const canStoreEditInitialCommande = useCallback(
    (row) => {
      if (isAdmin || siteType !== 'store' || getId(row?.storeSiteId) !== site?._id) return false;
      if (getActiveDispatchListForCommande(row)) return false;
      return !(row?.workflow?.steps || []).some((step) => step.completedAt);
    },
    [getActiveDispatchListForCommande, isAdmin, site, siteType]
  );

  const workshopFilterOptions = useMemo(() => {
    const unique = (values) => [...new Set(values.filter(Boolean))].sort();
    return {
      jobs: unique(rowsForView.map((row) => row.workshopSheetNumber || row.jobNumber)),
      categories: ['Verre fournisseur', 'Verre de stock'],
      stores: unique(rowsForView.map((row) => [row.storeSiteId?.code, getLabel(row.storeSiteId, '')].filter(Boolean).join(' - '))),
      mountingTypes: unique(rowsForView.map((row) => row.frame?.mountingType || '—')),
      teleEdging: ['Oui', 'Non'],
      mountingLocations: ['Atelier', 'Magasin'],
      frameTypes: ['Simple Boucle', 'Commande journalière', 'Deuxième Boucle', 'Retaillage', 'Clip', 'Palets bruts'],
      statuses: unique(rowsForView.map((row) => getSimpleCommandeStatus(row, viewMode))),
      ncas: WORKSHOP_NON_CONFORMITY_FILTER_OPTIONS,
    };
  }, [rowsForView, viewMode]);

  const workshopKpis = useMemo(() => {
    const scopedRows = rowsForView.filter((row) => {
      if (viewMode !== 'nonStock' && !isStockFollowupView) return false;
      if (isAdmin) return true;
      if (siteType !== 'workshop') return false;

      return getId(row.workshopSiteId) === site?._id;
    });

    return {
      awaitingDispatch: scopedRows.filter((row) => isDispatchListEligible(row) && !getActiveDispatchListForCommande(row)).length,
      awaitingLens: scopedRows.filter(isAwaitingWorkshopLens).length,
      awaitingMatching: scopedRows.filter(isAwaitingWorkshopMatching).length,
      awaitingStoreDispatch: scopedRows.filter(isAwaitingWorkshopDispatchPreparation).length,
      lensReceptionLate: scopedRows.filter((row) => isLensReceptionLate(row, 20)).length,
      matchingLate: scopedRows.filter((row) => isMatchingLate(row, 3)).length,
      lensWithoutDossier: scopedRows.filter((row) => isLensWithoutDossierLate(row, 6)).length,
    };
  }, [getActiveDispatchListForCommande, isAdmin, isStockFollowupView, rowsForView, site, siteType, viewMode]);

  const storeFollowupKpis = useMemo(() => {
    const scopedRows = rowsForView.filter((row) => {
      if (viewMode !== 'nonStock' && !isStockFollowupView) return false;
      if (isAdmin) return true;
      if (siteType !== 'store') return false;

      const storeId = getId(row.storeSiteId);
      const currentStep = getCurrentStep(row);

      return currentStep?.ownerType === 'store' || storeId === site?._id;
    });

    const storeIssueRows = rowsForView.filter((row) => {
      if (viewMode !== 'nonStock' && !isStockFollowupView) return false;
      if (isAdmin) return true;
      if (siteType !== 'store') return false;
      return getId(row.storeSiteId) === site?._id;
    });

    return {
      toReceive: scopedRows.filter(isStoreCommandeToReceive).length,
      pendingWorkshopIssue: storeIssueRows.filter(isStorePendingWorkshopIssue).length,
    };
  }, [isAdmin, isStockFollowupView, rowsForView, site, siteType, viewMode]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const normalizedFilters = {
      job: columnFilters.job.trim().toLowerCase(),
      category: columnFilters.category.trim().toLowerCase(),
      store: columnFilters.store.trim().toLowerCase(),
      mountingType: columnFilters.mountingType.trim().toLowerCase(),
      teleEdging: columnFilters.teleEdging.trim().toLowerCase(),
      mountingLocation: columnFilters.mountingLocation.trim().toLowerCase(),
      frameType: columnFilters.frameType.trim().toLowerCase(),
      status: columnFilters.status.trim().toLowerCase(),
      nca: (Array.isArray(columnFilters.nca) ? columnFilters.nca : [columnFilters.nca])
        .map((value) => String(value || '').trim())
        .filter(Boolean),
    };

    return rowsForView.filter((row) => {
      if (workshopKpiFilter === 'awaitingDispatch' && (!isDispatchListEligible(row) || getActiveDispatchListForCommande(row))) return false;
      if (workshopKpiFilter === 'awaitingLens' && !isAwaitingWorkshopLens(row)) return false;
      if (workshopKpiFilter === 'awaitingMatching' && !isAwaitingWorkshopMatching(row)) return false;
      if (workshopKpiFilter === 'awaitingStoreDispatch' && !isAwaitingWorkshopDispatchPreparation(row)) return false;
      if (workshopKpiFilter === 'lensReceptionLate' && !isLensReceptionLate(row, 20)) return false;
      if (workshopKpiFilter === 'matchingLate' && !isMatchingLate(row, 3)) return false;
      if (workshopKpiFilter === 'lensWithoutDossier' && !isLensWithoutDossierLate(row, 6)) return false;
      if (workshopKpiFilter === 'storeToReceive' && !isStoreCommandeToReceive(row)) return false;
      if (workshopKpiFilter === 'storePendingWorkshopIssue' && !isStorePendingWorkshopIssue(row)) return false;
      if (workshopKpiFilter === 'dispatchable') {
        if (getActiveDispatchListForCommande(row)) return false;
        if (isAdmin && !isDispatchListEligible(row) && !isReturnDispatchListEligible(row)) return false;
        if (!isAdmin && siteType === 'workshop' && !isReturnDispatchListEligible(row)) return false;
        if (!isAdmin && siteType !== 'workshop' && !isDispatchListEligible(row)) return false;
      }

      const job = row.workshopSheetNumber || row.jobNumber || '';
      const category = getCommandeCategoryLabel(row);
      const storeLabel = [row.storeSiteId?.code, getLabel(row.storeSiteId, '')].filter(Boolean).join(' - ');
      const mountingType = row.frame?.mountingType || '—';
      const supportsTeleEdging = NON_STOCK_STORE_MOUNT_PROCESS_CODES.includes(row.process);
      const teleEdging = supportsTeleEdging ? (row.frame?.teleEdging ? 'oui' : 'non') : '';
      const mountingLocation = row.mountingLocation === 'workshop' ? 'atelier' : row.mountingLocation === 'store' ? 'magasin' : '';
      const frameType = getFrameTypeLabel(row.frame?.type, row.process, row.dailyOrder).toLowerCase();
      const status = getSimpleCommandeStatus(row, viewMode);
      const ncaLabel = hasNonConformity(row) ? 'Avec non-conformité' : 'Sans non-conformité';
      const searchable = [
        row.jobNumber,
        row.workshopSheetNumber,
        row.customerSnapshot?.fullName,
        category,
        storeLabel,
        row.frame?.displayLabel,
        getStockItemLabel(row.lens?.stockItemId),
        row.workflow?.displayStatus,
        ncaLabel,
      ]
        .join(' ')
        .toLowerCase();

      if (normalizedSearch && !searchable.includes(normalizedSearch)) return false;
      if (normalizedFilters.job && !job.toLowerCase().includes(normalizedFilters.job)) return false;
      if (normalizedFilters.category && !category.toLowerCase().includes(normalizedFilters.category)) return false;
      if (normalizedFilters.store && !storeLabel.toLowerCase().includes(normalizedFilters.store)) return false;
      if (normalizedFilters.mountingType && !mountingType.toLowerCase().includes(normalizedFilters.mountingType)) return false;
      if (normalizedFilters.teleEdging && teleEdging !== normalizedFilters.teleEdging) return false;
      if (normalizedFilters.mountingLocation && mountingLocation !== normalizedFilters.mountingLocation) return false;
      if (normalizedFilters.frameType && frameType !== normalizedFilters.frameType) return false;
      if (normalizedFilters.status && !status.toLowerCase().includes(normalizedFilters.status)) return false;
      if (
        normalizedFilters.nca.length &&
        !normalizedFilters.nca.some((filter) => matchesWorkshopNonConformityFilter(row, filter))
      ) {
        return false;
      }
      if (!isUpdatedAtInRange(row.updatedAt, updatedAtRange.from, updatedAtRange.to)) return false;
      return true;
    });
  }, [columnFilters, getActiveDispatchListForCommande, isAdmin, rowsForView, search, siteType, updatedAtRange, viewMode, workshopKpiFilter]);

  useEffect(() => {
    if (!useServerPagination || loading || filteredRows.length === rowsForView.length) return;
    console.warn('[commandes] Comparaison filtres serveur/client : certaines lignes serveur ont été écartées côté client.', {
      serverRows: rowsForView.length,
      clientRows: filteredRows.length,
      filters: { search: deferredSearch, ...deferredColumnFilters },
    });
  }, [deferredColumnFilters, deferredSearch, filteredRows.length, loading, rowsForView.length, useServerPagination]);

  const handleOpenCreate = () => {
    const isStockView = viewMode !== 'nonStock';
    setForm({
      ...emptyForm,
      lensMode: viewConfig.defaultLensMode,
      stockItemId: isStockView ? emptyForm.stockItemId : '',
      mountingLocation: isStockView ? emptyForm.mountingLocation : 'workshop',
      storeSiteId: !isAdmin && siteType === 'store' ? site?._id || '' : '',
      workshopSiteId: !isAdmin && siteType === 'workshop' ? site?._id || '' : defaultWorkshopSiteId,
    });
    setEditingId(null);
    setInitialReservation({ stockItemId: '', quantity: 0 });
    setOpen(true);
  };

  const handleOpenEdit = (row) => {
    setForm(fromApiItem(row));
    setEditingId(row._id);
    setInitialReservation({
      stockItemId: getId(row.lens?.stockItemId),
      quantity: Number(row.lens?.quantity || 0),
    });
    setOpen(true);
  };

  const getDefaultWorkflowSiteId = (row, ownerType) => {
    if (ownerType === 'store') return getId(row?.storeSiteId);
    if (ownerType === 'workshop') return getId(row?.workshopSiteId);
    return '';
  };

  const handleOpenWorkflowCorrection = (row) => {
    setWorkflowCorrectionSteps((row.workflow?.steps || []).map((step) => ({
      code: step.code,
      label: step.label,
      ownerType: step.ownerType,
      completed: Boolean(step.completedAt),
      completedAt: step.completedAt ? toDateTimeLocalValue(step.completedAt) : '',
      completedSiteId: getId(step.completedSiteId) || getDefaultWorkflowSiteId(row, step.ownerType),
    })));
    setWorkflowCorrectionError('');
    setWorkflowCorrectionOpen(true);
  };

  const handleWorkflowCorrectionToggle = (index, checked) => {
    setWorkflowCorrectionSteps((current) => current.map((step, stepIndex) => {
      if (!checked && stepIndex >= index) {
        return { ...step, completed: false, completedAt: '' };
      }
      if (checked && stepIndex <= index) {
        return {
          ...step,
          completed: true,
          completedAt: step.completedAt || toDateTimeLocalValue(),
          completedSiteId: step.completedSiteId || getDefaultWorkflowSiteId(viewingRow, step.ownerType),
        };
      }
      return step;
    }));
  };

  const handleWorkflowCorrectionField = (index, field, value) => {
    setWorkflowCorrectionSteps((current) => current.map((step, stepIndex) => (
      stepIndex === index ? { ...step, [field]: value } : step
    )));
  };

  const handleSaveWorkflowCorrection = async () => {
    if (!viewingRow) return;
    try {
      setWorkflowCorrectionSaving(true);
      setWorkflowCorrectionError('');
      const updated = await requestJson(`/commandes/${viewingRow._id}/workflow/admin-correction`, {
        method: 'PATCH',
        body: JSON.stringify({
          steps: workflowCorrectionSteps.map((step) => ({
            code: step.code,
            completed: step.completed,
            completedAt: step.completed ? new Date(step.completedAt).toISOString() : undefined,
            completedSiteId: step.completed ? step.completedSiteId : undefined,
          })),
        }),
      });
      setRows((current) => current.map((row) => (row._id === updated._id ? updated : row)));
      setViewingRow(updated);
      setWorkflowCorrectionOpen(false);
    } catch (err) {
      setWorkflowCorrectionError(err.message);
    } finally {
      setWorkflowCorrectionSaving(false);
    }
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const setFieldValue = (name, value) => {
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const openConfirmDialog = ({ title, description, confirmLabel = 'Confirmer', color = 'primary', onConfirm }) => {
    setConfirmDialog({
      open: true,
      title,
      description,
      confirmLabel,
      color,
      onConfirm,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog((current) => ({ ...current, open: false }));
  };

  const handleConfirmDialog = async () => {
    const action = confirmDialog.onConfirm;
    closeConfirmDialog();
    if (action) await action();
  };

  const handleOpenActionMenu = (event, row) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuRow(row);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuRow(null);
  };

  const selectedStoreSite = storeSites.find((site) => site._id === form.storeSiteId) || null;
  const selectedWorkshopSite = workshopSites.find((site) => site._id === form.workshopSiteId) || null;
  const selectedCustomer = customers.find((customer) => customer._id === form.customerId) || null;
  const selectedAdvisor = users.find((user) => user._id === form.advisorId) || null;
  const selectedWorkflowOperator = users.find((user) => user._id === workflowOperatorId) || null;
  const isStoreBulkAction = bulkActionCode.startsWith('STORE_');
  const bulkOperatorOptions = isStoreBulkAction ? storeUsers : workshopUsers;
  const selectedBulkOperator = bulkOperatorOptions.find((user) => user._id === bulkOperatorId) || null;
  const selectedTraceabilityTechnician = workshopUsers.find((user) => user._id === traceabilityTechnicianId) || null;
  const selectedBulkStoreSite = isAdmin
    ? [selectedBulkOperator?.siteId, ...(selectedBulkOperator?.siteIds || [])].find((candidate) => candidate?.type === 'store') || null
    : siteType === 'store' ? site : null;
  const isAaBulkStore = /^AA/i.test(String(selectedBulkStoreSite?.code || '')) || /AFFLELOU/i.test(String(selectedBulkStoreSite?.storeType || ''));
  useEffect(() => {
    if (storeSendFrameType !== 'clip' || isAaBulkStore) return;
    setStoreSendFrameType('new');
  }, [isAaBulkStore, storeSendFrameType]);
  const bulkDisplayResults = bulkResults || { successCount: 0, errorCount: 0, results: [] };
  const selectedWorkshopBulkAction =
    [...bulkWorkshopActions, stockLensBulkAction].find((action) => action.code === workshopBulkTab) || bulkWorkshopActions[0];
  const selectedStoreBulkAction =
    bulkStoreActions.find((action) => action.code === storeBulkTab) || bulkStoreActions[0];
  const selectedPickingOperator = workshopUsers.find((user) => user._id === pickingOperatorId) || null;
  const selectedStockItem = stockItems.find((item) => item._id === form.stockItemId) || null;
  const dispatchListRows = useMemo(() => dispatchLists.map(toDispatchListRow), [dispatchLists]);
  const dispatchTableRows = useMemo(() => dispatchTable.rows.map(toDispatchListRow), [dispatchTable.rows]);
  const returnDraftDispatchLists = useMemo(
    () => dispatchListRows.filter((list) =>
      list.direction === 'workshop_to_store' &&
      ['draft', 'partial'].includes(list.status) &&
      (isAdmin || getId(list.workshopSiteId) === site?._id)
    ),
    [dispatchListRows, isAdmin, site?._id]
  );
  const selectedAutoReturnBlLists = useMemo(
    () => returnDraftDispatchLists.filter((list) => autoReturnBlSelection.includes(list._id)),
    [autoReturnBlSelection, returnDraftDispatchLists]
  );
  const dispatchSenderWorkshopId = useMemo(() => {
    const scannedList = dispatchListRows.find((list) =>
      String(list.listNumber || '').toUpperCase() === dispatchSendNumberInput.trim().toUpperCase()
    );
    return getId(selectedAutoReturnBlLists[0]?.workshopSiteId) ||
      getId(scannedList?.workshopSiteId) ||
      (siteType === 'workshop' ? site?._id : '') ||
      defaultWorkshopSiteId;
  }, [defaultWorkshopSiteId, dispatchListRows, dispatchSendNumberInput, selectedAutoReturnBlLists, site?._id, siteType]);
  const dispatchSendTarget = useMemo(
    () => dispatchListRows.find((list) => String(list.listNumber || '').toUpperCase() === dispatchSendNumberInput.trim().toUpperCase()) || null,
    [dispatchListRows, dispatchSendNumberInput]
  );
  const dispatchSendRequiresWorkshopSigner = Boolean(
    dispatchSendTarget ? dispatchSendTarget.direction === 'workshop_to_store' : siteType === 'workshop'
  );
  const dispatchSendSourceSiteId = getId(
    dispatchSendTarget?.direction === 'workshop_to_store'
      ? dispatchSendTarget?.workshopSiteId
      : dispatchSendTarget?.storeSiteId
  );
  const dispatchSendSignerLabel = dispatchSendRequiresWorkshopSigner
    ? 'Collaborateur atelier signataire'
    : 'Conseiller magasin signataire';
  const dispatchSendRequiresPrint = Boolean(
    dispatchSendTarget && (dispatchSendTarget.direction || 'store_to_workshop') === 'store_to_workshop'
  );
  const dispatchSendIsPrinted = Boolean(dispatchSendTarget?.print?.isPrinted);
  const dispatchSenderOptions = useMemo(
    () => workshopUsers.filter((user) => userHasSite(user, dispatchSenderWorkshopId)),
    [dispatchSenderWorkshopId, workshopUsers]
  );
  const dispatchSendSignerOptions = useMemo(
    () => (dispatchSendRequiresWorkshopSigner ? workshopUsers : storeUsers)
      .filter((user) => userHasSite(user, dispatchSendSourceSiteId)),
    [dispatchSendRequiresWorkshopSigner, dispatchSendSourceSiteId, storeUsers, workshopUsers]
  );
  const selectedDispatchSender = dispatchSenderOptions.find((user) => user._id === dispatchSenderUserId) || null;
  const selectedDispatchSigner = dispatchSendSignerOptions.find((user) => user._id === dispatchSenderUserId) || null;
  const dispatchableOrderRows = useMemo(
    () =>
      rowsForView.filter((row) => {
        if (getActiveDispatchListForCommande(row)) return false;
        if (isAdmin) return isDispatchListEligible(row) || isReturnDispatchListEligible(row);
        if (siteType === 'store') {
          return isDispatchListEligible(row) && getId(row.storeSiteId) === site?._id;
        }
        if (siteType === 'workshop') {
          return isReturnDispatchListEligible(row) && getId(row.workshopSiteId) === site?._id;
        }
        return false;
      }),
    [getActiveDispatchListForCommande, isAdmin, rowsForView, site, siteType]
  );
  const visibleDispatchListRows = useMemo(() => {
    if (viewMode === 'nonStock') return dispatchListRows;
    const stockCommandeIds = new Set(rowsForView.map((row) => String(row._id)));
    return dispatchListRows.filter((list) =>
      (list.items || []).some((item) => stockCommandeIds.has(String(item.commandeId?._id || item.commandeId || '')))
    );
  }, [dispatchListRows, rowsForView, viewMode]);
  const scopedDispatchListRows = useMemo(
    () =>
      visibleDispatchListRows.filter((list) => {
        if (isAdmin) return true;
        if (siteType === 'store') return getId(list.storeSiteId) === site?._id;
        if (siteType === 'workshop') return getId(list.workshopSiteId) === site?._id;
        return false;
      }),
    [visibleDispatchListRows, isAdmin, site, siteType]
  );
  const dispatchListKpis = useMemo(() => {
    const isSentStatus = (status) => status === 'sent' || status === 'pending';
    const matchesSendDirection = (list) => {
      const direction = list.direction || 'store_to_workshop';
      if (isAdmin) return true;
      if (siteType === 'store') return direction === 'store_to_workshop';
      if (siteType === 'workshop') return direction === 'workshop_to_store';
      return false;
    };
    const matchesReceiveDirection = (list) => {
      const direction = list.direction || 'store_to_workshop';
      if (isAdmin) return true;
      if (siteType === 'store') return direction === 'workshop_to_store';
      if (siteType === 'workshop') return direction === 'store_to_workshop';
      return false;
    };

    // Compteurs serveur (tous les BL, sans la limite de 100 du tableau) ; calcul
    // local conservé en secours si l'API des compteurs ne répond pas.
    if (serverDispatchListKpis) {
      return {
        toSend: serverDispatchListKpis.toSend,
        toReceive: serverDispatchListKpis.toReceive,
        byDirection: serverDispatchListKpis.byDirection,
      };
    }
    return {
      toSend: scopedDispatchListRows.filter((list) => list.status === 'draft' && matchesSendDirection(list)).length,
      toReceive: scopedDispatchListRows.filter((list) => isSentStatus(list.status) && matchesReceiveDirection(list)).length,
      byDirection: null,
    };
  }, [isAdmin, scopedDispatchListRows, serverDispatchListKpis, siteType]);
  const getDispatchListKpiHelper = (key) => {
    if (isAdmin) {
      const detail = dispatchListKpis.byDirection;
      if (!detail) return key === 'toSend' ? 'BL à envoyer, tous sens confondus' : 'BL à réceptionner, tous sens confondus';
      return `${detail.store_to_workshop[key]} magasin → atelier · ${detail.workshop_to_store[key]} atelier → magasin`;
    }
    if (key === 'toSend') {
      return siteType === 'workshop' ? 'Retours atelier à expédier au magasin' : 'Lots magasin à envoyer à l’atelier';
    }
    return siteType === 'workshop' ? 'Lots magasin à réceptionner à l’atelier' : 'Retours atelier à réceptionner en magasin';
  };

  const openCommandeCardDialog = async (key, title, helper) => {
    setWorkshopKpiFilter('');
    setCardDialogFilters({ search: '', store: '', step: '' });
    setCardDialogSelection([]);
    setCardDialogBulkMountingType('');
    let sourceRows = rowsForView;
    if (key === 'dispatchable' && (isAdmin || siteType === 'workshop')) {
      try {
        const params = new URLSearchParams({ kpi: 'dispatchable', limit: '500', page: '1' });
        if (siteType === 'workshop' && site?._id) params.set('workshopSiteId', site._id);
        const payload = await requestJson(`/commandes?${params.toString()}`);
        sourceRows = getCollection(payload);
      } catch (_error) {
        sourceRows = rowsForView;
      }
    }
    const rowsForCard = sourceRows.filter((row) => {
      if (key === 'storeToReceive') {
        return isStoreCommandeToReceive(row) && (isAdmin || getId(row.storeSiteId) === site?._id);
      }

      if (key === 'dispatchable') {
        // The server query already excludes jobs assigned to active BLs.
        if (isAdmin) return isDispatchListEligible(row) || isReturnDispatchListEligible(row);
        if (siteType === 'workshop') {
          return isReturnDispatchListEligible(row);
        }
        return isDispatchListEligible(row) && getId(row.storeSiteId) === site?._id;
      }

      if (!isAdmin && siteType === 'workshop' && getId(row.workshopSiteId) !== site?._id) return false;
      if (key === 'awaitingDispatch') return isDispatchListEligible(row) && !getActiveDispatchListForCommande(row);
      if (key === 'awaitingLens') return isAwaitingWorkshopLens(row);
      if (key === 'awaitingMatching') return isAwaitingWorkshopMatching(row);
      if (key === 'awaitingStoreDispatch') return isAwaitingWorkshopDispatchPreparation(row);
      if (key === 'lensReceptionLate') return isLensReceptionLate(row, 20);
      if (key === 'matchingLate') return isMatchingLate(row, 3);
      if (key === 'lensWithoutDossier') return isLensWithoutDossierLate(row, 6);
      return false;
    });

    setCardDialog({ type: 'commandes', key, title, helper, rows: rowsForCard });
  };

  const toggleCommandeCardFilter = (key) => {
    setWorkshopKpiFilter((current) => (current === key ? '' : key));
    setDispatchListKpiFilter('');
  };

  const toggleDispatchListCardFilter = (key) => {
    setDispatchListKpiFilter((current) => (current === key ? '' : key));
    setWorkshopKpiFilter('');
  };

  const openDispatchListCardDialog = (key, title, helper) => {
    const isSentStatus = (status) => status === 'sent' || status === 'pending';
    const rowsForCard = scopedDispatchListRows.filter((list) => {
      const direction = list.direction || 'store_to_workshop';
      if (key === 'toReceive') {
        return isSentStatus(list.status) && direction === 'workshop_to_store';
      }
      if (key === 'toSend') {
        return list.status === 'draft' && direction === 'store_to_workshop';
      }
      return false;
    });

    setCardDialog({ type: 'dispatchLists', key, title, helper, rows: rowsForCard });
  };
  const filteredCardDialogRows = useMemo(() => {
    const entries = cardDialog?.rows || [];
    if (cardDialog?.type === 'dispatchLists') return entries;
    const search = cardDialogFilters.search.trim().toLowerCase();
    return entries.filter((row) => {
      const store = row.storeSiteId?.code || getSiteLabel(row.storeSiteId) || '';
      const step = getDisplayStep(getCurrentStep(row), viewMode)?.label || 'Terminé';
      const values = [row.jobNumber, row.workshopSheetNumber, store, step, getCommandeCategoryLabel(row)].join(' ').toLowerCase();
      return (!search || values.includes(search))
        && (!cardDialogFilters.store || store === cardDialogFilters.store)
        && (!cardDialogFilters.step || step === cardDialogFilters.step);
    });
  }, [cardDialog, cardDialogFilters, viewMode]);
  const cardDialogFilterOptions = useMemo(() => ({
    stores: [...new Set((cardDialog?.rows || []).map((row) => row.storeSiteId?.code || getSiteLabel(row.storeSiteId)).filter(Boolean))].sort(),
    steps: [...new Set((cardDialog?.rows || []).map((row) => getDisplayStep(getCurrentStep(row), viewMode)?.label || 'Terminé'))].sort(),
  }), [cardDialog, viewMode]);
  const dispatchListFilterOptions = useMemo(() => {
    const activeSites = sites.filter((entry) => entry.active !== false);
    const toOptions = (type, ownOnly) => activeSites
      .filter((entry) => entry.type === type && (!ownOnly || entry._id === site?._id))
      .map((entry) => [entry._id, entry.code || getSiteLabel(entry)])
      .sort((left, right) => String(left[1]).localeCompare(String(right[1]), 'fr', { numeric: true }));
    return {
      statuses: DISPATCH_LIST_STATUS_OPTIONS,
      stores: toOptions('store', !isAdmin && siteType === 'store'),
      workshops: toOptions('workshop', !isAdmin && siteType === 'workshop'),
    };
  }, [isAdmin, site?._id, siteType, sites]);
  const toggleDispatchListSort = useCallback((field) => {
    setDispatchListSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);
  const dispatchEditCandidates = useMemo(() => {
    if (!dispatchEditTarget) return [];
    const isReturn = (dispatchEditTarget.direction || 'store_to_workshop') === 'workshop_to_store';
    const currentItems = dispatchEditTarget.items || [];
    const currentIds = new Set(currentItems.map((item) => String(item.commandeId?._id || item.commandeId || '')));
    const rowsById = new Map(rowsForView.map((row) => [String(row._id), row]));
    const currentRows = currentItems.map((item) => {
      const id = String(item.commandeId?._id || item.commandeId || '');
      return rowsById.get(id) || {
        _id: id,
        jobNumber: item.jobNumber,
        customerSnapshot: { fullName: item.customerName || '' },
        advisorSnapshot: { fullName: item.advisorName || '' },
        inCurrentDispatchList: true,
      };
    }).filter((row) => row._id);
    const availableRows = rowsForView.filter((row) => {
      const belongsToList = currentIds.has(String(row._id));
      const eligible = isReturn ? isReturnDispatchListEligible(row) : isDispatchListEligible(row);
      const sameStore = getId(row.storeSiteId) === getId(dispatchEditTarget.storeSiteId);
      const sameWorkshop = getId(row.workshopSiteId) === getId(dispatchEditTarget.workshopSiteId);
      const activeList = getActiveDispatchListForCommande(row);
      const occupiedByAnotherList = activeList && String(activeList._id) !== String(dispatchEditTarget._id);
      return sameStore && sameWorkshop && !occupiedByAnotherList && !belongsToList && eligible;
    });
    return [...currentRows, ...availableRows];
  }, [dispatchEditTarget, getActiveDispatchListForCommande, rowsForView]);
  const processPreview = resolveProcessFromForm(form);
  const workflowPreview = workflowOptions[processPreview];
  const workflowSteps = editingId
    ? rows.find((row) => row._id === editingId)?.workflow?.steps || workflowPreview.steps
    : workflowPreview.steps;
  const isStockLensProcess = form.lensMode === 'stock';
  const isWorkshopFollowupView = viewMode === 'nonStock' || isStockFollowupView;
  const isDispatchWorkflowView = isWorkshopFollowupView || dispatchOnly;
  const canDeclareNonConformity = isWorkshopFollowupView && (isAdmin || siteType === 'workshop' || siteType === 'store');
  const editingCommande = editingId ? rows.find((row) => row._id === editingId) : null;
  const canEditCurrentCommande = Boolean(editingCommande && (isAdmin || canStoreEditInitialCommande(editingCommande)));
  const isStoreProcessOnlyEdit = Boolean(editingCommande && !isAdmin && canStoreEditInitialCommande(editingCommande));
  const lockFollowupIdentityFields = Boolean(editingId && !canEditCurrentCommande);
  const currentWorkflowStep = workflowSteps.find((step) => !step.completedAt);
  const viewingWorkflowSteps = normalizeWorkflowStepsForDisplay(
    viewingRow?.workflow?.steps?.length
      ? viewingRow.workflow.steps
      : workflowOptions[viewingRow?.process]?.steps || []
  );
  const viewingActiveStep = viewingWorkflowSteps.findIndex((step) => !step.completedAt);
  const normalizedViewingActiveStep =
    viewingActiveStep === -1 ? viewingWorkflowSteps.length : viewingActiveStep;
  const selectedStockEffectiveAvailable = selectedStockItem
    ? Number(selectedStockItem.availableQty || 0) +
      (editingId && initialReservation.stockItemId === getId(selectedStockItem)
        ? Number(initialReservation.quantity || 0)
        : 0)
    : 0;

  const filteredStockItems = useMemo(() => {
    const normalizedSearch = lensSearch.trim().toLowerCase();
    if (!normalizedSearch) return availableStockItems;

    return availableStockItems.filter((item) =>
      [
        item.barcode,
        item.supplier,
        item.label,
        formatDiopter(item.sphere),
        formatDiopter(item.cylinder),
        item.availableQty,
        item.reservedQty,
      ]
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [availableStockItems, lensSearch]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError('');
      const requestedQuantity = Number(form.lensQuantity || 1);

      if (isStockLensProcess && !selectedStockItem) {
        throw new Error('Sélectionne un verre de stock avant de valider la commande.');
      }

      if (isStockLensProcess && selectedStockItem && requestedQuantity > selectedStockEffectiveAvailable) {
        throw new Error(
          `Stock insuffisant : ${selectedStockEffectiveAvailable} verre(s) disponible(s), ${requestedQuantity} demandé(s).`
        );
      }

      await requestJson(editingId ? `/commandes/${editingId}` : '/commandes', {
        method: editingId ? 'PUT' : 'POST',
        body: JSON.stringify(toApiPayload(form)),
      });
      setOpen(false);
      await loadRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await requestJson(`/commandes/${id}`, { method: 'DELETE' });
      setDeleteTarget(null);
      await loadRows();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSelectStockItem = (item) => {
    setForm((current) => ({
      ...current,
      stockItemId: getId(item),
      lensSupplier: item.supplier || current.lensSupplier,
      lensQuantity: Number(current.lensQuantity || 0) > 0 ? current.lensQuantity : 1,
    }));
  };

  const handleCompleteWorkflowStep = async (stepCode) => {
    if (!editingId) return;
    if (isAdmin && !selectedWorkflowOperator) {
      setError('Sélectionne l’utilisateur qui bippe cette étape.');
      return;
    }

    openConfirmDialog({
      title: 'Confirmer le bip',
      description: 'Valider cette étape avec la date et l’heure actuelles ?',
      confirmLabel: 'Valider le bip',
      onConfirm: async () => {
        try {
          setSaving(true);
          setError('');
          const updatedCommande = await requestJson(`/commandes/${editingId}/workflow/steps/${stepCode}/complete`, {
            method: 'PATCH',
            body: JSON.stringify({
              completedAt: new Date().toISOString(),
              completedBy: isAdmin ? selectedWorkflowOperator._id : undefined,
            }),
          });
          setRows((current) => current.map((row) => (row._id === editingId ? updatedCommande : row)));
        } catch (err) {
          setError(err.message);
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const handleOpenPickingForCommande = (row) => {
    setPickingJobNumber(row.jobNumber || row.workshopSheetNumber || '');
    setPickingRightBarcode('');
    setPickingLeftBarcode('');
    setPickingOperatorId('');
    setPickingOpen(true);
  };

  const canActOnCommande = (row) => {
    if (isAdmin) return false;
    const step = getCurrentStep(row);
    if (hasPendingMissingInitialFile(row) && step?.ownerType === 'store' && getId(row.storeSiteId) === site?._id) {
      return true;
    }
    if (getActiveDispatchListForCommande(row)) return false;
    if (step?.code === 'ATELIER_RECEPTION_MONTURE') return false;
    if (step?.code === 'MAGASIN_MONTAGE') return false;
    if (isDispatchListEligible(row) || isReturnDispatchListEligible(row)) return false;
    return Boolean(step) && step.ownerType === siteType;
  };

  const canPrepareDispatch = (row) => {
    if (getActiveDispatchListForCommande(row)) return false;
    if (isDispatchListEligible(row)) {
      return isAdmin || (siteType === 'store' && getId(row.storeSiteId) === site?._id);
    }
    if (isReturnDispatchListEligible(row)) {
      return isAdmin || (siteType === 'workshop' && getId(row.workshopSiteId) === site?._id);
    }
    return false;
  };

  const completeRowStep = async (row, extraPayload = {}) => {
    const step = getCurrentStep(row);
    if (!step) return;

    const actionLabel = getRowWorkflowActionLabel(row);

    openConfirmDialog({
      title: actionLabel,
      description: `Confirmer cette action pour le JOB ${row.jobNumber} ? La date et l’heure actuelles seront enregistrées.`,
      confirmLabel: actionLabel,
      onConfirm: async () => {
        try {
          setSaving(true);
          setError('');
          const updatedCommande = await requestJson(`/commandes/${row._id}/workflow/steps/${step.code}/complete`, {
            method: 'PATCH',
            body: JSON.stringify({ completedAt: new Date().toISOString(), ...extraPayload }),
          });
          setRows((current) => current.map((item) => (item._id === row._id ? updatedCommande : item)));
          if (viewingRow?._id === row._id) setViewingRow(updatedCommande);
          setSnackbar({
            open: true,
            severity: 'success',
            message: `${actionLabel} validé pour le JOB ${row.jobNumber}.`,
          });
        } catch (err) {
          setError(err.message);
          setSnackbar({
            open: true,
            severity: 'error',
            message: err.message,
          });
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const handleCompleteRowStep = async (row) => {
    const step = getCurrentStep(row);
    if (!step) return;

    if (step.code === 'ATELIER_RECUPERE_VERRE_STOCK') {
      handleOpenPickingForCommande(row);
      return;
    }

    if (viewMode === 'nonStock' && isWorkshopSendStep(step) && !['RETAILLAGE', 'CLIP'].includes(row.process)) {
      setMountingTypeRow(row);
      setMountingTypeValue(row.frame?.mountingType || '');
      setMountingTypeMode('send');
      setMountingTypeOpen(true);
      return;
    }

    await completeRowStep(row);
  };

  // Le type de montage est renseigné par l'atelier avant l'expédition, y
  // compris pour les workflows « montage magasin » (notamment le
  // télédébordage qui peut initialiser la valeur à T.D.). Ces dossiers restent
  // donc éditables au stylo par l'atelier et par l'administrateur.
  const canEditMountingType = (row) =>
    isWorkshopFollowupView &&
    (
      row?.mountingLocation === 'workshop' ||
      row?.frame?.sentToWorkshop ||
      [
        'NEW_FRAME_NON_STOCK_LENS_STORE_MOUNT',
        'EXISTING_FRAME_NON_STOCK_LENS_STORE_MOUNT',
      ].includes(row?.process)
    ) &&
    (isAdmin || (siteType === 'workshop' && getId(row.workshopSiteId) === site?._id));

  const handleOpenMountingTypeEdit = (row) => {
    setMountingTypeRow(row);
    setMountingTypeValue(row.frame?.mountingType || '');
    setMountingTypeMode('edit');
    setMountingTypeOpen(true);
  };

  const handleOpenNonConformity = (row = null, type = null) => {
    const inferredType = type || (siteType === 'store' ? 'NCM' : 'NCA');
    setNonConformityRow(row);
    setNonConformityJobNumber(row?.jobNumber || row?.workshopSheetNumber || '');
    setNonConformityForm({ type: inferredType, scope: '', reason: '', comment: '' });
    setNonConformityPhotos((current) => {
      current.forEach((photo) => URL.revokeObjectURL(photo.url));
      return [];
    });
    setNonConformityOpen(true);
  };

  const canDeclareTraceability = (row) =>
    isWorkshopFollowupView &&
    (isAdmin || (siteType === 'workshop' && getId(row?.workshopSiteId) === site?._id));

  const handleOpenTraceability = (row) => {
    setTraceabilityRow(row);
    setTraceabilityForm(emptyTraceabilityForm);
    setTraceabilityTechnicianId('');
    setTraceabilityOpen(true);
  };

  const handleTraceabilityCheckChange = (key, checked) => {
    setTraceabilityForm((current) => ({
      ...current,
      checks: {
        ...current.checks,
        [key]: checked,
      },
    }));
  };

  const handleTraceabilityFieldChange = (section, key, value) => {
    setTraceabilityForm((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [key]: value,
      },
    }));
  };

  const handleTraceabilitySubmit = async (event) => {
    event.preventDefault();
    if (!traceabilityRow) return;
    try {
      setTraceabilitySaving(true);
      setError('');
      const updatedCommande = await requestJson(`/commandes/${traceabilityRow._id}/traceability`, {
        method: 'POST',
        body: JSON.stringify(stripEmptyValues({
          ...traceabilityForm,
          declaredBy: selectedTraceabilityTechnician?._id,
        })),
      });
      setRows((current) => current.map((row) => (row._id === updatedCommande._id ? updatedCommande : row)));
      if (viewingRow?._id === updatedCommande._id) setViewingRow(updatedCommande);
      setTraceabilityOpen(false);
      setTraceabilityRow(null);
      setTraceabilityForm(emptyTraceabilityForm);
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Traçabilité déclarée pour le JOB ${updatedCommande.jobNumber}.`,
      });
    } catch (err) {
      setError(err.message);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    } finally {
      setTraceabilitySaving(false);
    }
  };

  const handleNonConformitySubmit = async (event) => {
    event.preventDefault();
    const normalizedJobNumber = String(nonConformityJobNumber || '').trim();
    if (!nonConformityRow && !normalizedJobNumber) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Saisis ou scanne un numéro de JOB.',
      });
      return;
    }
    if (!String(nonConformityForm.comment || '').trim()) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Le commentaire est obligatoire pour déclarer une non-conformité.',
      });
      return;
    }

    try {
      setNonConformitySaving(true);
      setError('');
      const fields = stripEmptyValues(nonConformityRow ? nonConformityForm : { ...nonConformityForm, jobNumber: normalizedJobNumber });
      let body = JSON.stringify(fields);
      if (nonConformityPhotos.length) {
        // Avec photos, la déclaration part en multipart (champs + fichiers).
        body = new FormData();
        Object.entries(fields).forEach(([key, value]) => body.append(key, value));
        nonConformityPhotos.forEach((photo) => body.append('photos', photo.file));
      }
      const updatedCommande = nonConformityRow
        ? await requestJson(`/commandes/${nonConformityRow._id}/non-conformities`, { method: 'POST', body })
        : await requestJson('/commandes/by-job/non-conformities', { method: 'POST', body });
      nonConformityPhotos.forEach((photo) => URL.revokeObjectURL(photo.url));
      setNonConformityPhotos([]);
      setRows((current) => current.map((row) => (row._id === updatedCommande._id ? updatedCommande : row)));
      if (viewingRow?._id === updatedCommande._id) setViewingRow(updatedCommande);
      if (!nonConformityRow) await loadRows();
      setNonConformityOpen(false);
      setSnackbar({
        open: true,
        severity: 'warning',
        message: `Non-conformité ${nonConformityForm.type === 'NCM' ? 'magasin' : 'atelier'} déclarée pour le JOB ${updatedCommande.jobNumber}.`,
      });
    } catch (err) {
      setError(err.message);
      setSnackbar({
        open: true,
        severity: 'error',
        message: err.message,
      });
    } finally {
      setNonConformitySaving(false);
    }
  };

  const handleNonConformityPhotosSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    if (nonConformityPhotos.length + files.length > NC_PHOTO_MAX) {
      setSnackbar({ open: true, severity: 'error', message: `${NC_PHOTO_MAX} photos maximum par non-conformité.` });
      return;
    }
    try {
      setNonConformityPhotoBusy(true);
      const compressed = [];
      for (const file of files) {
        const photo = await compressPhoto(file);
        compressed.push({ file: photo, url: URL.createObjectURL(photo) });
      }
      setNonConformityPhotos((current) => [...current, ...compressed]);
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err.message });
    } finally {
      setNonConformityPhotoBusy(false);
    }
  };

  const removeNonConformityPhoto = (index) => {
    setNonConformityPhotos((current) => {
      URL.revokeObjectURL(current[index]?.url);
      return current.filter((_photo, photoIndex) => photoIndex !== index);
    });
  };

  const handleCancelNonConformity = async (event) => {
    event.preventDefault();
    if (!cancelNonConformityTarget || !cancelNonConformityReason.trim()) return;

    try {
      setCancelNonConformitySaving(true);
      setError('');
      const updatedCommande = await requestJson(
        `/commandes/${cancelNonConformityTarget.commandeId}/non-conformities/${cancelNonConformityTarget.item._id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            status: 'cancelled',
            cancellationReason: cancelNonConformityReason.trim(),
          }),
        }
      );
      setRows((current) => current.map((row) => (row._id === updatedCommande._id ? updatedCommande : row)));
      if (viewingRow?._id === updatedCommande._id) setViewingRow(updatedCommande);
      setCancelNonConformityTarget(null);
      setCancelNonConformityReason('');
      setSnackbar({
        open: true,
        severity: 'success',
        message: `NCA annulée pour le JOB ${updatedCommande.jobNumber}.`,
      });
    } catch (err) {
      setError(err.message);
      setSnackbar({
        open: true,
        severity: 'error',
        message: err.message,
      });
    } finally {
      setCancelNonConformitySaving(false);
    }
  };

  const handleDeleteEmbeddedCommandeItem = (kind, item) => {
    if (!isAdmin || !viewingRow || !item?._id) return;
    const isNonConformity = kind === 'non-conformity';
    const label = isNonConformity
      ? `${item.type || 'NCA'} (${getOptionLabelByValue(nonConformityReasonOptions, item.reason)})`
      : 'la fiche de traçabilité';
    const endpoint = isNonConformity
      ? `/commandes/${viewingRow._id}/non-conformities/${item._id}`
      : `/commandes/${viewingRow._id}/traceability/${item._id}`;
    openConfirmDialog({
      title: `Supprimer ${label}`,
      description: `Confirmer la suppression de ${label} pour le JOB ${viewingRow.jobNumber} ? Cette action est réservée aux administrateurs et ne modifie pas les étapes du workflow.`,
      confirmLabel: 'Supprimer',
      color: 'error',
      onConfirm: async () => {
        try {
          setSaving(true);
          setError('');
          const updatedCommande = await requestJson(endpoint, { method: 'DELETE' });
          setRows((current) => current.map((row) => (row._id === updatedCommande._id ? updatedCommande : row)));
          setViewingRow(updatedCommande);
          setSnackbar({
            open: true,
            severity: 'success',
            message: `${isNonConformity ? 'Non-conformité' : 'Fiche de traçabilité'} supprimée pour le JOB ${updatedCommande.jobNumber}.`,
          });
        } catch (err) {
          setError(err.message);
          setSnackbar({ open: true, severity: 'error', message: err.message });
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const handleDeleteWorkflowAlert = (item) => {
    if (!isAdmin || !viewingRow || !item?._id) return;
    openConfirmDialog({
      title: 'Supprimer l’alerte de workflow',
      description: `Confirmer la suppression de cette alerte pour le JOB ${viewingRow.jobNumber} ? Les étapes du workflow ne seront pas modifiées.`,
      confirmLabel: 'Supprimer',
      color: 'error',
      onConfirm: async () => {
        try {
          setSaving(true);
          setError('');
          const updatedCommande = await requestJson(`/commandes/${viewingRow._id}/workflow-alerts/${item._id}`, { method: 'DELETE' });
          setRows((current) => current.map((row) => (row._id === updatedCommande._id ? updatedCommande : row)));
          setViewingRow(updatedCommande);
          setSnackbar({ open: true, severity: 'success', message: `Alerte supprimée pour le JOB ${updatedCommande.jobNumber}.` });
        } catch (err) {
          setError(err.message);
          setSnackbar({ open: true, severity: 'error', message: err.message });
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const handleOpenPicking = () => {
    setPickingJobNumber('');
    setPickingRightBarcode('');
    setPickingLeftBarcode('');
    setPickingOperatorId('');
    setPickingOpen(true);
  };

  const handleOpenWorkshopBulkAction = (actionCode) => {
    setWorkshopBulkTab(actionCode);
    setBulkActionCode(actionCode);
    setBulkJobInput('');
    if (!WORKSHOP_MOUNTING_TYPE_ACTION_CODES.includes(actionCode)) setBulkMountingType('');
    setBulkResults({ successCount: 0, errorCount: 0, results: [] });
    setWorkshopBulkDialogOpen(true);
  };

  const handleOpenStoreBulkAction = (actionCode) => {
    setStoreBulkTab(actionCode);
    setBulkActionCode(actionCode);
    setBulkJobInput('');
    setBulkMountingType('');
    if (actionCode === 'STORE_CREATE') {
      setStoreSendFrameType('new');
      setStoreSendMountingLocation('workshop');
    }
    setStoreSendCustomerLastName('');
    setStoreSendCustomerFirstName('');
    setStoreSendTeleEdging(false);
    setStoreSendFrameOrigin('');
    setStoreSendFrameOriginCode('');
    setBulkResults({ successCount: 0, errorCount: 0, results: [] });
    setStoreBulkDialogOpen(true);
  };

  const handleBulkWorkflowSubmit = async (event) => {
    event?.preventDefault?.();
    const scannedJobNumber = bulkJobInput.trim();
    const effectiveActionCode = isWorkshopFollowupView && siteType === 'store'
      ? storeBulkTab
      : canBulkWorkshopScan
        ? workshopBulkTab
        : bulkActionCode;
    const effectiveIsStoreBulkAction = effectiveActionCode.startsWith('STORE_');
    // Retaillage, Clip et commande journalière : fiche atelier imprimée à la création.
    const isSpecialStoreCreation = ['STORE_CREATE', 'STORE_SEND'].includes(effectiveActionCode)
      && ['retailing', 'clip', 'daily'].includes(storeSendFrameType);
    let workshopSheetPrintWindow = null;

    try {
      setBulkSaving(true);
      setError('');
      if (isAdmin && !selectedBulkOperator) {
        throw new Error(`Sélectionne un opérateur ${effectiveIsStoreBulkAction ? 'magasin' : 'atelier'}.`);
      }
      if (!scannedJobNumber) {
        throw new Error('Scanne un numéro de job.');
      }

      if (isSpecialStoreCreation) {
        workshopSheetPrintWindow = window.open('', '_blank', 'width=960,height=720');
        if (!workshopSheetPrintWindow) {
          throw new Error('Autorise les fenêtres contextuelles pour imprimer automatiquement la fiche atelier.');
        }
        workshopSheetPrintWindow.document.write('<!doctype html><title>Préparation de la fiche atelier</title><p>Préparation de la fiche atelier…</p>');
      }

      if (['STORE_CREATE', 'STORE_SEND'].includes(effectiveActionCode)) {
        const payload = await requestJson('/commandes/workflow/bulk-create-send', {
          method: 'POST',
          body: JSON.stringify({
            completedBy: isAdmin ? selectedBulkOperator._id : undefined,
            jobNumbers: [scannedJobNumber],
            frameType: storeSendFrameType,
            mountingLocation: storeSendMountingLocation,
            teleEdging: storeSendTeleEdging,
            customerLastName: storeSendCustomerLastName,
            customerFirstName: storeSendCustomerFirstName,
            frameOrigin: FRAME_ORIGIN_TYPES.includes(storeSendFrameType) ? storeSendFrameOrigin || undefined : undefined,
            frameOriginCode: FRAME_ORIGIN_TYPES.includes(storeSendFrameType) && storeSendFrameOrigin ? storeSendFrameOriginCode : undefined,
            workshopSiteId: storeSendWorkshopSiteId,
            sendImmediately: effectiveActionCode === 'STORE_SEND',
          }),
        });

        const result = payload.results?.[0] || {
          jobNumber: scannedJobNumber,
          success: false,
          message: 'Réponse magasin invalide.',
        };

        setBulkResults((current) => {
          const previous = current || { successCount: 0, errorCount: 0, results: [] };
          return {
            successCount: previous.successCount + (result.success ? 1 : 0),
            errorCount: previous.errorCount + (result.success ? 0 : 1),
            results: [{ ...result, scannedAt: new Date().toISOString() }, ...previous.results],
          };
        });
        setBulkJobInput('');

        if (result.success) {
          playActionSound('success');
          if (isSpecialStoreCreation && workshopSheetPrintWindow) {
            printSpecialWorkshopSheet(workshopSheetPrintWindow, {
              jobNumber: result.jobNumber,
              customerName: `${storeSendCustomerFirstName.trim()} ${storeSendCustomerLastName.trim()}`.trim() || '—',
              processLabel: storeSendFrameType === 'clip'
                ? 'CLIP'
                : storeSendFrameType === 'daily' ? 'COMMANDE JOURNALIÈRE' : 'RETAILLAGE',
              storeLabel: getSiteLabel(isAdmin ? selectedBulkOperator?.siteId : site),
              dispatchListNumber: result.dispatchListNumber,
              originLabel: getFrameOriginLabel({ origin: storeSendFrameOrigin, originCode: storeSendFrameOriginCode }),
            });
          }
          setStoreSendCustomerLastName('');
          setStoreSendCustomerFirstName('');
          setStoreSendFrameOrigin('');
          setStoreSendFrameOriginCode('');
          await loadRows({ silent: true });
        } else {
          workshopSheetPrintWindow?.close();
          setSnackbar({
            open: true,
            severity: 'error',
            message: result.message,
          });
        }
        return;
      }

      const normalizedScannedJobNumber = String(scannedJobNumber || '').trim().toUpperCase();
      const scannedCommande = rows.find((row) =>
        [row.jobNumber, row.workshopSheetNumber]
          .some((value) => String(value || '').trim().toUpperCase() === normalizedScannedJobNumber)
      );
      const scannedServiceDoesNotNeedMountingType = ['RETAILLAGE', 'CLIP'].includes(scannedCommande?.process);
      if (
        WORKSHOP_MOUNTING_TYPE_ACTION_CODES.includes(effectiveActionCode)
        && !scannedServiceDoesNotNeedMountingType
        && !mountingTypeOptions.includes(bulkMountingType)
      ) {
        throw new Error('Sélectionne le type de montage pour l’envoi atelier.');
      }

      const payload = await requestJson('/commandes/workflow/bulk-complete', {
        method: 'POST',
        body: JSON.stringify({
          actionCode: effectiveActionCode,
          completedBy: isAdmin ? selectedBulkOperator._id : undefined,
          jobNumbers: [scannedJobNumber],
          mountingType: WORKSHOP_MOUNTING_TYPE_ACTION_CODES.includes(effectiveActionCode) && !scannedServiceDoesNotNeedMountingType
            ? bulkMountingType
            : undefined,
        }),
      });

      const result = payload.results?.[0] || {
        jobNumber: scannedJobNumber,
        success: false,
        message: 'Réponse atelier invalide.',
      };

      setBulkResults((current) => {
        const previous = current || { successCount: 0, errorCount: 0, results: [] };
        return {
          successCount: previous.successCount + (result.success ? 1 : 0),
          errorCount: previous.errorCount + (result.success ? 0 : 1),
          results: [{ ...result, scannedAt: new Date().toISOString() }, ...previous.results],
        };
      });
      setBulkJobInput('');

      if (result.success) {
        playActionSound('success');
      } else {
        setSnackbar({
          open: true,
          severity: 'error',
          message: `${scannedJobNumber} rejeté : ${result.message}`,
        });
      }
      await loadRows();
    } catch (err) {
      workshopSheetPrintWindow?.close();
      setError(err.message);
      setSnackbar({
        open: true,
        severity: 'error',
        message: err.message,
      });
    } finally {
      setBulkSaving(false);
    }
  };

  const handlePickingSubmit = async (event) => {
    event.preventDefault();
    try {
      setPickingSaving(true);
      setError('');
      if (isAdmin && !selectedPickingOperator) {
        throw new Error('Sélectionne un opérateur atelier.');
      }

      const updatedCommande = await requestJson('/commandes/workflow/pick-stock-lenses', {
        method: 'POST',
        body: JSON.stringify({
          jobNumber: pickingJobNumber,
          rightBarcode: pickingRightBarcode,
          leftBarcode: pickingLeftBarcode,
          completedBy: isAdmin ? selectedPickingOperator._id : undefined,
        }),
      });

      setRows((current) => {
        const exists = current.some((row) => row._id === updatedCommande._id);
        return exists
          ? current.map((row) => (row._id === updatedCommande._id ? updatedCommande : row))
          : [updatedCommande, ...current];
      });
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Picking validé pour le JOB ${updatedCommande.jobNumber}.`,
      });
      setPickingJobNumber('');
      setPickingRightBarcode('');
      setPickingLeftBarcode('');
    } catch (err) {
      setError(err.message);
      setSnackbar({
        open: true,
        severity: 'error',
        message: err.message,
      });
    } finally {
      setPickingSaving(false);
    }
  };

  const commandeGridRows = filteredRows.map((row) => ({
    ...row,
    id: row._id,
    dispatchList: getActiveDispatchListForCommande(row),
    dispatchListNumber: getActiveDispatchListForCommande(row)?.listNumber || '',
    jobDisplay: row.workshopSheetNumber || row.jobNumber,
    advisorDisplay: row.advisorSnapshot?.fullName || getLabel(row.advisorId),
    customerDisplay: row.customerSnapshot?.fullName || getLabel(row.customerId),
    categoryDisplay: getCommandeCategoryLabel(row),
    storeDisplay: row.storeSiteId?.code || getLabel(row.storeSiteId),
    supplierDisplay: getCommandeSupplier(row),
    supplierAccountDisplay: getStoreSupplierAccount(row),
    mountingDisplay: getCommandeDisplayLabel(row, viewMode),
    mountingTypeDisplay: row.frame?.mountingType || '—',
    frameSelectionDisplay: getFrameTypeLabel(row.frame?.type, row.process, row.dailyOrder),
    frameOriginDisplay: getFrameOriginLabel(row.frame),
    mountingSelectionDisplay: getMountingLocationLabel(row.mountingLocation),
    workflowDisplay: workflowOptions[row.process]?.label || row.process || '—',
    statusDisplay: getSimpleCommandeStatus(row, viewMode),
    currentOwnerType: getDisplayStep(getCurrentStep(row), viewMode)?.ownerType,
    lastUpdatedAt:
      [...(row.workflow?.steps || [])]
        .reverse()
        .find((step) => step.completedAt)?.completedAt || row.createdAt || row.updatedAt || '',
    currentStepSinceAt: getCurrentStepSince(row),
    blockedDays: getDaysSince(getCurrentStepSince(row)),
    isBlocked: isCommandeBlocked(row, blockedThresholdDays),
  }));

  const selectedCommandeRows = useMemo(() => {
    if (Array.isArray(rowSelectionModel)) {
      const selectedIds = new Set(rowSelectionModel);
      return commandeGridRows.filter((row) => selectedIds.has(row.id));
    }

    const selectedIds = rowSelectionModel?.ids instanceof Set
      ? rowSelectionModel.ids
      : new Set(rowSelectionModel?.ids || []);

    // DataGrid represents "select all" with an exclusion model: every visible row
    // is selected except the IDs held in this set.
    if (rowSelectionModel?.type === 'exclude') {
      return commandeGridRows.filter((row) => !selectedIds.has(row.id));
    }

    return commandeGridRows.filter((row) => selectedIds.has(row.id));
  }, [commandeGridRows, rowSelectionModel]);
  const selectedDispatchableRows = useMemo(
    () => selectedCommandeRows.filter((row) => isDispatchListEligible(row) && !row.dispatchList),
    [selectedCommandeRows]
  );
  const selectedReturnDispatchableRows = useMemo(
    () => selectedCommandeRows.filter((row) => isReturnDispatchListEligible(row) && !row.dispatchList),
    [selectedCommandeRows]
  );
  const isWorkshopSelectionForReturn =
    selectedCommandeRows.length > 0 && selectedReturnDispatchableRows.length === selectedCommandeRows.length;
  const selectedNonDispatchableCount = selectedCommandeRows.length - (
    isWorkshopSelectionForReturn ? selectedReturnDispatchableRows.length : selectedDispatchableRows.length
  );
  const canCreateDispatchListFromSelection =
    selectedCommandeRows.length > 0 && selectedNonDispatchableCount === 0;

  const clearCommandeSelection = () => {
    setRowSelectionModel({ type: 'include', ids: new Set() });
  };

  const createDispatchListFromRows = async (rowsToDispatch, direction = 'store_to_workshop') => {
    const payload = await requestJson('/commandes/workflow/dispatch-lists', {
      method: 'POST',
      body: JSON.stringify({
        direction,
        commandeIds: rowsToDispatch.map((row) => row._id),
        createdBy: isAdmin ? selectedBulkOperator?._id : undefined,
      }),
    });
    return payload;
  };

  const handleCreateDispatchListFromSelection = () => {
    if (!selectedCommandeRows.length) {
      setSnackbar({ open: true, severity: 'warning', message: 'Sélectionne les dossiers à mettre sur le BL.' });
      return;
    }
    if (selectedNonDispatchableCount > 0) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'Le BL atelier accepte uniquement les commandes prêtes à être transmises à l’atelier.',
      });
      return;
    }
    if (isAdmin && !selectedBulkOperator) {
      setSnackbar({ open: true, severity: 'error', message: 'Sélectionne un opérateur magasin.' });
      return;
    }

    const createDispatchList = async () => {
      try {
        setDispatchListSaving(true);
        setError('');
        const payload = await createDispatchListFromRows(
          selectedCommandeRows,
          isWorkshopSelectionForReturn ? 'workshop_to_store' : 'store_to_workshop'
        );
        clearCommandeSelection();
        await loadDispatchLists({ silent: true });
        setSnackbar({
          open: true,
          severity: 'success',
          message: payload.status === 'received'
            ? `BL ${payload.listNumber} créé et réceptionné automatiquement (EDOI) avec ${payload.items?.length || 0} dossier(s).`
            : `BL ${payload.listNumber} créé en attente d’envoi avec ${payload.items?.length || 0} dossier(s).`,
        });
      } catch (err) {
        setError(err.message);
        setSnackbar({ open: true, severity: 'error', message: err.message });
      } finally {
        setDispatchListSaving(false);
      }
    };

    openConfirmDialog({
      title: 'Créer le bon de livraison',
      description: `Créer un BL avec ${selectedCommandeRows.length} dossier(s) sélectionné(s) ?`,
      confirmLabel: 'Créer le BL',
      color: 'primary',
      onConfirm: createDispatchList,
    });
  };

  const openDispatchCreateDialog = (direction = 'store_to_workshop') => {
    setDispatchCreateDirection(direction);
    setDispatchScanInput('');
    setDispatchScanRows([]);
    setDispatchCreateOpen(true);
  };

  const handleDispatchScanSubmit = (event) => {
    event?.preventDefault?.();
    const scannedJob = dispatchScanInput.trim();
    if (!scannedJob) return;
    const exactJob = scannedJob.toLowerCase();
    const row = commandeGridRows.find((item) =>
      [item.jobNumber, item.workshopSheetNumber, item.jobDisplay]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase() === exactJob)
    );
    if (!row) {
      setDispatchScanRows((current) => [
        { jobNumber: scannedJob, success: false, message: 'JOB introuvable dans le tableau filtré.' },
        ...current,
      ]);
      setDispatchScanInput('');
      return;
    }
    const eligible = dispatchCreateDirection === 'workshop_to_store'
      ? isReturnDispatchListEligible(row)
      : isDispatchListEligible(row);
    if (!eligible || row.dispatchList) {
      setDispatchScanRows((current) => [
        {
          jobNumber: scannedJob,
          success: false,
          message: row.dispatchList
            ? `JOB déjà présent dans le BL ${row.dispatchList.listNumber}.`
            : 'JOB non éligible pour ce BL.',
        },
        ...current,
      ]);
      setDispatchScanInput('');
      return;
    }
    setDispatchScanRows((current) => {
      if (current.some((item) => item.success && item._id === row._id)) return current;
      return [
        { ...row, jobNumber: row.jobNumber || scannedJob, success: true, message: 'Ajouté au BL.' },
        ...current,
      ];
    });
    setDispatchScanInput('');
  };

  const executeCreateDispatchListFromScan = async () => {
    const rowsToDispatch = dispatchScanRows.filter((row) => row.success && row._id);
    if (!rowsToDispatch.length) {
      setSnackbar({ open: true, severity: 'warning', message: 'Scanne au moins un JOB éligible.' });
      return;
    }
    try {
      setDispatchListSaving(true);
      setError('');
      const payload = await createDispatchListFromRows(rowsToDispatch, dispatchCreateDirection);
      setDispatchCreateOpen(false);
      setDispatchSendNumberInput(payload.listNumber || '');
      setSnackbar({
        open: true,
        severity: 'success',
        message: `BL ${payload.listNumber} créé. Tu peux maintenant l’envoyer.`,
      });
      await loadRows({ silent: true });
      await loadDispatchLists({ silent: true });
    } catch (err) {
      setError(err.message);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    } finally {
      setDispatchListSaving(false);
    }
  };

  const handleCreateDispatchListFromScan = () => {
    const rowsToDispatch = dispatchScanRows.filter((row) => row.success && row._id);
    if (!rowsToDispatch.length) {
      setSnackbar({ open: true, severity: 'warning', message: 'Scanne au moins un JOB éligible.' });
      return;
    }
    openConfirmDialog({
      title: 'Créer le bon de livraison',
      description: `Créer un BL avec ${rowsToDispatch.length} dossier(s) scanné(s) ? Il restera à envoyer ensuite.`,
      confirmLabel: 'Créer le BL',
      color: 'primary',
      onConfirm: executeCreateDispatchListFromScan,
    });
  };

  const sendDispatchListByNumber = async (listNumber, { closeDialog = false } = {}) => {
    try {
      setDispatchListSaving(true);
      setError('');
      const payload = await requestJson('/commandes/workflow/dispatch-lists/send-by-number', {
        method: 'POST',
        body: JSON.stringify({
          listNumber,
          completedBy: isAdmin ? selectedBulkOperator?._id : undefined,
          sentBy: dispatchSenderUserId || undefined,
          mountingType: bulkMountingType || undefined,
        }),
      });
      if (closeDialog) setDispatchSendOpen(false);
      setDispatchSendNumberInput('');
      setSnackbar({
        open: true,
        severity: payload.errorCount ? 'warning' : 'success',
        message: `BL envoyé : ${payload.successCount || 0} dossier(s), ${payload.errorCount || 0} rejeté(s).`,
      });
      await loadRows({ silent: true });
      await loadDispatchLists({ silent: true });
    } catch (err) {
      setError(err.message);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    } finally {
      setDispatchListSaving(false);
    }
  };

  const handleSendDispatchListByNumber = async (event) => {
    event?.preventDefault?.();
    const listNumber = dispatchSendNumberInput.trim();
    if (!listNumber) {
      setSnackbar({ open: true, severity: 'warning', message: 'Scanne ou saisis un numéro de BL.' });
      return;
    }
    if (!selectedDispatchSigner) {
      setSnackbar({ open: true, severity: 'warning', message: `Sélectionne le ${dispatchSendRequiresWorkshopSigner ? 'collaborateur atelier' : 'conseiller magasin'} qui signe cet envoi.` });
      return;
    }
    if (dispatchSendRequiresPrint && !dispatchSendIsPrinted) {
      setSnackbar({ open: true, severity: 'warning', message: 'Imprime le BL au moins une fois avant de l’envoyer à l’atelier.' });
      return;
    }

    openConfirmDialog({
      title: 'Envoyer le bon de livraison',
      description: `Confirmer l’envoi du BL ${listNumber.toUpperCase()} ? Les dossiers du lot passeront à l’étape suivante.`,
      confirmLabel: 'Envoyer le BL',
      color: 'primary',
      onConfirm: () => sendDispatchListByNumber(listNumber, { closeDialog: true }),
    });
  };

  const markDispatchListPrinted = async (list) => {
    if (!list?._id || list.print?.isPrinted) return;
    try {
      const payload = await requestJson(`/commandes/workflow/dispatch-lists/${list._id}/print`, {
        method: 'POST',
      });
      const updatedList = payload.data || payload;
      setDispatchLists((current) => current.map((item) =>
        item._id === list._id ? { ...item, ...updatedList, print: updatedList.print || { isPrinted: true } } : item
      ));
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err.message || 'Impossible d’enregistrer l’impression du BL.' });
    }
  };

  const handleSendDispatchListFromRow = (list) => {
    if (!list?.listNumber) return;
    setDispatchSendNumberInput(list.listNumber);
    setDispatchSenderUserId('');
    setDispatchSendOpen(true);
  };

  const canEditDispatchList = (list) => {
    if (list?.status !== 'draft') return false;
    if (isAdmin) return true;
    const direction = list?.direction || 'store_to_workshop';
    return (siteType === 'store' && direction === 'store_to_workshop' && getId(list.storeSiteId) === site?._id) ||
      (siteType === 'workshop' && direction === 'workshop_to_store' && getId(list.workshopSiteId) === site?._id);
  };

  const canDeleteDispatchList = (list) => isAdmin && (list?.status === 'draft' || list?.canDelete);

  const openDispatchListEditor = (list) => {
    if (isAdmin && !selectedBulkOperator) {
      setSnackbar({ open: true, severity: 'error', message: 'Sélectionne un opérateur du site concerné.' });
      return;
    }
    setDispatchEditTarget(list);
    setDispatchEditCommandeIds((list.items || []).map((item) => String(item.commandeId?._id || item.commandeId || '')).filter(Boolean));
    setDispatchEditCheckedIds([]);
  };

  const toggleDispatchEditChecked = (commandeId) => {
    const id = String(commandeId);
    setDispatchEditCheckedIds((current) => current.includes(id)
      ? current.filter((value) => value !== id)
      : [...current, id]);
  };

  const moveCheckedDispatchItems = (toBl) => {
    setDispatchEditCommandeIds((current) => {
      const checked = new Set(dispatchEditCheckedIds);
      return toBl
        ? [...new Set([...current, ...dispatchEditCheckedIds])]
        : current.filter((id) => !checked.has(id));
    });
    setDispatchEditCheckedIds([]);
  };

  const saveDispatchListItems = async () => {
    if (!dispatchEditTarget) return;
    if (!dispatchEditCommandeIds.length) {
      setSnackbar({ open: true, severity: 'warning', message: 'Un BL doit contenir au moins un dossier.' });
      return;
    }
    try {
      setDispatchListSaving(true);
      setError('');
      const updated = await requestJson(`/commandes/workflow/dispatch-lists/${dispatchEditTarget._id}/items`, {
        method: 'PUT',
        body: JSON.stringify({
          commandeIds: dispatchEditCommandeIds,
          completedBy: isAdmin ? selectedBulkOperator?._id : undefined,
        }),
      });
      setDispatchEditTarget(null);
      await loadDispatchLists({ silent: true });
      await loadRows({ silent: true });
      setSnackbar({
        open: true,
        severity: 'success',
        message: `BL ${updated.listNumber} mis à jour avec ${updated.items?.length || 0} dossier(s).`,
      });
    } catch (err) {
      setError(err.message);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    } finally {
      setDispatchListSaving(false);
    }
  };

  const handleDeleteDispatchList = (list) => {
    if (!list?._id) return;
    const isHistoricalList = list.status !== 'draft';
    openConfirmDialog({
      title: 'Supprimer le bon de livraison',
      description: isHistoricalList
        ? `Supprimer définitivement le BL ${list.listNumber} ? Ses JOB ne sont plus actifs, aucun workflow ne sera modifié.`
        : `Supprimer définitivement le BL ${list.listNumber} ? Les dossiers redeviendront disponibles pour un nouveau BL.`,
      confirmLabel: 'Supprimer le BL',
      color: 'error',
      onConfirm: async () => {
        try {
          setDispatchListSaving(true);
          setError('');
          const payload = await requestJson(`/commandes/workflow/dispatch-lists/${list._id}`, { method: 'DELETE' });
          if (dispatchDetailTarget?._id === list._id) setDispatchDetailTarget(null);
          if (dispatchEditTarget?._id === list._id) setDispatchEditTarget(null);
          await loadDispatchLists({ silent: true });
          await loadRows({ silent: true });
          setSnackbar({ open: true, severity: 'success', message: payload.message || `BL ${list.listNumber} supprimé.` });
        } catch (err) {
          setError(err.message);
          setSnackbar({ open: true, severity: 'error', message: err.message });
        } finally {
          setDispatchListSaving(false);
        }
      },
    });
  };

  const receiveDispatchListByNumber = async (listNumber, { closeDialog = false, itemIds } = {}) => {
    try {
      setDispatchListSaving(true);
      setError('');
      const payload = await requestJson('/commandes/workflow/dispatch-lists/receive-by-number', {
        method: 'POST',
        body: JSON.stringify({
          listNumber,
          itemIds,
          completedBy: isAdmin ? selectedBulkOperator?._id : undefined,
        }),
      });
      const results = payload.results || [];
      setBulkResults({
        successCount: payload.successCount || results.filter((result) => result.success).length,
        errorCount: payload.errorCount || results.filter((result) => !result.success).length,
        results: results.map((result) => ({ ...result, scannedAt: new Date().toISOString() })),
      });
      setDispatchListNumberInput('');
      if (closeDialog) setDispatchReceiveOpen(false);
      await loadRows({ silent: true });
      await loadDispatchLists({ silent: true });
      setSnackbar({
        open: true,
        severity: payload.errorCount ? 'warning' : 'success',
        message: `BL ${payload.list?.listNumber || listNumber} réceptionné : ${payload.successCount} validé(s), ${payload.errorCount} rejeté(s).`,
      });
    } catch (err) {
      setError(err.message);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    } finally {
      setDispatchListSaving(false);
    }
  };

  const canReceiveDispatchList = (list) => {
    const status = list?.status || '';
    const direction = list?.direction || 'store_to_workshop';
    if (!['sent', 'pending'].includes(status)) return false;
    if (isAdmin) return true;
    return (siteType === 'store' && direction === 'workshop_to_store') ||
      (siteType === 'workshop' && direction === 'store_to_workshop');
  };

  const handleReceiveDispatchListFromRow = (list) => {
    if (!list?.listNumber) return;
    if (isAdmin && !selectedBulkOperator) {
      setSnackbar({ open: true, severity: 'error', message: 'Sélectionne un opérateur du site concerné.' });
      return;
    }
    openConfirmDialog({
      title: 'Réceptionner le BL',
      description: `Confirmer la réception du BL ${list.listNumber} avec ${list.itemCount || 0} dossier(s) ?`,
      confirmLabel: 'Réceptionner',
      color: 'primary',
      onConfirm: () => receiveDispatchListByNumber(list.listNumber),
    });
  };

  const handlePartialReceiveDispatchList = (list) => {
    if (!list?.listNumber) return;
    setPartialReceiveTarget(list);
    setPartialReceiveIds((list.items || []).filter((item) => item.status !== 'received').map((item) => String(item.commandeId)));
  };

  const handlePrintDispatchList = (list, { senderUser = null } = {}) => {
    const items = list.items || [];
    const hasClientOrAdvisor = items.some((item) => {
      const customerName = String(item.customerName || '').trim();
      const advisorName = String(item.advisorName || '').trim();
      return Boolean(customerName || advisorName);
    });
    const optionalHeaderHtml = hasClientOrAdvisor ? '<th>Client</th><th>Conseiller</th>' : '';
    const emptyColspan = hasClientOrAdvisor ? 4 : 2;
    const barcodeSvg = getDispatchBarcodeSvg(list.listNumber);
    const departureSigner = senderUser || list.sentBy || null;
    const departureSignerLabel = departureSigner ? getUserLabel(departureSigner) : 'À renseigner lors de l’envoi';
    const rowsHtml = items.length
      ? items.map((item, index) => `
          <tr>
            <td>${index + 1}</td>
            <td class="job">${escapeHtml(item.jobNumber || '—')}</td>
            ${hasClientOrAdvisor ? `
              <td>${escapeHtml(item.customerName || '—')}</td>
              <td>${escapeHtml(item.advisorName || '—')}</td>
            ` : ''}
          </tr>
        `).join('')
      : `<tr><td colspan="${emptyColspan}" class="empty">Aucun dossier dans ce BL.</td></tr>`;

    const html = `
      <!doctype html>
      <html lang="fr">
        <head>
          <meta charset="utf-8" />
          <title>BL ${escapeHtml(list.listNumber || '')}</title>
          <style>
            @page { size: A4; margin: 14mm; }
            * { box-sizing: border-box; }
            body {
              margin: 0;
              font-family: Arial, Helvetica, sans-serif;
              color: #0f172a;
              background: #ffffff;
            }
            .header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 24px;
              border-bottom: 3px solid #0ea5e9;
              padding-bottom: 16px;
              margin-bottom: 18px;
            }
            .brand { display: flex; align-items: center; gap: 12px; }
            .brand img { width: 54px; height: 54px; }
            h1 { margin: 0; font-size: 26px; }
            .subtitle { margin-top: 4px; color: #475569; font-size: 13px; }
            .chip {
              display: inline-block;
              padding: 6px 10px;
              border-radius: 999px;
              background: #dbeafe;
              color: #1e40af;
              font-weight: 700;
              font-size: 12px;
            }
            .barcode { width: 230px; height: 58px; margin-top: 8px; }
            .meta {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 10px 20px;
              margin: 16px 0 22px;
              font-size: 13px;
            }
            .meta div { padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 8px; }
            .label { display: block; color: #64748b; font-size: 11px; text-transform: uppercase; font-weight: 700; margin-bottom: 4px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #cbd5e1; padding: 8px 9px; text-align: left; vertical-align: top; }
            th { background: #f1f5f9; font-weight: 800; }
            td:first-child, th:first-child { width: 42px; text-align: center; }
            .job { font-weight: 800; }
            .empty { text-align: center; color: #64748b; padding: 24px; }
            .footer { margin-top: 28px; display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
            .signature { border: 1px solid #cbd5e1; border-radius: 8px; height: 90px; padding: 10px; color: #64748b; font-size: 12px; }
            @media print { .no-print { display: none; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="brand">
              <img src="/optibox-logo.svg" alt="Optibox" />
              <div>
                <h1>Bon de livraison</h1>
                <div class="subtitle">Optibox</div>
              </div>
            </div>
            <div>
              <div class="chip">${escapeHtml(list.listNumber || '—')}</div>
              <div class="barcode">${barcodeSvg}</div>
            </div>
          </div>
          <div class="meta">
            <div><span class="label">Sens</span>${escapeHtml(list.directionLabel || '—')}</div>
            <div><span class="label">Statut</span>${escapeHtml(list.statusLabel || '—')}</div>
            <div><span class="label">Magasin</span>${escapeHtml(list.storeLabel || '—')}</div>
            <div><span class="label">Atelier</span>${escapeHtml(list.workshopLabel || '—')}</div>
            <div><span class="label">Créé le</span>${escapeHtml(formatDateTime(list.createdAt))}</div>
            <div><span class="label">Nombre de dossiers</span>${items.length}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>JOB</th>
                ${optionalHeaderHtml}
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
          <div class="footer">
            <div class="signature">Signature départ<br /><strong>${escapeHtml(departureSignerLabel)}</strong></div>
            <div class="signature">Signature réception</div>
          </div>
          <script>
            window.addEventListener('load', () => {
              window.focus();
              window.print();
            });
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=960,height=720');
    if (!printWindow) {
      setSnackbar({ open: true, severity: 'error', message: 'Impossible d’ouvrir la fenêtre d’impression.' });
      return;
    }

    try {
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.opener = null;
      if ((list.direction || 'store_to_workshop') === 'store_to_workshop') {
        void markDispatchListPrinted(list);
      }
    } catch (_error) {
      try {
        const blobUrl = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
        printWindow.location.href = blobUrl;
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      } catch (_fallbackError) {
        printWindow.close();
        setSnackbar({ open: true, severity: 'error', message: 'Impossible de préparer le BL à imprimer.' });
      }
    }
  };

  const handlePrintDispatchLists = (lists) => {
    if (!lists?.length) return;
    const sections = lists.map((list) => {
      const items = list.items || [];
      const rows = items.length
        ? items.map((item, index) => `
            <tr>
              <td>${index + 1}</td>
              <td><strong>${escapeHtml(item.jobNumber || '—')}</strong></td>
              <td>${escapeHtml(item.customerName || '—')}</td>
              <td>${escapeHtml(item.advisorName || '—')}</td>
            </tr>
          `).join('')
        : '<tr><td colspan="4" class="empty">Aucun dossier dans ce BL.</td></tr>';
      return `
        <section>
          <header>
            <h2>${escapeHtml(list.listNumber || 'BL')}</h2>
            <span>${escapeHtml(list.directionLabel || '—')} · ${escapeHtml(list.statusLabel || '—')}</span>
          </header>
          <p><strong>Magasin :</strong> ${escapeHtml(list.storeLabel || '—')} &nbsp; <strong>Atelier :</strong> ${escapeHtml(list.workshopLabel || '—')}</p>
          <table>
            <thead><tr><th>#</th><th>JOB</th><th>Client</th><th>Conseiller</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </section>`;
    }).join('');
    const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8" /><title>BL à réceptionner</title><style>@page{size:A4;margin:14mm}*{box-sizing:border-box}body{margin:0;font-family:Arial,Helvetica,sans-serif;color:#0f172a}h1{margin:0 0 18px;font-size:24px}section{break-after:page;margin-bottom:24px}section:last-child{break-after:auto}header{display:flex;justify-content:space-between;align-items:baseline;gap:16px;border-bottom:2px solid #0ea5e9;padding-bottom:8px}h2{margin:0;font-size:18px}header span,p{font-size:12px;color:#475569}table{width:100%;border-collapse:collapse;font-size:12px}th,td{border:1px solid #cbd5e1;padding:8px;text-align:left}th{background:#f1f5f9}.empty{text-align:center;color:#64748b}</style></head><body><h1>Liste des bons de livraison à réceptionner</h1>${sections}<script>window.addEventListener('load',()=>{window.focus();window.print()})</script></body></html>`;
    const printWindow = window.open('', '_blank', 'width=960,height=720');
    if (!printWindow) {
      setSnackbar({ open: true, severity: 'error', message: 'Impossible d’ouvrir la fenêtre d’impression.' });
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.opener = null;
  };

  const handleExportDispatchListsCsv = (lists) => {
    const rows = [
      ['BL', 'Sens', 'Statut', 'Magasin', 'Atelier', 'JOB', 'Client', 'Conseiller'],
      ...lists.flatMap((list) => (list.items?.length ? list.items : [{}]).map((item) => [
        list.listNumber || '',
        list.directionLabel || '',
        list.statusLabel || '',
        list.storeLabel || '',
        list.workshopLabel || '',
        item.jobNumber || '',
        item.customerName || '',
        item.advisorName || '',
      ])),
    ];
    downloadCsv(`bl-a-receptionner-${new Date().toISOString().slice(0, 10)}.csv`, rows);
  };

  const openAutoReturnBlDialog = (listIds = null) => {
    const ids = listIds || returnDraftDispatchLists.map((list) => list._id);
    setAutoReturnBlSelection(ids);
    setAutoReturnBlOpen(true);
  };

  const executeAutoCreateReturnBls = async () => {
    try {
      setAutoReturnBlSaving(true);
      setError('');
      const payload = await requestJson('/commandes/workflow/dispatch-lists/auto-return', {
        method: 'POST',
        body: JSON.stringify({ createdBy: isAdmin ? selectedBulkOperator?._id : undefined }),
      });
      await loadRows({ silent: true });
      await loadDispatchLists({ silent: true });
      const listIds = (payload.lists || []).map((list) => list._id);
      setAutoReturnBlSelection(listIds);
      setAutoReturnBlOpen(true);
      setSnackbar({
        open: true,
        severity: payload.createdCount ? 'success' : 'info',
        message: payload.createdCount
          ? `${payload.createdCount} BL retour créé(s) pour ${payload.commandeCount} dossier(s).${payload.autoReceivedCount ? ` Dont ${payload.autoReceivedCount} BL EDOI réceptionné(s) automatiquement.` : ''}`
          : 'Aucun dossier prêt à envoyer au magasin.',
      });
    } catch (err) {
      setError(err.message);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    } finally {
      setAutoReturnBlSaving(false);
    }
  };

  const handleAutoCreateReturnBls = () => {
    if (isAdmin && !selectedBulkOperator) {
      setSnackbar({ open: true, severity: 'error', message: 'Sélectionne un opérateur atelier.' });
      return;
    }
    setAutoReturnBlSaving(true);
    setError('');
    requestJson('/commandes/workflow/dispatch-lists/auto-return', {
      method: 'POST',
      body: JSON.stringify({ preview: true, createdBy: isAdmin ? selectedBulkOperator?._id : undefined }),
    })
      .then((payload) => {
        setAutoReturnBlPreview(payload);
        setAutoReturnBlPreviewOpen(true);
      })
      .catch((err) => {
        setError(err.message);
        setSnackbar({ open: true, severity: 'error', message: err.message });
      })
      .finally(() => setAutoReturnBlSaving(false));
  };

  const handlePrintAutoReturnBls = () => {
    const lists = selectedAutoReturnBlLists;
    if (!lists.length) {
      setSnackbar({ open: true, severity: 'warning', message: 'Sélectionne au moins un BL à imprimer.' });
      return;
    }
    const printWindow = window.open('', '_blank', 'width=960,height=720');
    if (!printWindow) {
      setSnackbar({ open: true, severity: 'error', message: 'Impossible d’ouvrir la fenêtre d’impression.' });
      return;
    }
    const pages = lists.map((list) => {
      const departureSignerLabel = getUserLabel(selectedDispatchSender || list.sentBy) || 'À renseigner lors de l’envoi';
      const rowsHtml = (list.items || []).map((item, index) =>
        `<tr><td>${index + 1}</td><td>${escapeHtml(item.jobNumber || '—')}</td><td>${escapeHtml(item.customerName || '—')}</td><td>${escapeHtml(item.advisorName || '—')}</td></tr>`
      ).join('') || '<tr><td colspan="4">Aucun dossier</td></tr>';
      return `<section class="page"><header><div><h1>Bon de livraison</h1><p>Optibox · Atelier vers magasin</p></div><div><strong>${escapeHtml(list.listNumber || '—')}</strong><div class="barcode">${getDispatchBarcodeSvg(list.listNumber)}</div></div></header><div class="meta"><span><b>Magasin</b>${escapeHtml(list.storeLabel || '—')}</span><span><b>Atelier</b>${escapeHtml(list.workshopLabel || '—')}</span><span><b>Créé le</b>${escapeHtml(formatDateTime(list.createdAt))}</span><span><b>Dossiers</b>${(list.items || []).length}</span></div><table><thead><tr><th>#</th><th>JOB</th><th>Client</th><th>Conseiller</th></tr></thead><tbody>${rowsHtml}</tbody></table><footer><div>Signature départ<br/><strong>${escapeHtml(departureSignerLabel)}</strong></div><div>Signature réception</div></footer></section>`;
    }).join('');
    const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8"/><title>BL retours atelier</title><style>@page{size:A4;margin:14mm}*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#0f172a;margin:0}.page{break-after:page;min-height:260mm}.page:last-child{break-after:auto}header{display:flex;justify-content:space-between;gap:20px;border-bottom:3px solid #0ea5e9;padding-bottom:12px}h1{margin:0;font-size:24px}p{margin:5px 0 0;color:#475569}.barcode{width:220px;height:58px;margin-top:7px}.meta{display:grid;grid-template-columns:repeat(2,1fr);gap:10px;margin:18px 0}.meta span,footer div{border:1px solid #cbd5e1;border-radius:6px;padding:9px}.meta b{display:block;font-size:10px;text-transform:uppercase;color:#64748b;margin-bottom:4px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{border:1px solid #cbd5e1;padding:8px;text-align:left}th{background:#f1f5f9}footer{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:30px}footer div{height:82px;color:#64748b}</style></head><body>${pages}<script>window.addEventListener('load',()=>{window.focus();window.print()})</script></body></html>`;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.opener = null;
  };

  const executeSendAutoReturnBls = async () => {
    try {
      setAutoReturnBlSaving(true);
      setError('');
      const payload = await requestJson('/commandes/workflow/dispatch-lists/send-batch', {
        method: 'POST',
        body: JSON.stringify({
          listIds: selectedAutoReturnBlLists.map((list) => list._id),
          completedBy: isAdmin ? selectedBulkOperator?._id : undefined,
          sentBy: dispatchSenderUserId || undefined,
        }),
      });
      await loadRows({ silent: true });
      await loadDispatchLists({ silent: true });
      setAutoReturnBlSelection([]);
      setSnackbar({
        open: true,
        severity: payload.errorCount ? 'warning' : 'success',
        message: `${payload.successCount || 0} BL envoyé(s), ${payload.errorCount || 0} en erreur.`,
      });
    } catch (err) {
      setError(err.message);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    } finally {
      setAutoReturnBlSaving(false);
    }
  };

  const handleSendAutoReturnBls = () => {
    if (!selectedAutoReturnBlLists.length) {
      setSnackbar({ open: true, severity: 'warning', message: 'Sélectionne au moins un BL à envoyer.' });
      return;
    }
    if (!selectedDispatchSender) {
      setSnackbar({ open: true, severity: 'warning', message: 'Sélectionne le collaborateur atelier qui signe les BL.' });
      return;
    }
    openConfirmDialog({
      title: 'Envoyer les BL retour',
      description: `Confirmer l’envoi de ${selectedAutoReturnBlLists.length} BL vers les magasins ?`,
      confirmLabel: 'Envoyer les BL',
      color: 'primary',
      onConfirm: executeSendAutoReturnBls,
    });
  };

  const handleReceiveDispatchListByNumber = (event) => {
    event?.preventDefault?.();
    const listNumber = dispatchListNumberInput.trim();
    if (!listNumber) {
      setSnackbar({ open: true, severity: 'warning', message: 'Scanne ou saisis un numéro de BL.' });
      return;
    }
    if (isAdmin && !selectedBulkOperator) {
      setSnackbar({ open: true, severity: 'error', message: 'Sélectionne un opérateur du site concerné.' });
      return;
    }

    openConfirmDialog({
      title: 'Réceptionner le BL',
      description: `Confirmer la réception du BL ${listNumber.toUpperCase()} ? Tous les dossiers du lot seront réceptionnés.`,
      confirmLabel: 'Réceptionner le lot',
      color: 'primary',
      onConfirm: () => receiveDispatchListByNumber(listNumber, { closeDialog: true }),
    });
  };

  const handleExportWorkshopCsv = () => {
    const rows = [
      ['JOB', 'Type de montage', 'Magasin'],
      ...filteredRows.map((row) => [
        row.workshopSheetNumber || row.jobNumber || '',
        row.frame?.mountingType || '',
        [row.storeSiteId?.code, getSiteLabel(row.storeSiteId)]
          .filter(Boolean)
          .join(' - ') || getLabel(row.storeSiteId),
      ]),
    ];

    downloadCsv(`suivi-atelier-${new Date().toISOString().slice(0, 10)}.csv`, rows);
  };

  const handlePrintSpecialWorkshopSheet = (row) => {
    const isSpecialProcess = ['RETAILLAGE', 'CLIP'].includes(row?.process);
    if (!isSpecialProcess) return;

    const printWindow = window.open('', '_blank', 'width=960,height=720');
    if (!printWindow) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Autorise les fenêtres contextuelles pour imprimer la fiche atelier.',
      });
      return;
    }

    printSpecialWorkshopSheet(printWindow, {
      jobNumber: row.jobNumber || row.workshopSheetNumber,
      customerName: row.customerSnapshot?.fullName || getLabel(row.customerId) || '—',
      processLabel: row.process === 'CLIP' ? 'CLIP' : 'RETAILLAGE',
      storeLabel: [row.storeSiteId?.code, getSiteLabel(row.storeSiteId)].filter(Boolean).join(' - ') || '—',
    });
  };

  const commandeGridColumns = [
    {
      field: 'workflowAction',
      headerName: 'Suivi',
      minWidth: 210,
      flex: 0.85,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row;
        const currentStep = getCurrentStep(row);
        const dispatchList = row.dispatchList;

        // Un BL partiel ne doit pas bloquer l'action du dossier : les dossiers
        // restants peuvent être remis dans un nouveau BL.
        if (dispatchList && dispatchList.status !== 'partial') {
          const dispatchStatusLabel =
            dispatchList.status === 'draft'
              ? 'À envoyer'
              : dispatchList.status === 'partial'
                ? 'Partiel'
                : 'En attente réception';
          return (
            <Stack justifyContent="center" sx={{ height: '100%', width: '100%', py: 0.5 }}>
              <Tooltip title={`JOB déjà affecté au BL ${dispatchList.listNumber}`}>
                <Chip
                  size="small"
                  label={`Dans BL · ${dispatchStatusLabel}`}
                  color={dispatchList.status === 'draft' ? 'info' : 'secondary'}
                  variant="outlined"
                  sx={{ width: 'fit-content', fontWeight: 850 }}
                />
              </Tooltip>
            </Stack>
          );
        }

        if (canActOnCommande(row)) {
          const buttonSx = getRowWorkflowButtonSx(row);

          return (
            <Stack justifyContent="center" sx={{ height: '100%', width: '100%', py: 0.5 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<QrCodeScannerIcon />}
                onClick={() => handleCompleteRowStep(row)}
                disabled={saving}
                sx={{
                  ...buttonSx,
                  fontWeight: 900,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  boxShadow: '0 8px 18px rgba(15, 23, 42, 0.16), inset 0 1px 0 rgba(255,255,255,0.22)',
                  '& .MuiButton-startIcon': { color: 'inherit' },
                  '&:hover': {
                    ...buttonSx,
                    filter: 'brightness(1.08)',
                    boxShadow: '0 10px 22px rgba(15, 23, 42, 0.22), inset 0 1px 0 rgba(255,255,255,0.28)',
                  },
                }}
              >
                {getRowWorkflowActionLabel(row)}
              </Button>
            </Stack>
          );
        }

        if (canPrepareDispatch(row)) {
          return (
            <Stack justifyContent="center" sx={{ height: '100%', width: '100%', py: 0.5 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<LocalShippingRoundedIcon />}
                onClick={() => setRowSelectionModel({ type: 'include', ids: new Set([row.id]) })}
                sx={{
                  fontWeight: 900,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  color: '#ffffff',
                  background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)',
                  '&:hover': { background: 'linear-gradient(135deg, #0284c7, #0891b2)' },
                }}
              >
                Mettre en BL
              </Button>
            </Stack>
          );
        }

        const waitingSite = getWaitingSiteForStep(row, currentStep);

        return (
          <Stack justifyContent="center" sx={{ height: '100%', width: '100%', py: 0.5 }}>
            <Tooltip
              title={
                currentStep && waitingSite ? (
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ py: 0.5 }}>
                    <Avatar
                      src={waitingSite.logoUrl || undefined}
                      alt={getLabel(waitingSite)}
                      sx={{
                        width: 30,
                        height: 30,
                        bgcolor: currentStep.ownerType === 'workshop' ? 'secondary.main' : 'primary.main',
                        fontSize: 13,
                        fontWeight: 900,
                      }}
                    >
                      {getSiteAvatarText(waitingSite)}
                    </Avatar>
                    <Box>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 900, lineHeight: 1.1 }}>
                        {currentStep.ownerType === 'workshop' ? 'Atelier attendu' : 'Magasin attendu'}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.1 }}>
                        {[waitingSite.code, getLabel(waitingSite)].filter(Boolean).join(' - ')}
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  ''
                )
              }
              arrow
            >
              <Chip
                size="small"
                label={
                  currentStep
                    ? isDispatchListEligible(row) || isReturnDispatchListEligible(row)
                      ? 'À mettre en BL'
                      : currentStep.ownerType === 'workshop'
                      ? 'En attente atelier'
                      : 'En attente magasin'
                    : 'Terminé'
                }
                color={!currentStep ? 'success' : isDispatchListEligible(row) || isReturnDispatchListEligible(row) ? 'info' : 'default'}
                variant="outlined"
                sx={{ width: 'fit-content' }}
              />
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      field: 'jobDisplay',
      headerName: 'JOB',
      minWidth: 90,
      flex: 0.4,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 900 }}>
          {params.value}
        </Typography>
      ),
    },
    ...(isWorkshopFollowupView
      ? [
          {
            field: 'dispatchListNumber',
            headerName: 'BL',
            minWidth: 145,
            flex: 0.7,
            renderCell: (params) =>
              params.value ? (
                <Tooltip title="Bon de livraison en cours">
                  <Chip size="small" label={params.value} color="primary" variant="outlined" sx={{ fontWeight: 850 }} />
                </Tooltip>
              ) : (
                <Typography variant="body2" color="text.secondary">—</Typography>
              ),
          },
          {
            field: 'categoryDisplay',
            headerName: 'Catégorie',
            minWidth: 150,
            flex: 0.72,
            renderCell: (params) => (
              <Chip
                size="small"
                label={params.value}
                color={isStockCommande(params.row) ? 'info' : 'secondary'}
                variant="outlined"
                sx={{ fontWeight: 800 }}
              />
            ),
          },
        ]
      : []),
    ...(!isWorkshopFollowupView
      ? [
          { field: 'advisorDisplay', headerName: 'Conseiller', minWidth: 170, flex: 1 },
          { field: 'customerDisplay', headerName: 'Client', minWidth: 180, flex: 1 },
        ]
      : [
          {
            field: 'customerDisplay',
            headerName: 'Client',
            minWidth: 170,
            flex: 0.75,
            valueFormatter: (value) => value || '—',
          },
          {
            field: 'processSelectionDisplay',
            headerName: 'Monture / Montage',
            minWidth: 220,
            flex: 0.96,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%', minWidth: 0 }}>
                <Chip
                  size="small"
                  label={params.row.frameSelectionDisplay || '—'}
                  color={params.row.frame?.type === 'existing' ? 'secondary' : 'primary'}
                  variant="outlined"
                  sx={{ height: 21, fontSize: '0.68rem', fontWeight: 800 }}
                />
                <Chip
                  size="small"
                  label={params.row.mountingSelectionDisplay && params.row.mountingSelectionDisplay !== '—'
                    ? `Montage ${params.row.mountingSelectionDisplay}`
                    : '—'}
                  color={params.row.mountingLocation === 'workshop' ? 'success' : 'info'}
                  variant="outlined"
                  sx={{ height: 21, fontSize: '0.68rem', fontWeight: 800 }}
                />
              </Stack>
            ),
          },
        ]),
    {
      field: 'storeDisplay',
      headerName: 'Site',
      minWidth: 115,
      flex: 0.48,
      renderCell: (params) => {
        const storeSite = params.row.storeSiteId;
        return (
          <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0, height: '100%' }}>
            <Avatar
              src={storeSite?.logoUrl || undefined}
              alt={getLabel(storeSite)}
              sx={{
                width: 30,
                height: 30,
                bgcolor: 'primary.main',
                fontSize: 12,
                fontWeight: 900,
                flexShrink: 0,
              }}
            >
              {getSiteAvatarText(storeSite)}
            </Avatar>
            <Typography variant="body2" noWrap title={params.value} sx={{ fontWeight: 700 }}>
              {params.value || '—'}
            </Typography>
          </Stack>
        );
      },
    },
    ...(!isWorkshopFollowupView
      ? [
          { field: 'supplierDisplay', headerName: 'Fournisseur', minWidth: 130, flex: 0.8 },
          { field: 'supplierAccountDisplay', headerName: 'Compte fournisseur', minWidth: 170, flex: 0.9 },
          {
            field: 'mountingDisplay',
            headerName: 'Monture',
            minWidth: 170,
            flex: 0.9,
            renderCell: (params) => (
              <Chip size="small" label={params.value || '—'} color="primary" variant="outlined" />
            ),
          },
          { field: 'workflowDisplay', headerName: 'Workflow', minWidth: 220, flex: 1.1 },
        ]
      : [
          {
            field: 'mountingTypeDisplay',
            headerName: 'Type de montage',
            minWidth: 170,
            flex: 0.9,
            renderCell: (params) =>
              params.value && params.value !== '—' ? (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
                  <Chip
                    size="small"
                    label={params.value}
                    sx={{
                      ...getMountingTypeChipSx(params.value),
                      fontWeight: 800,
                    }}
                  />
                  {params.row.frame?.mountingTypeHistory?.length > 0 && (
                    <Tooltip title={`${params.row.frame.mountingTypeHistory.length} modification(s) du type de montage`}>
                      <HistoryRoundedIcon fontSize="small" color="warning" />
                    </Tooltip>
                  )}
                  {canEditMountingType(params.row) && (
                    <Tooltip title="Modifier le type de montage">
                      <IconButton size="small" onClick={() => handleOpenMountingTypeEdit(params.row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              ) : (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
                  <Typography variant="body2">—</Typography>
                  {canEditMountingType(params.row) && (
                    <Tooltip title="Renseigner le type de montage">
                      <IconButton size="small" onClick={() => handleOpenMountingTypeEdit(params.row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              ),
          },
        ]),
    {
      field: 'lastUpdatedAt',
      headerName: 'Dernière mise à jour',
      minWidth: 180,
      flex: 0.9,
      valueFormatter: (value) => formatDateTime(value),
    },
    {
      field: 'indicators',
      headerName: isWorkshopFollowupView ? '' : 'Indicateurs',
      description: 'Indicateurs',
      width: isWorkshopFollowupView ? 56 : undefined,
      minWidth: isWorkshopFollowupView ? 56 : 132,
      maxWidth: isWorkshopFollowupView ? 56 : undefined,
      flex: isWorkshopFollowupView ? 0 : 0.55,
      align: isWorkshopFollowupView ? 'center' : 'right',
      headerAlign: isWorkshopFollowupView ? 'center' : 'right',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const ncaItems = getActiveNonConformitiesByType(params.row, 'NCA');
        const ncmItems = getActiveNonConformitiesByType(params.row, 'NCM');
        const traceabilityCount = params.row.traceabilityReports?.length || 0;
        const missingInitialFile = params.row.missingInitialFile;
        return (
          <Stack direction="row" spacing={0.35} alignItems="center" justifyContent={isWorkshopFollowupView ? 'center' : 'flex-end'} sx={{ width: '100%' }}>
            {ncaItems.length > 0 && <NonConformityMarker type="NCA" count={ncaItems.length} />}
            {ncmItems.length > 0 && <NonConformityMarker type="NCM" count={ncmItems.length} />}
            {traceabilityCount > 0 && (
              <Tooltip title={`${traceabilityCount} fiche${traceabilityCount > 1 ? 's' : ''} Traçabilité`}>
                <Box
                  component="span"
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(20, 184, 166, 0.14)',
                    color: 'success.main',
                    border: '1px solid',
                    borderColor: 'rgba(20, 184, 166, 0.45)',
                  }}
                >
                  <FactCheckRoundedIcon sx={{ fontSize: 16 }} />
                </Box>
              </Tooltip>
            )}
            {hasMountingImpossibleAlert(params.row) && (
              <Tooltip title="Montage impossible signalé">
                <WarningAmberRoundedIcon fontSize="small" sx={{ color: 'error.main' }} />
              </Tooltip>
            )}
            {hasPendingMissingInitialFile(params.row) && (
              <Tooltip title={missingInitialFile.resolvedAt
                ? `Fiche dossier initialement absente, régularisée le ${formatDateTime(missingInitialFile.resolvedAt)}`
                : 'Fiche dossier absente : informations attendues du magasin'}
              >
                <WarningAmberRoundedIcon fontSize="small" sx={{ color: missingInitialFile.resolvedAt ? 'warning.main' : 'error.main' }} />
              </Tooltip>
            )}
            {!ncaItems.length && !ncmItems.length && !traceabilityCount && !hasMountingImpossibleAlert(params.row) && !missingInitialFile?.detectedAt && (
              <Typography variant="body2" color="text.secondary">—</Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Action',
      // Les dossiers Retaillage/Clip affichent l'œil, l'impression, la
      // modification, la suppression et le menu. La largeur historique de
      // 132 px rognait le premier bouton (l'œil) dans le tableau atelier.
      width: isWorkshopFollowupView ? 188 : 132,
      minWidth: isWorkshopFollowupView ? 188 : 132,
      flex: 0,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row;
        const hasMenuActions =
          canActOnCommande(row) ||
          canDeclareNonConformity ||
          canDeclareTraceability(row);

        return (
          <Stack direction="row" spacing={0.25} justifyContent="flex-end" sx={{ width: '100%' }}>
            <Tooltip title="Voir">
              <IconButton
                size="small"
                onClick={async () => {
                  setViewingRow(row);
                  try {
                    const payload = await requestJson(`/commandes/${row._id}`);
                    setViewingRow(payload?.data || payload);
                  } catch (_error) {
                    // La ligne déjà chargée reste affichée si le détail n'est pas disponible.
                  }
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {isWorkshopFollowupView && ['RETAILLAGE', 'CLIP'].includes(row.process) && (
              <Tooltip title="Imprimer la fiche atelier">
                <IconButton size="small" color="primary" onClick={() => handlePrintSpecialWorkshopSheet(row)}>
                  <PrintRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {(isAdmin || canStoreEditInitialCommande(row)) && (
              <>
                <Tooltip title="Modifier">
                  <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {(isAdmin || canStoreEditInitialCommande(row)) && (
                  <Tooltip title="Supprimer">
                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
            {hasMenuActions && (
              <Tooltip title="Actions métier">
                <IconButton size="small" onClick={(event) => handleOpenActionMenu(event, row)}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  const selectedNonConformityReason = nonConformityReasonOptions.find(
    (option) => option.value === nonConformityForm.reason
  );
  const availableNonConformityScopeOptions = selectedNonConformityReason
    ? nonConformityScopeOptions.filter((option) => selectedNonConformityReason.scopes?.includes(option.value))
    : [];
  const viewingMailHistory = viewingRow?.mailHistory || [];
  const viewingTraceabilityMails = viewingMailHistory.filter((item) => item.type === 'traceability_notification');
  const viewingOtherMails = viewingMailHistory.filter((item) => item.type !== 'traceability_notification');
  const renderDispatchSortHeader = (field, label, align = 'left') => {
    const active = dispatchListSort.field === field;
    return (
      <TableCell align={align} sortDirection={active ? dispatchListSort.direction : false}>
        <TableSortLabel
          active={active}
          direction={active ? dispatchListSort.direction : 'asc'}
          onClick={() => toggleDispatchListSort(field)}
          sx={{ fontWeight: 850 }}
        >
          {label}
        </TableSortLabel>
      </TableCell>
    );
  };
  const renderDispatchListTable = (title, lists) => (
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1} alignItems="center">
        <LocalShippingRoundedIcon color="primary" fontSize="small" />
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {dispatchTable.loading
              ? 'Chargement…'
              : `${dispatchTable.total} BL au total${dispatchTable.total > lists.length ? ` · ${lists.length} sur cette page` : ''}.`}
          </Typography>
        </Box>
      </Stack>
      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxHeight: 320 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Actions</TableCell>
              {renderDispatchSortHeader('listNumber', 'BL')}
              <TableCell>Sens</TableCell>
              {renderDispatchSortHeader('status', 'Statut')}
              {renderDispatchSortHeader('magasin', 'Magasin')}
              {renderDispatchSortHeader('atelier', 'Atelier')}
              {renderDispatchSortHeader('dossiers', 'Dossiers', 'right')}
              {renderDispatchSortHeader('createdAt', 'Créé le')}
              {renderDispatchSortHeader('sentAt', 'Envoyé le')}
              {renderDispatchSortHeader('receivedAt', 'Réceptionné le')}
            </TableRow>
          </TableHead>
          <TableBody>
            {lists.map((list) => (
              <TableRow key={list._id} hover>
                <TableCell>
                  <Stack direction="row" spacing={0.5} justifyContent="flex-start">
                    {(list.direction || 'store_to_workshop') === 'store_to_workshop' && list.status !== 'received' && (list.items || []).some((item) => item.alreadyReceivedAtWorkshop) && (
                      <Tooltip title="Au moins un dossier de ce BL a déjà été réceptionné à l’atelier, mais le BL n’est pas encore réceptionné.">
                        <WarningAmberRoundedIcon fontSize="small" sx={{ color: 'warning.main' }} />
                      </Tooltip>
                    )}
                    {list.status === 'draft' && (
                      <Tooltip title="Envoyer le BL">
                        <span>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<LocalShippingRoundedIcon />}
                            onClick={() => handleSendDispatchListFromRow(list)}
                            disabled={dispatchListSaving}
                            sx={{ fontWeight: 900, minWidth: 0 }}
                          >
                            Envoyer
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    {canReceiveDispatchList(list) && (
                      <Tooltip title="Réceptionner le BL">
                        <span>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<MoveToInboxRoundedIcon />}
                            onClick={() => handleReceiveDispatchListFromRow(list)}
                            disabled={dispatchListSaving}
                            sx={{ fontWeight: 900, minWidth: 0 }}
                          >
                            Réceptionner
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    {canReceiveDispatchList(list) && (list.items || []).length > 1 && (
                      <Tooltip title="Réceptionner une sélection de dossiers">
                        <IconButton size="small" color="warning" onClick={() => handlePartialReceiveDispatchList(list)}>
                          <FactCheckRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canEditDispatchList(list) && (
                      <Tooltip title="Modifier les dossiers du BL">
                        <IconButton size="small" color="primary" onClick={() => openDispatchListEditor(list)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDeleteDispatchList(list) && (
                      <Tooltip title="Supprimer le BL">
                        <IconButton size="small" color="error" onClick={() => handleDeleteDispatchList(list)} disabled={dispatchListSaving}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Voir les dossiers">
                      <IconButton size="small" onClick={() => setDispatchDetailTarget(list)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Imprimer le BL">
                      <IconButton size="small" color="primary" onClick={() => handlePrintDispatchList(list)}>
                        <PrintRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontWeight: 900 }}>{list.listNumber}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={list.directionLabel || ((list.direction || 'store_to_workshop') === 'workshop_to_store' ? 'Atelier → Magasin' : 'Magasin → Atelier')}
                    color={(list.direction || 'store_to_workshop') === 'workshop_to_store' ? 'secondary' : 'primary'}
                    sx={{ fontWeight: 800 }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={list.statusLabel}
                    color={
                      list.status === 'received'
                        ? 'success'
                        : list.status === 'partial'
                          ? 'warning'
                          : list.status === 'draft'
                            ? 'info'
                            : 'primary'
                    }
                    sx={{ fontWeight: 850 }}
                  />
                </TableCell>
                <TableCell>{list.storeLabel || '—'}</TableCell>
                <TableCell>{list.workshopLabel || '—'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 850 }}>{list.itemCount}</TableCell>
                <TableCell>{formatDateTime(list.createdAt)}</TableCell>
                <TableCell>{formatDateTime(list.sentAt)}</TableCell>
                <TableCell>{formatDateTime(list.receivedAt)}</TableCell>
              </TableRow>
            ))}
            {!lists.length && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  Aucun BL pour le moment.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={dispatchTable.total}
        page={dispatchTable.total ? Math.min(dispatchTablePage, Math.max(Math.ceil(dispatchTable.total / dispatchTableRowsPerPage) - 1, 0)) : 0}
        onPageChange={(_event, nextPage) => setDispatchTablePage(nextPage)}
        rowsPerPage={dispatchTableRowsPerPage}
        onRowsPerPageChange={(event) => setDispatchTableRowsPerPage(Number(event.target.value))}
        rowsPerPageOptions={[25, 50, 100, 200]}
        labelRowsPerPage="BL par page"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
        showFirstButton
        showLastButton
      />
    </Stack>
  );

  const getCardDialogActionForRow = (row) => {
    if (canPrepareDispatch(row)) {
      return { code: 'DISPATCH_LIST', label: 'Mettre en BL', icon: <LocalShippingRoundedIcon fontSize="small" /> };
    }
    if (!canActOnCommande(row)) return null;

    const stepCode = getCurrentStep(row)?.code;
    if (stepCode === 'ATELIER_RECEPTION_DOSSIER') {
      return { code: 'WORKSHOP_RECEIVE_FILE', label: 'Réceptionner dossier', icon: <FolderCopyRoundedIcon fontSize="small" /> };
    }
    if (['ATELIER_RECEPTION_VERRE', 'ATELIER_RECEPTION_DOSSIER_COMMANDE_VERRE'].includes(stepCode)) {
      return { code: 'WORKSHOP_RECEIVE_LENS', label: 'Réceptionner verre', icon: <LensBlurRoundedIcon fontSize="small" /> };
    }
    if (['ATELIER_RAPPROCHEMENT_MONTAGE', 'ATELIER_RAPPROCHEMENT', 'ATELIER_MONTAGE', 'ATELIER_MONTAGE_RAPPROCHEMENT'].includes(stepCode)) {
      return { code: 'WORKSHOP_MATCH', label: 'Rapprocher', icon: <JoinInnerRoundedIcon fontSize="small" /> };
    }
    if (WORKSHOP_SEND_STEP_CODES.includes(stepCode)) {
      return {
        code: 'WORKSHOP_SEND',
        label: 'Préparer le retour magasin',
        icon: <LocalShippingRoundedIcon fontSize="small" />,
        needsMountingType: !['RETAILLAGE', 'CLIP'].includes(row.process),
      };
    }
    return null;
  };

  const cardDialogActionableRows = cardDialog?.type === 'commandes'
    ? filteredCardDialogRows.filter((row) => getCardDialogActionForRow(row))
    : [];
  const cardDialogBulkAction = cardDialogActionableRows.length
    ? getCardDialogActionForRow(cardDialogActionableRows[0])
    : null;
  const cardDialogRowsForBulkAction = cardDialogBulkAction
    ? cardDialogActionableRows.filter((row) => getCardDialogActionForRow(row)?.code === cardDialogBulkAction.code)
    : [];
  const cardDialogSelectedActionRows = cardDialogRowsForBulkAction.filter((row) =>
    cardDialogSelection.includes(String(row._id))
  );
  const toggleCardDialogSelection = (id) => {
    const normalizedId = String(id);
    setCardDialogSelection((current) => current.includes(normalizedId)
      ? current.filter((value) => value !== normalizedId)
      : [...current, normalizedId]);
  };
  const toggleAllCardDialogActionRows = () => {
    const visibleIds = cardDialogRowsForBulkAction.map((row) => String(row._id));
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => cardDialogSelection.includes(id));
    setCardDialogSelection((current) => allSelected
      ? current.filter((id) => !visibleIds.includes(id))
      : [...new Set([...current, ...visibleIds])]);
  };
  const moveCardDialogSelectionToDispatch = () => {
    if (!cardDialogSelectedActionRows.length) return;
    setRowSelectionModel({
      type: 'include',
      ids: new Set(cardDialogSelectedActionRows.map((row) => row._id)),
    });
    setSnackbar({
      open: true,
      severity: 'info',
      message: `${cardDialogSelectedActionRows.length} JOB(s) sélectionné(s) pour le BL.`,
    });
    setCardDialog(null);
  };

  const handleCardDialogBulkAction = () => {
    if (!cardDialogBulkAction || !cardDialogSelectedActionRows.length) return;
    if (cardDialogBulkAction.code === 'DISPATCH_LIST') {
      moveCardDialogSelectionToDispatch();
      return;
    }
    if (cardDialogBulkAction.needsMountingType && !mountingTypeOptions.includes(cardDialogBulkMountingType)) {
      setSnackbar({ open: true, severity: 'error', message: 'Sélectionne le type de montage pour préparer le retour.' });
      return;
    }
    if (isAdmin && !selectedBulkOperator) {
      setSnackbar({ open: true, severity: 'error', message: 'Sélectionne un opérateur atelier.' });
      return;
    }

    const jobNumbers = cardDialogSelectedActionRows.map((row) => row.jobNumber || row.workshopSheetNumber).filter(Boolean);
    openConfirmDialog({
      title: cardDialogBulkAction.label,
      description: `Confirmer cette action pour ${jobNumbers.length} JOB(s) sélectionné(s) ?`,
      confirmLabel: cardDialogBulkAction.label,
      onConfirm: async () => {
        try {
          setSaving(true);
          setError('');
          const payload = await requestJson('/commandes/workflow/bulk-complete', {
            method: 'POST',
            body: JSON.stringify({
              actionCode: cardDialogBulkAction.code,
              jobNumbers,
              mountingType: cardDialogBulkAction.needsMountingType ? cardDialogBulkMountingType : undefined,
              completedBy: isAdmin ? selectedBulkOperator._id : undefined,
            }),
          });
          const failed = (payload.results || []).filter((result) => !result.success);
          await loadRows({ silent: true });
          setCardDialogSelection([]);
          setSnackbar({
            open: true,
            severity: failed.length ? 'warning' : 'success',
            message: failed.length
              ? `${jobNumbers.length - failed.length} JOB(s) validé(s), ${failed.length} rejeté(s).`
              : `${jobNumbers.length} JOB(s) validé(s).`,
          });
        } catch (err) {
          setError(err.message);
          setSnackbar({ open: true, severity: 'error', message: err.message });
        } finally {
          setSaving(false);
        }
      },
    });
  };

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>
            {menuPage.sectionTitle}
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="h4" sx={{ fontWeight: 900 }}>
                {menuPage.pageTitle}
              </Typography>
              {isWorkshopFollowupView && (
                <Tooltip title="Voir les workflows possibles">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setWorkflowDocumentationOpen(true)}
                    aria-label="Voir la documentation des workflows"
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end" sx={{ rowGap: 1 }}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  loadRows();
                  loadDispatchLists();
                  loadWorkshopKpis();
                }}
                sx={{ fontWeight: 800 }}
              >
                Rafraîchir
              </Button>
              {canCreateOrder && (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreate}
                  sx={{ fontWeight: 900 }}
                >
                  {viewConfig.addLabel}
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      </Stack>

      <WorkflowDocumentationDialog
        open={workflowDocumentationOpen}
        onClose={() => setWorkflowDocumentationOpen(false)}
      />

      {canBulkWorkshopScan && (
        <Box
          sx={{
            order: !dispatchOnly && (siteType === 'workshop' || isAdmin) ? 2 : 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          {[
            {
              key: 'awaitingDispatch',
              label: 'Dossiers en attente de réception',
              value: workshopKpis.awaitingDispatch,
              helper: 'Job créé, pas encore mis dans un BL',
              gradient: 'linear-gradient(135deg, rgba(14,165,233,0.22), rgba(37,99,235,0.08))',
              color: 'primary.main',
              icon: <MoveToInboxRoundedIcon />,
            },
            {
              key: 'awaitingLens',
              label: 'Dossiers en attente de verre',
              value: workshopKpis.awaitingLens,
              helper: 'Dossier réceptionné, verre attendu',
              gradient: 'linear-gradient(135deg, rgba(168,85,247,0.20), rgba(14,165,233,0.08))',
              color: 'secondary.main',
              icon: <LensBlurRoundedIcon />,
            },
            {
              key: 'awaitingMatching',
              label: 'Dossiers en attente de rapprochement',
              value: workshopKpis.awaitingMatching,
              helper: 'Verre reçu, rapprochement à réaliser',
              gradient: 'linear-gradient(135deg, rgba(34,197,94,0.22), rgba(20,184,166,0.08))',
              color: 'success.main',
              icon: <JoinInnerRoundedIcon />,
            },
            {
              key: 'awaitingStoreDispatch',
              label: 'Dossier à faire',
              value: workshopKpis.awaitingStoreDispatch,
              helper: 'Monture et verres en production',
              gradient: 'linear-gradient(135deg, rgba(59,130,246,0.22), rgba(14,165,233,0.08))',
              color: 'info.main',
              icon: <LocalShippingRoundedIcon />,
            },
            {
              key: 'lensReceptionLate',
              label: 'Alerte réception verre',
              value: workshopKpis.lensReceptionLate,
              helper: 'Dossier sans mouvement depuis plus de 20 jours',
              gradient: 'linear-gradient(135deg, rgba(245,158,11,0.24), rgba(239,68,68,0.09))',
              color: 'warning.main',
              icon: <WarningAmberRoundedIcon />,
            },
            {
              key: 'matchingLate',
              label: 'Alerte rapprochement',
              value: workshopKpis.matchingLate,
              helper: 'Réception verre depuis plus de 3 jours',
              gradient: 'linear-gradient(135deg, rgba(239,68,68,0.22), rgba(244,63,94,0.08))',
              color: 'error.main',
              icon: <WarningAmberRoundedIcon />,
            },
            {
              key: 'lensWithoutDossier',
              label: 'Alerte verre sans dossier',
              value: workshopKpis.lensWithoutDossier,
              helper: 'Verre reçu sans dossier depuis plus de 6 jours',
              gradient: 'linear-gradient(135deg, rgba(249,115,22,0.22), rgba(239,68,68,0.08))',
              color: 'warning.main',
              icon: <WarningAmberIcon />,
            },
          ].map((kpi, index) => {
            const active = workshopKpiFilter === kpi.key;
            return [
              index === 0 && (
                <Typography key="dossiers-heading" variant="subtitle1" sx={{ gridColumn: '1 / -1', fontWeight: 950 }}>
                  Dossiers
                </Typography>
              ),
              index === 4 && (
                <Typography key="alertes-heading" variant="subtitle1" sx={{ gridColumn: '1 / -1', fontWeight: 950, mt: 1 }}>
                  Alertes
                </Typography>
              ),
              <Card
                key={kpi.key}
                elevation={0}
                onClick={() => {
                  if (useServerPagination) toggleCommandeCardFilter(kpi.key);
                  else openCommandeCardDialog(kpi.key, kpi.label, kpi.helper);
                }}
                sx={{
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: active ? kpi.color : 'divider',
                  background: kpi.gradient,
                  boxShadow: active ? `0 0 0 1px ${kpi.color}` : 'none',
                  transition: '160ms ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    borderColor: kpi.color,
                  },
                }}
              >
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: kpi.color,
                        flexShrink: 0,
                        '& .MuiSvgIcon-root': { fontSize: 30 },
                      }}
                    >
                      {kpi.icon}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                        {kpi.label}
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 950, lineHeight: 1 }}>
                        {serverWorkshopKpis?.[kpi.key] ?? '…'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
                        {active
                          ? 'Filtre actif sur le tableau'
                          : kpi.helper}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>,
            ];
          })}
        </Box>
      )}

      {isWorkshopFollowupView && siteType === 'store' && !dispatchOnly && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(280px, 1fr))' },
            gap: 2,
          }}
        >
          <Card
            elevation={0}
            onClick={() => openDispatchListCardDialog(
              'toReceive',
              'JOB à réceptionner',
              'Retours atelier regroupés par bon de livraison.',
            )}
            sx={{
              cursor: 'pointer',
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(135deg, rgba(34,197,94,0.22), rgba(14,165,233,0.08))',
              transition: '160ms ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                borderColor: 'success.main',
              },
            }}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                    JOB à réceptionner
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 950, lineHeight: 1 }}>
                    {storeFollowupKpis.toReceive}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
                    Retours atelier attendus au magasin
                  </Typography>
                </Box>
                <Avatar sx={{ width: 54, height: 54, bgcolor: 'success.main' }}>
                  <CheckCircleOutlineIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
          <Card
            elevation={0}
            onClick={() => toggleCommandeCardFilter('dispatchable')}
            sx={{
              cursor: 'pointer',
              border: '1px solid',
              borderColor: workshopKpiFilter === 'dispatchable' ? 'warning.main' : 'divider',
              background: 'linear-gradient(135deg, rgba(250,204,21,0.24), rgba(245,158,11,0.08))',
              boxShadow: workshopKpiFilter === 'dispatchable' ? '0 0 0 1px rgba(245,158,11,0.75)' : 'none',
              transition: '160ms ease',
              '&:hover': { transform: 'translateY(-1px)', borderColor: 'warning.main' },
            }}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                    Jobs en attente de BL
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 950, lineHeight: 1 }}>
                    {serverWorkshopKpis?.dispatchable ?? dispatchableOrderRows.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
                    {workshopKpiFilter === 'dispatchable' ? 'Filtre actif sur le tableau' : 'Dossiers prêts à mettre en BL atelier'}
                  </Typography>
                </Box>
                <Avatar sx={{ width: 54, height: 54, bgcolor: 'warning.main', color: '#111827' }}>
                  <FactCheckRoundedIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
          <Card
            elevation={0}
            onClick={() => toggleCommandeCardFilter('storePendingWorkshopIssue')}
            sx={{
              cursor: 'pointer',
              border: '1px solid',
              borderColor: workshopKpiFilter === 'storePendingWorkshopIssue' ? 'error.main' : 'divider',
              background: 'linear-gradient(135deg, rgba(239,68,68,0.24), rgba(244,63,94,0.08))',
              boxShadow: workshopKpiFilter === 'storePendingWorkshopIssue' ? '0 0 0 1px rgba(239,68,68,0.75)' : 'none',
              transition: '160ms ease',
              '&:hover': { transform: 'translateY(-1px)', borderColor: 'error.main' },
            }}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                    NCA / Traçabilité à recevoir
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 950, lineHeight: 1 }}>
                    {serverWorkshopKpis?.storePendingWorkshopIssue ?? storeFollowupKpis.pendingWorkshopIssue}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
                    Dossiers non conformes encore non réceptionnés
                  </Typography>
                </Box>
                <Avatar sx={{ width: 54, height: 54, bgcolor: 'error.main' }}>
                  <WarningAmberRoundedIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}

      {!dispatchOnly && isWorkshopFollowupView && (siteType === 'workshop' || isAdmin) && (
        <Stack spacing={1.25} sx={{ order: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
            Bons de livraison
          </Typography>
          <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          <Card
            elevation={0}
            onClick={() => openCommandeCardDialog('dispatchable', 'Jobs en attente de BL', 'Dossiers prêts à intégrer à un bon de livraison')}
            sx={{
              cursor: 'pointer',
              border: '1px solid',
              borderColor: workshopKpiFilter === 'dispatchable' ? 'warning.main' : 'divider',
              background: 'linear-gradient(135deg, rgba(250,204,21,0.24), rgba(245,158,11,0.08))',
              boxShadow: workshopKpiFilter === 'dispatchable' ? '0 0 0 1px rgba(245,158,11,0.75)' : 'none',
              transition: '160ms ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                borderColor: 'warning.main',
              },
            }}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                    Jobs en attente de BL
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 950, lineHeight: 1 }}>
                    {serverWorkshopKpis?.dispatchable ?? '…'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
                    {workshopKpiFilter === 'dispatchable'
                      ? 'Filtre actif sur le tableau'
                      : 'Dossiers prêts à mettre en BL retour'}
                  </Typography>
                </Box>
                <Avatar sx={{ width: 54, height: 54, bgcolor: 'warning.main', color: '#111827' }}>
                  <FactCheckRoundedIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
          </Box>
        </Stack>
      )}

      {dispatchOnly && isDispatchWorkflowView && (siteType === 'store' || siteType === 'workshop' || isAdmin) && (
        <Stack spacing={1.25}>
          <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
            Bons de livraison
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            {[
              {
                key: 'toSend',
                label: 'BL à envoyer',
                value: dispatchListKpis.toSend,
                helper: getDispatchListKpiHelper('toSend'),
                gradient: 'linear-gradient(135deg, rgba(124,58,237,0.22), rgba(37,99,235,0.08))',
                color: 'secondary.main',
                icon: <LocalShippingRoundedIcon />,
              },
              {
                key: 'toReceive',
                label: 'BL à réceptionner',
                value: dispatchListKpis.toReceive,
                helper: getDispatchListKpiHelper('toReceive'),
                gradient: 'linear-gradient(135deg, rgba(20,184,166,0.22), rgba(34,197,94,0.08))',
                color: 'success.main',
                icon: <MoveToInboxRoundedIcon />,
              },
            ].map((kpi) => {
              const active = dispatchListKpiFilter === kpi.key;
              return (
                <Card
                  key={kpi.key}
                  elevation={0}
                  onClick={() => toggleDispatchListCardFilter(kpi.key)}
                  sx={{
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: active ? kpi.color : 'divider',
                    background: kpi.gradient,
                    boxShadow: active ? `0 0 0 1px ${kpi.color}` : 'none',
                    transition: '160ms ease',
                    '&:hover': { transform: 'translateY(-1px)', borderColor: kpi.color },
                  }}
                >
                  <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                      <Box>
                        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                          {kpi.label}
                        </Typography>
                        <Typography variant="h3" sx={{ fontWeight: 950, lineHeight: 1 }}>
                          {kpi.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
                          {active ? 'Filtre actif sur les bons de livraison' : kpi.helper}
                        </Typography>
                      </Box>
                      <Avatar sx={{ width: 54, height: 54, bgcolor: kpi.color }}>
                        {kpi.icon}
                      </Avatar>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Stack>
      )}

      {isDispatchWorkflowView && siteType === 'store' && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                {dispatchOnly ? 'Bons de livraison magasin' : 'Bippage rapide magasin'}
              </Typography>
              {!dispatchOnly && (
                <Stack spacing={0.75}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                    Bippage JOB
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
                    {bulkStoreActions.map((action) => {
                  const style = bulkActionStyles[action.code] || bulkActionStyles.STORE_SEND;
                  return (
                    <Button
                      key={action.code}
                      variant="contained"
                      startIcon={action.icon}
                      onClick={() => handleOpenStoreBulkAction(action.code)}
                      sx={{
                        ...style,
                        fontWeight: 900,
                        textTransform: 'none',
                        minHeight: 38,
                        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.16), inset 0 1px 0 rgba(255,255,255,0.22)',
                        '& .MuiButton-startIcon': { color: 'inherit' },
                        '&:hover': {
                          ...style,
                          filter: 'brightness(1.08)',
                          boxShadow: '0 12px 28px rgba(15, 23, 42, 0.22), inset 0 1px 0 rgba(255,255,255,0.28)',
                        },
                      }}
                    >
                      {action.code === 'STORE_CREATE' ? 'Nouveau JOB' : action.label}
                    </Button>
                  );
                    })}
                  </Stack>
                </Stack>
              )}
              <Stack spacing={0.75}>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                  Bippage Bons de livraison
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
                {isWorkshopFollowupView && (
                  <>
                    <Button
                      variant="contained"
                      startIcon={<FactCheckRoundedIcon />}
                      onClick={() => openDispatchCreateDialog('store_to_workshop')}
                      sx={{ color: '#111827', background: 'linear-gradient(135deg, #fde047, #f59e0b)', fontWeight: 900, textTransform: 'none', minHeight: 38, '&:hover': { background: 'linear-gradient(135deg, #facc15, #d97706)' } }}
                    >
                      Créer BL
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<LocalShippingRoundedIcon />}
                      onClick={() => { setDispatchSendNumberInput(''); setDispatchSenderUserId(''); setDispatchSendOpen(true); }}
                      sx={{ color: '#ffffff', background: 'linear-gradient(135deg, #7c3aed, #2563eb)', fontWeight: 900, textTransform: 'none', minHeight: 38, '&:hover': { background: 'linear-gradient(135deg, #6d28d9, #1d4ed8)' } }}
                    >
                      Envoyer BL
                    </Button>
                  </>
                )}
                <Button
                  variant="contained"
                  startIcon={<MoveToInboxRoundedIcon />}
                  onClick={() => {
                    setDispatchListNumberInput('');
                    setDispatchReceiveOpen(true);
                  }}
                  sx={{
                    color: '#ffffff',
                    background: 'linear-gradient(135deg, #0d9488, #16a34a)',
                    fontWeight: 900,
                    textTransform: 'none',
                    minHeight: 38,
                    '&:hover': { background: 'linear-gradient(135deg, #0f766e, #15803d)' },
                  }}
                >
                  Réceptionner BL
                </Button>
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={storeBulkDialogOpen}
        onClose={() => setStoreBulkDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ color: 'primary.main', display: 'flex', '& .MuiSvgIcon-root': { fontSize: 22 } }}>
              {selectedStoreBulkAction.icon}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {selectedStoreBulkAction.label}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {['STORE_CREATE', 'STORE_SEND'].includes(storeBulkTab) ? (
              <StoreSendInlineForm
                frameType={storeSendFrameType}
                setFrameType={setStoreSendFrameType}
                mountingLocation={storeSendMountingLocation}
                setMountingLocation={setStoreSendMountingLocation}
                teleEdging={storeSendTeleEdging}
                setTeleEdging={setStoreSendTeleEdging}
                frameOrigin={storeSendFrameOrigin}
                setFrameOrigin={setStoreSendFrameOrigin}
                frameOriginCode={storeSendFrameOriginCode}
                setFrameOriginCode={setStoreSendFrameOriginCode}
                customerLastName={storeSendCustomerLastName}
                setCustomerLastName={setStoreSendCustomerLastName}
                customerFirstName={storeSendCustomerFirstName}
                setCustomerFirstName={setStoreSendCustomerFirstName}
                isAdmin={isAdmin}
                selectedBulkOperator={selectedBulkOperator}
                bulkOperatorOptions={bulkOperatorOptions}
                setBulkOperatorId={setBulkOperatorId}
                bulkJobInput={bulkJobInput}
                setBulkJobInput={setBulkJobInput}
                bulkDisplayResults={bulkDisplayResults}
                onSubmit={handleBulkWorkflowSubmit}
                onEnsureAction={() => {
                  if (bulkActionCode !== storeBulkTab) setBulkActionCode(storeBulkTab);
                }}
                isAaStore={isAaBulkStore}
              />
            ) : (
              <BulkScanInlineForm
                bulkJobInput={bulkJobInput}
                setBulkJobInput={setBulkJobInput}
                bulkDisplayResults={bulkDisplayResults}
                onSubmit={handleBulkWorkflowSubmit}
                onEnsureAction={() => {
                  if (bulkActionCode !== storeBulkTab) setBulkActionCode(storeBulkTab);
                }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStoreBulkDialogOpen(false)} sx={{ fontWeight: 800 }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {canWorkshopDispatchTools && (
        <Card elevation={0} sx={{ order: !dispatchOnly ? 1 : 0, border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                {dispatchOnly ? 'Bons de livraison atelier' : 'Bippage rapide atelier'}
              </Typography>
              <Stack spacing={1.5}>
                {!dispatchOnly && (
                  <Box>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase' }}>
                      Dossiers
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
                      {bulkWorkshopActions.map((action) => {
                        const style = bulkActionStyles[action.code] || bulkActionStyles.WORKSHOP_SEND;
                        return (
                          <Button
                            key={action.code}
                            variant="contained"
                            startIcon={action.icon}
                            onClick={() => handleOpenWorkshopBulkAction(action.code)}
                            sx={{
                              ...style,
                              fontWeight: 900,
                              textTransform: 'none',
                              minHeight: 38,
                              boxShadow: '0 10px 24px rgba(15, 23, 42, 0.16), inset 0 1px 0 rgba(255,255,255,0.22)',
                              '& .MuiButton-startIcon': { color: 'inherit' },
                              '&:hover': {
                                ...style,
                                filter: 'brightness(1.08)',
                                boxShadow: '0 12px 28px rgba(15, 23, 42, 0.22), inset 0 1px 0 rgba(255,255,255,0.28)',
                              },
                            }}
                          >
                            {action.label}
                          </Button>
                        );
                      })}
                    </Stack>
                  </Box>
                )}

                <Box>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase' }}>
                    Bons de livraison
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<FactCheckRoundedIcon />}
                      onClick={() => openDispatchCreateDialog('workshop_to_store')}
                      sx={{ color: '#111827', background: 'linear-gradient(135deg, #fde047, #f59e0b)', fontWeight: 900, textTransform: 'none', minHeight: 38, '&:hover': { background: 'linear-gradient(135deg, #facc15, #d97706)' } }}
                    >
                      {viewMode === 'nonStock' ? 'Créer BL retour' : 'Créer BL verre'}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<FactCheckRoundedIcon />}
                      onClick={handleAutoCreateReturnBls}
                      disabled={autoReturnBlSaving}
                      sx={{ color: '#111827', background: 'linear-gradient(135deg, #fef08a, #f59e0b)', fontWeight: 900, textTransform: 'none', minHeight: 38, '&:hover': { background: 'linear-gradient(135deg, #fde047, #d97706)' } }}
                    >
                      Préparer les BL retours
                    </Button>
                    <Button variant="outlined" startIcon={<PrintRoundedIcon />} onClick={() => openAutoReturnBlDialog()} disabled={!returnDraftDispatchLists.length} sx={{ fontWeight: 900, textTransform: 'none', minHeight: 38 }}>
                      Gérer les BL prêts
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<LocalShippingRoundedIcon />}
                      onClick={() => { setDispatchSendNumberInput(''); setDispatchSendOpen(true); }}
                      sx={{ color: '#ffffff', background: 'linear-gradient(135deg, #7c3aed, #2563eb)', fontWeight: 900, textTransform: 'none', minHeight: 38, '&:hover': { background: 'linear-gradient(135deg, #6d28d9, #1d4ed8)' } }}
                    >
                      Envoyer BL
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<MoveToInboxRoundedIcon />}
                      onClick={() => { setDispatchListNumberInput(''); setDispatchReceiveOpen(true); }}
                      sx={{ color: '#ffffff', background: 'linear-gradient(135deg, #0d9488, #16a34a)', fontWeight: 900, textTransform: 'none', minHeight: 38, '&:hover': { background: 'linear-gradient(135deg, #0f766e, #15803d)' } }}
                    >
                      Réceptionner BL
                    </Button>
                  </Stack>
                </Box>

                {!dispatchOnly && canPickStockLenses && (
                  <Box>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase' }}>
                      Verres de stock
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<QrCodeScannerIcon />}
                      onClick={handleOpenPicking}
                      sx={{ color: '#ffffff', background: 'linear-gradient(135deg, #0891b2, #0d9488)', fontWeight: 900, textTransform: 'none', minHeight: 38, boxShadow: '0 10px 24px rgba(15, 23, 42, 0.16), inset 0 1px 0 rgba(255,255,255,0.22)', '& .MuiButton-startIcon': { color: 'inherit' }, '&:hover': { background: 'linear-gradient(135deg, #0891b2, #0d9488)', filter: 'brightness(1.08)', boxShadow: '0 12px 28px rgba(15, 23, 42, 0.22), inset 0 1px 0 rgba(255,255,255,0.28)' } }}
                    >
                      Picking de verre
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<LocalShippingRoundedIcon />}
                      onClick={() => handleOpenWorkshopBulkAction(stockLensBulkAction.code)}
                      sx={{ color: '#ffffff', background: 'linear-gradient(135deg, #2563eb, #06b6d4)', fontWeight: 900, textTransform: 'none', minHeight: 38, boxShadow: '0 10px 24px rgba(15, 23, 42, 0.16), inset 0 1px 0 rgba(255,255,255,0.22)', '& .MuiButton-startIcon': { color: 'inherit' }, '&:hover': { background: 'linear-gradient(135deg, #2563eb, #06b6d4)', filter: 'brightness(1.08)', boxShadow: '0 12px 28px rgba(15, 23, 42, 0.22), inset 0 1px 0 rgba(255,255,255,0.28)' } }}
                    >
                      Envoi verre
                    </Button>
                  </Box>
                )}

                {!dispatchOnly && (
                  <Box>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase' }}>
                      Alertes
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<WarningAmberIcon />}
                      onClick={() => handleOpenNonConformity()}
                      sx={{ fontWeight: 900, textTransform: 'none', color: '#1f1300', background: 'linear-gradient(135deg, #fbbf24, #f97316)', boxShadow: '0 10px 24px rgba(249, 115, 22, 0.22), inset 0 1px 0 rgba(255,255,255,0.35)', '&:hover': { background: 'linear-gradient(135deg, #f59e0b, #ea580c)', boxShadow: '0 12px 28px rgba(249, 115, 22, 0.28), inset 0 1px 0 rgba(255,255,255,0.4)' } }}
                    >
                      Non Conformité Atelier
                    </Button>
                  </Box>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {dispatchOnly && isDispatchWorkflowView && (siteType === 'store' || siteType === 'workshop' || isAdmin) && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Stack spacing={1.25}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FilterAltRoundedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    Filtres
                  </Typography>
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setDispatchListFilters({ global: '', listNumber: '', status: '', store: '', workshop: '' })}
                  disabled={!Object.values(dispatchListFilters).some(Boolean)}
                  sx={{ fontWeight: 800 }}
                >
                  Réinitialiser
                </Button>
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' },
                  gap: 1,
                }}
              >
                <TextField
                  size="small"
                  label="Recherche globale"
                  value={dispatchListFilters.global}
                  onChange={(event) => setDispatchListFilters((current) => ({
                    ...current,
                    global: event.target.value.replace(/[\r\n\t]/g, ''),
                  }))}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === 'Tab') {
                      event.preventDefault();
                      event.stopPropagation();
                    }
                  }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
                />
                <TextField
                  size="small"
                  label="BL"
                  value={dispatchListFilters.listNumber}
                  onChange={(event) => setDispatchListFilters((current) => ({
                    ...current,
                    listNumber: event.target.value.replace(/[\r\n\t]/g, '').toUpperCase(),
                  }))}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === 'Tab') {
                      event.preventDefault();
                      event.stopPropagation();
                    }
                  }}
                />
                <TextField
                  select
                  size="small"
                  label="Statut"
                  value={dispatchListFilters.status}
                  onChange={(event) => setDispatchListFilters((current) => ({ ...current, status: event.target.value }))}
                >
                  <MenuItem value="">Tous les statuts</MenuItem>
                  {dispatchListFilterOptions.statuses.map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Magasin"
                  value={dispatchListFilters.store}
                  onChange={(event) => setDispatchListFilters((current) => ({ ...current, store: event.target.value }))}
                >
                  <MenuItem value="">Tous les magasins</MenuItem>
                  {dispatchListFilterOptions.stores.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Atelier"
                  value={dispatchListFilters.workshop}
                  onChange={(event) => setDispatchListFilters((current) => ({ ...current, workshop: event.target.value }))}
                >
                  <MenuItem value="">Tous les ateliers</MenuItem>
                  {dispatchListFilterOptions.workshops.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </TextField>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {dispatchOnly && isWorkshopFollowupView && (siteType === 'store' || siteType === 'workshop' || isAdmin) && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            {renderDispatchListTable('Bons de livraison', dispatchTableRows)}
          </CardContent>
        </Card>
      )}

      <Dialog
        open={Boolean(dispatchEditTarget)}
        onClose={() => !dispatchListSaving && setDispatchEditTarget(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <EditIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Modifier le BL {dispatchEditTarget?.listNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ajoute ou retire des dossiers avant l’envoi final.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Seuls les dossiers du même magasin et du même atelier, prêts à être envoyés, sont disponibles.
          </Alert>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="stretch">
            {[
              {
                title: 'Dossiers disponibles',
                rows: dispatchEditCandidates.filter((row) => !dispatchEditCommandeIds.includes(String(row._id))),
                color: 'text.secondary',
              },
              {
                title: 'Dossiers dans le BL',
                rows: dispatchEditCandidates.filter((row) => dispatchEditCommandeIds.includes(String(row._id))),
                color: 'primary.main',
              },
            ].map((column, index) => (
              <Box key={column.title} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: column.color, mb: 0.75 }}>
                  {column.title} ({column.rows.length})
                </Typography>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: 330, overflow: 'auto' }}>
                  <List dense disablePadding>
                    {column.rows.map((row) => {
                      const id = String(row._id);
                      const checked = dispatchEditCheckedIds.includes(id);
                      return (
                        <ListItem key={id} disablePadding divider>
                          <ListItemButton onClick={() => toggleDispatchEditChecked(id)} dense>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                            </ListItemIcon>
                            <ListItemText
                              primary={row.jobNumber || '—'}
                              secondary={row.customerSnapshot?.fullName || getCustomerLabel(row.customerId) || 'Sans client'}
                              primaryTypographyProps={{ fontWeight: 850 }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                    {!column.rows.length && (
                      <ListItem>
                        <ListItemText primary="Aucun dossier" primaryTypographyProps={{ color: 'text.secondary', align: 'center' }} />
                      </ListItem>
                    )}
                  </List>
                </Box>
                {index === 0 ? (
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{ mt: 1, fontWeight: 850 }}
                    disabled={!dispatchEditCheckedIds.some((id) => !dispatchEditCommandeIds.includes(id))}
                    onClick={() => moveCheckedDispatchItems(true)}
                  >
                    Ajouter au BL →
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="warning"
                    sx={{ mt: 1, fontWeight: 850 }}
                    disabled={!dispatchEditCheckedIds.some((id) => dispatchEditCommandeIds.includes(id)) || dispatchEditCommandeIds.length <= 1}
                    onClick={() => moveCheckedDispatchItems(false)}
                  >
                    ← Retirer du BL
                  </Button>
                )}
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', ml: 1 }}>
            {dispatchEditCommandeIds.length} dossier(s) sélectionné(s)
          </Typography>
          <Button onClick={() => setDispatchEditTarget(null)} disabled={dispatchListSaving} sx={{ fontWeight: 850 }}>
            Annuler
          </Button>
          <Button variant="contained" onClick={saveDispatchListItems} disabled={dispatchListSaving || !dispatchEditCommandeIds.length} sx={{ fontWeight: 900 }}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(dispatchDetailTarget)}
        onClose={() => setDispatchDetailTarget(null)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <FactCheckRoundedIcon color="primary" />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  BL {dispatchDetailTarget?.listNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {dispatchDetailTarget?.directionLabel} · {dispatchDetailTarget?.itemCount || 0} dossier(s)
                </Typography>
              </Box>
            </Stack>
            {dispatchDetailTarget && (
              <Button
                variant="outlined"
                startIcon={<PrintRoundedIcon />}
                onClick={() => handlePrintDispatchList(dispatchDetailTarget)}
                sx={{ fontWeight: 850 }}
              >
                Imprimer
              </Button>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
              <Chip label={dispatchDetailTarget?.statusLabel || '—'} color="primary" sx={{ fontWeight: 850 }} />
              <Chip label={`Magasin : ${dispatchDetailTarget?.storeLabel || '—'}`} variant="outlined" />
              <Chip label={`Atelier : ${dispatchDetailTarget?.workshopLabel || '—'}`} variant="outlined" />
              {dispatchDetailTarget?.sentBy && (
                <Chip
                  icon={<PersonRoundedIcon />}
                  label={`Signé par : ${getUserLabel(dispatchDetailTarget.sentBy)}`}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Stack>
            {dispatchDetailTarget?.listNumber && (
              <Box
                sx={{ width: 280, maxWidth: '100%', height: 72, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
                dangerouslySetInnerHTML={{ __html: getDispatchBarcodeSvg(dispatchDetailTarget.listNumber) }}
              />
            )}
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>JOB</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Conseiller</TableCell>
                    <TableCell>Statut ligne</TableCell>
                    <TableCell>État du dossier</TableCell>
                    <TableCell>Workflow</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(dispatchDetailTarget?.items || []).map((item, index) => (
                    <TableRow key={`${dispatchDetailTarget?._id}-${item.commandeId || item.jobNumber}-${index}`}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>{item.jobNumber || '—'}</TableCell>
                      <TableCell>{item.customerName || '—'}</TableCell>
                      <TableCell>{item.advisorName || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            item.status === 'received'
                              ? 'Réceptionné'
                              : item.status === 'skipped'
                                ? 'Rejeté'
                                : 'En attente'
                          }
                          color={
                            item.status === 'received'
                              ? 'success'
                              : item.status === 'skipped'
                                ? 'error'
                                : 'default'
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {dispatchDetailCommandes.loading
                          ? '…'
                          : getDispatchItemDossierState(dispatchDetailCommandes.byId.get(String(item.commandeId || '')), viewMode)
                            || (item.status === 'received' ? 'Réceptionné' : (item.workflowStepLabel || '—'))}
                      </TableCell>
                      <TableCell>
                        {dispatchDetailCommandes.loading ? '…' : (() => {
                          const choices = getCommandeWorkflowChoices(dispatchDetailCommandes.byId.get(String(item.commandeId || '')));
                          if (!choices.length) return '—';
                          return (
                            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                              {choices.map((choice) => (
                                <Chip key={choice.key} size="small" variant="outlined" label={choice.label} />
                              ))}
                            </Stack>
                          );
                        })()}
                      </TableCell>
                      <TableCell>{item.message || '—'}</TableCell>
                    </TableRow>
                  ))}
                  {!(dispatchDetailTarget?.items || []).length && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                        Aucun dossier dans ce BL.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDispatchDetailTarget(null)} sx={{ fontWeight: 800 }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={autoReturnBlPreviewOpen}
        onClose={() => !autoReturnBlSaving && setAutoReturnBlPreviewOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <FactCheckRoundedIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>Aperçu des BL retour à créer</Typography>
              <Typography variant="body2" color="text.secondary">Un BL sera créé pour chaque magasin ci-dessous.</Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            {autoReturnBlPreview?.commandeCount || 0} dossier(s) seront répartis dans {autoReturnBlPreview?.createdCount || 0} BL. Les dossiers déjà associés à un BL actif ne sont pas repris.
          </Alert>
          <Stack spacing={1.25}>
            {(autoReturnBlPreview?.lists || []).map((list) => (
              <Paper key={`${list.listNumber}-${getId(list.storeSiteId)}`} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 900 }}>{list.listNumber}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {list.storeSiteId?.code || getSiteLabel(list.storeSiteId) || 'Magasin'} · {list.items?.length || 0} dossier(s)
                    </Typography>
                  </Box>
                  <Chip size="small" color="primary" variant="outlined" label="Atelier → Magasin" />
                </Stack>
                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>JOB</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Conseiller</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(list.items || []).map((item, index) => (
                        <TableRow key={`${item.jobNumber}-${index}`}>
                          <TableCell sx={{ fontWeight: 800 }}>{item.jobNumber || '—'}</TableCell>
                          <TableCell>{item.customerName || '—'}</TableCell>
                          <TableCell>{item.advisorName || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))}
            {!autoReturnBlPreview?.lists?.length && (
              <Typography align="center" color="text.secondary" sx={{ py: 3 }}>Aucun dossier prêt à mettre dans un BL retour.</Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutoReturnBlPreviewOpen(false)} disabled={autoReturnBlSaving}>Annuler</Button>
          <Button
            variant="contained"
            startIcon={<FactCheckRoundedIcon />}
            disabled={!autoReturnBlPreview?.createdCount || autoReturnBlSaving}
            onClick={() => {
              setAutoReturnBlPreviewOpen(false);
              executeAutoCreateReturnBls();
            }}
          >
            Créer {autoReturnBlPreview?.createdCount || 0} BL
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={autoReturnBlOpen}
        onClose={() => !autoReturnBlSaving && setAutoReturnBlOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <FactCheckRoundedIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>BL retours prêts à envoyer</Typography>
              <Typography variant="body2" color="text.secondary">Un BL est créé par magasin. Sélectionne les lots à imprimer ou à expédier.</Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Autocomplete
              options={dispatchSenderOptions}
              value={selectedDispatchSender}
              onChange={(_event, value) => setDispatchSenderUserId(value?._id || '')}
              getOptionLabel={(user) => user ? getUserLabel(user) : ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Collaborateur atelier signataire"
                  helperText="Ce nom apparaîtra sur chaque BL et dans la zone de signature départ."
                  required
                />
              )}
              noOptionsText="Aucun utilisateur actif rattaché à cet atelier"
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(returnDraftDispatchLists.length) && autoReturnBlSelection.length === returnDraftDispatchLists.length}
                  indeterminate={autoReturnBlSelection.length > 0 && autoReturnBlSelection.length < returnDraftDispatchLists.length}
                  onChange={(event) => setAutoReturnBlSelection(event.target.checked ? returnDraftDispatchLists.map((list) => list._id) : [])}
                />
              }
              label={`Tout sélectionner (${returnDraftDispatchLists.length} BL)`}
            />
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxHeight: 420 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>BL</TableCell>
                    <TableCell>Magasin</TableCell>
                    <TableCell>Dossiers</TableCell>
                    <TableCell>Créé le</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {returnDraftDispatchLists.map((list) => {
                    const checked = autoReturnBlSelection.includes(list._id);
                    return (
                      <TableRow key={list._id} hover selected={checked}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={checked}
                            onChange={(event) => setAutoReturnBlSelection((current) => event.target.checked
                              ? [...new Set([...current, list._id])]
                              : current.filter((id) => id !== list._id))}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>{list.listNumber}</TableCell>
                        <TableCell>{list.storeLabel || '—'}</TableCell>
                        <TableCell>{list.itemCount || 0}</TableCell>
                        <TableCell>{formatDateTime(list.createdAt)}</TableCell>
                      </TableRow>
                    );
                  })}
                  {!returnDraftDispatchLists.length && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>Aucun BL retour prêt à envoyer.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', ml: 1 }}>{selectedAutoReturnBlLists.length} BL sélectionné(s)</Typography>
          <Button onClick={() => setAutoReturnBlOpen(false)} disabled={autoReturnBlSaving}>Fermer</Button>
          <Button startIcon={<PrintRoundedIcon />} onClick={handlePrintAutoReturnBls} disabled={!selectedAutoReturnBlLists.length || autoReturnBlSaving}>Imprimer la sélection</Button>
          <Button variant="contained" startIcon={<LocalShippingRoundedIcon />} onClick={handleSendAutoReturnBls} disabled={!selectedAutoReturnBlLists.length || autoReturnBlSaving}>
            {autoReturnBlSaving ? 'Envoi...' : 'Envoyer la sélection'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={workshopBulkDialogOpen}
        onClose={() => setWorkshopBulkDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ color: 'primary.main', display: 'flex', '& .MuiSvgIcon-root': { fontSize: 22 } }}>
              {selectedWorkshopBulkAction.icon}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {selectedWorkshopBulkAction.label}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {isAdmin && (
              <Autocomplete
                options={workshopUsers}
                value={selectedTraceabilityTechnician}
                onChange={(_event, value) => setTraceabilityTechnicianId(value?._id || '')}
                getOptionLabel={(user) =>
                  user ? `${getUserLabel(user)} — ${getSiteLabel(user.siteId)}` : ''
                }
                renderInput={(params) => <TextField {...params} label="Opérateur atelier" required />}
                fullWidth
              />
            )}
            {WORKSHOP_MOUNTING_TYPE_ACTION_CODES.includes(workshopBulkTab) && (
              <Autocomplete
                options={mountingTypeOptions}
                value={mountingTypeOptions.includes(bulkMountingType) ? bulkMountingType : null}
                onChange={(_event, value) => setBulkMountingType(value || '')}
                renderInput={(params) => <TextField {...params} label="Type de montage" required />}
                fullWidth
              />
            )}
            <BulkScanInlineForm
              bulkJobInput={bulkJobInput}
              setBulkJobInput={setBulkJobInput}
              bulkDisplayResults={bulkDisplayResults}
              onSubmit={handleBulkWorkflowSubmit}
              onEnsureAction={() => {
                if (bulkActionCode !== workshopBulkTab) setBulkActionCode(workshopBulkTab);
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWorkshopBulkDialogOpen(false)} sx={{ fontWeight: 800 }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dispatchCreateOpen} onClose={() => setDispatchCreateOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <FactCheckRoundedIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {dispatchCreateDirection === 'workshop_to_store' ? 'Créer un BL retour' : 'Créer un BL atelier'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              Scanne les JOB à intégrer au BL. Le BL sera créé en attente d’envoi, puis tu pourras l’envoyer par son numéro.
            </Alert>
            <Box component="form" onSubmit={handleDispatchScanSubmit}>
              <TextField
                autoFocus
                label="Scanner un JOB"
                placeholder="Scanne le code-barres du JOB"
                value={dispatchScanInput}
                onChange={(event) => setDispatchScanInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleDispatchScanSubmit(event);
                  }
                }}
                fullWidth
              />
            </Box>
            <Stack direction="row" spacing={1}>
              <Chip
                color="success"
                label={`${dispatchScanRows.filter((row) => row.success).length} ajouté(s)`}
              />
              <Chip
                color={dispatchScanRows.some((row) => !row.success) ? 'error' : 'default'}
                label={`${dispatchScanRows.filter((row) => !row.success).length} rejeté(s)`}
              />
            </Stack>
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>JOB</TableCell>
                    <TableCell>Résultat</TableCell>
                    <TableCell>Détail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dispatchScanRows.map((row, index) => (
                    <TableRow key={`${row._id || row.jobNumber}-${index}`}>
                      <TableCell sx={{ fontWeight: 800 }}>{row.jobNumber || row.jobDisplay}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={row.success ? 'success' : 'error'}
                          label={row.success ? 'Ajouté' : 'Rejeté'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{row.message}</TableCell>
                    </TableRow>
                  ))}
                  {!dispatchScanRows.length && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>
                        Aucun JOB scanné.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDispatchCreateOpen(false)} sx={{ fontWeight: 800 }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateDispatchListFromScan}
            disabled={dispatchListSaving || !dispatchScanRows.some((row) => row.success)}
            sx={{ fontWeight: 900 }}
          >
            Créer le BL
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dispatchSendOpen} onClose={() => setDispatchSendOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <LocalShippingRoundedIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Envoyer un BL
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleSendDispatchListByNumber} sx={{ pt: 1 }}>
            <TextField
              label="Numéro de BL"
              value={dispatchSendNumberInput}
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <Autocomplete
              options={dispatchSendSignerOptions}
              value={selectedDispatchSigner}
              onChange={(_event, value) => setDispatchSenderUserId(value?._id || '')}
              getOptionLabel={(user) => user ? getUserLabel(user) : ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={dispatchSendSignerLabel}
                  helperText="Le signataire sera enregistré sur le BL et affiché à l’impression."
                  required
                  sx={{ mt: 2 }}
                />
              )}
              noOptionsText={`Aucun utilisateur actif rattaché ${dispatchSendRequiresWorkshopSigner ? 'à cet atelier' : 'à ce magasin'}`}
              fullWidth
            />
            {dispatchSendRequiresPrint && (
              <Alert
                severity={dispatchSendIsPrinted ? 'success' : 'info'}
                sx={{ mt: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<PrintRoundedIcon />}
                    onClick={() => handlePrintDispatchList(dispatchSendTarget, { senderUser: selectedDispatchSigner })}
                    disabled={!dispatchSendTarget || !selectedDispatchSigner}
                    sx={{ fontWeight: 850 }}
                  >
                    Imprimer
                  </Button>
                }
              >
                {dispatchSendIsPrinted
                  ? 'BL imprimé. Il peut maintenant être envoyé à l’atelier.'
                  : 'Imprime le BL au moins une fois pour débloquer son envoi à l’atelier.'}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDispatchSendOpen(false)} sx={{ fontWeight: 800 }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSendDispatchListByNumber}
            disabled={
              dispatchListSaving ||
              !dispatchSendNumberInput.trim() ||
              !selectedDispatchSigner ||
              (dispatchSendRequiresPrint && !dispatchSendIsPrinted)
            }
            sx={{ fontWeight: 900 }}
          >
            Envoyer le BL
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(partialReceiveTarget)} onClose={() => setPartialReceiveTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Réception partielle du BL</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Sélectionne uniquement les dossiers réellement reçus physiquement. Les autres resteront en attente et le BL sera marqué « Partiel ».
          </Alert>
          <Stack spacing={0.5}>
            {(partialReceiveTarget?.items || []).map((item) => {
              const id = String(item.commandeId);
              return (
                <FormControlLabel
                  key={id}
                  control={<Checkbox checked={partialReceiveIds.includes(id)} onChange={() => setPartialReceiveIds((ids) => ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id])} />}
                  label={`${item.jobNumber}${item.customerName ? ` — ${item.customerName}` : ''}`}
                />
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPartialReceiveTarget(null)}>Annuler</Button>
          <Button
            variant="contained"
            disabled={dispatchListSaving || !partialReceiveIds.length}
            onClick={async () => {
              await receiveDispatchListByNumber(partialReceiveTarget.listNumber, { itemIds: partialReceiveIds });
              setPartialReceiveTarget(null);
            }}
          >
            Réceptionner la sélection
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dispatchReceiveOpen} onClose={() => setDispatchReceiveOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <MoveToInboxRoundedIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Réceptionner un BL
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleReceiveDispatchListByNumber} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              label="Scanner ou saisir un numéro de BL"
              placeholder={siteType === 'workshop' ? 'Ex: AA21-0001' : 'Ex: BL-AMOI-AA21-0001'}
              value={dispatchListNumberInput}
              onChange={(event) => setDispatchListNumberInput(event.target.value.toUpperCase())}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDispatchReceiveOpen(false)} sx={{ fontWeight: 800 }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleReceiveDispatchListByNumber}
            disabled={dispatchListSaving || !dispatchListNumberInput.trim()}
            sx={{ fontWeight: 900 }}
          >
            Réceptionner le BL
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(cardDialog)} onClose={() => setCardDialog(null)} fullWidth maxWidth="lg">
        <DialogTitle>{cardDialog?.title}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {cardDialog?.helper}
          </Typography>
          {cardDialog?.type !== 'dispatchLists' && (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2 }}>
              <TextField
                size="small"
                label="Recherche"
                value={cardDialogFilters.search}
                onChange={(event) => setCardDialogFilters((current) => ({ ...current, search: event.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
                fullWidth
              />
              <TextField
                select
                size="small"
                label="Magasin"
                value={cardDialogFilters.store}
                onChange={(event) => setCardDialogFilters((current) => ({ ...current, store: event.target.value }))}
                sx={{ minWidth: { md: 190 } }}
              >
                <MenuItem value="">Tous les magasins</MenuItem>
                {cardDialogFilterOptions.stores.map((store) => <MenuItem key={store} value={store}>{store}</MenuItem>)}
              </TextField>
              <TextField
                select
                size="small"
                label="Étape"
                value={cardDialogFilters.step}
                onChange={(event) => setCardDialogFilters((current) => ({ ...current, step: event.target.value }))}
                sx={{ minWidth: { md: 240 } }}
              >
                <MenuItem value="">Toutes les étapes</MenuItem>
                {cardDialogFilterOptions.steps.map((step) => <MenuItem key={step} value={step}>{step}</MenuItem>)}
              </TextField>
            </Stack>
          )}
          {cardDialog?.type === 'dispatchLists' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end" sx={{ mb: 2 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PrintRoundedIcon />}
                onClick={() => handlePrintDispatchLists(cardDialog.rows || [])}
                disabled={!cardDialog.rows?.length}
                sx={{ fontWeight: 850, textTransform: 'none' }}
              >
                Imprimer la liste complète
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownloadRoundedIcon />}
                onClick={() => handleExportDispatchListsCsv(cardDialog.rows || [])}
                disabled={!cardDialog.rows?.length}
                sx={{ fontWeight: 850, textTransform: 'none' }}
              >
                Export CSV
              </Button>
            </Stack>
          )}
          {cardDialog?.type === 'commandes' && cardDialogBulkAction && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 1.5 }}>
              <Chip size="small" label={`${cardDialogSelectedActionRows.length} sélectionné(s)`} color={cardDialogSelectedActionRows.length ? 'primary' : 'default'} sx={{ fontWeight: 850, alignSelf: { xs: 'flex-start', sm: 'auto' } }} />
              {cardDialogBulkAction.needsMountingType && (
                <Autocomplete
                  size="small"
                  options={mountingTypeOptions}
                  value={mountingTypeOptions.includes(cardDialogBulkMountingType) ? cardDialogBulkMountingType : null}
                  onChange={(_, value) => setCardDialogBulkMountingType(value || '')}
                  renderInput={(params) => <TextField {...params} label="Type de montage" />}
                  sx={{ minWidth: { sm: 230 } }}
                  disableClearable
                />
              )}
              <Button
                size="small"
                variant="contained"
                startIcon={cardDialogBulkAction.icon}
                disabled={!cardDialogSelectedActionRows.length}
                onClick={handleCardDialogBulkAction}
                sx={{ fontWeight: 900, textTransform: 'none' }}
              >
                {cardDialogBulkAction.label}
              </Button>
            </Stack>
          )}
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {cardDialog?.type === 'dispatchLists' ? (
                    <>
                      <TableCell>BL</TableCell>
                      <TableCell>Sens</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell>Magasin</TableCell>
                      <TableCell>Atelier</TableCell>
                      <TableCell align="right">Dossiers</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell padding="checkbox">
                        {cardDialogRowsForBulkAction.length > 0 && (
                          <Checkbox
                            size="small"
                            checked={cardDialogRowsForBulkAction.length > 0 && cardDialogRowsForBulkAction.every((row) => cardDialogSelection.includes(String(row._id)))}
                            indeterminate={cardDialogSelectedActionRows.length > 0 && cardDialogSelectedActionRows.length < cardDialogRowsForBulkAction.length}
                            onChange={toggleAllCardDialogActionRows}
                            inputProps={{ 'aria-label': 'Sélectionner tous les dossiers affichés' }}
                          />
                        )}
                      </TableCell>
                      <TableCell>JOB</TableCell>
                      <TableCell>Catégorie</TableCell>
                      <TableCell>Magasin</TableCell>
                      <TableCell>Étape actuelle</TableCell>
                      <TableCell>BL</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(cardDialog?.type === 'dispatchLists' ? cardDialog?.rows || [] : filteredCardDialogRows).map((row) =>
                  cardDialog?.type === 'dispatchLists' ? (
                    <Fragment key={row.id || row._id}>
                      <TableRow key={row.id || row._id} hover>
                        <TableCell sx={{ fontWeight: 800 }}>{row.listNumber}</TableCell>
                        <TableCell>{row.directionLabel}</TableCell>
                        <TableCell><Chip size="small" label={row.statusLabel} color={row.status === 'received' ? 'success' : 'primary'} /></TableCell>
                        <TableCell>{row.storeLabel || '—'}</TableCell>
                        <TableCell>{row.workshopLabel || '—'}</TableCell>
                        <TableCell align="right">{row.itemCount}</TableCell>
                        <TableCell align="right">
                          {cardDialog?.key === 'toSend' && row.status === 'draft' ? (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<LocalShippingRoundedIcon fontSize="small" />}
                              onClick={() => {
                                setCardDialog(null);
                                setDispatchSendNumberInput(row.listNumber || '');
                                setDispatchSenderUserId('');
                                setDispatchSendOpen(true);
                              }}
                              sx={{ fontWeight: 850, textTransform: 'none' }}
                            >
                              Envoyer
                            </Button>
                          ) : cardDialog?.key === 'toReceive' && ['sent', 'pending'].includes(row.status) ? (
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handlePrintDispatchList(row)}
                                aria-label={`Imprimer le BL ${row.listNumber}`}
                              >
                                <PrintRoundedIcon fontSize="small" />
                              </IconButton>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<MoveToInboxRoundedIcon fontSize="small" />}
                                onClick={() => {
                                  setCardDialog(null);
                                  setDispatchListNumberInput(row.listNumber || '');
                                  setDispatchReceiveOpen(true);
                                }}
                                sx={{ fontWeight: 850, textTransform: 'none' }}
                              >
                                Réceptionner
                              </Button>
                            </Stack>
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 38, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
                              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                Voir les dossiers du BL ({row.items?.length || 0})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>JOB</TableCell>
                                    <TableCell>Client</TableCell>
                                    <TableCell>Conseiller</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(row.items || []).map((item, index) => (
                                    <TableRow key={`${row.listNumber}-${item.jobNumber || index}`}>
                                      <TableCell>{index + 1}</TableCell>
                                      <TableCell sx={{ fontWeight: 800 }}>{item.jobNumber || '—'}</TableCell>
                                      <TableCell>{item.customerName || '—'}</TableCell>
                                      <TableCell>{item.advisorName || '—'}</TableCell>
                                    </TableRow>
                                  ))}
                                  {!row.items?.length && (
                                    <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>Aucun dossier dans ce BL.</TableCell></TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </AccordionDetails>
                          </Accordion>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  ) : (
                    <TableRow key={row._id} hover>
                      <TableCell padding="checkbox">
                        {cardDialogBulkAction && getCardDialogActionForRow(row)?.code === cardDialogBulkAction.code && (
                          <Checkbox
                            size="small"
                            checked={cardDialogSelection.includes(String(row._id))}
                            onChange={() => toggleCardDialogSelection(row._id)}
                            inputProps={{ 'aria-label': `Sélectionner le JOB ${row.jobNumber}` }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>{row.workshopSheetNumber || row.jobNumber}</TableCell>
                      <TableCell><Chip size="small" variant="outlined" label={getCommandeCategoryLabel(row)} /></TableCell>
                      <TableCell>{row.storeSiteId?.code || getSiteLabel(row.storeSiteId) || '—'}</TableCell>
                      <TableCell>{getDisplayStep(getCurrentStep(row), viewMode)?.label || 'Terminé'}</TableCell>
                      <TableCell>{getActiveDispatchListForCommande(row)?.listNumber || '—'}</TableCell>
                      <TableCell align="right">
                        {canActOnCommande(row) ? (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<QrCodeScannerIcon fontSize="small" />}
                            onClick={() => {
                              setCardDialog(null);
                              handleCompleteRowStep(row);
                            }}
                            disabled={saving}
                            sx={{ fontWeight: 850, textTransform: 'none' }}
                          >
                            {getRowWorkflowActionLabel(row)}
                          </Button>
                        ) : canPrepareDispatch(row) ? (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<LocalShippingRoundedIcon fontSize="small" />}
                            onClick={() => {
                              setRowSelectionModel({ type: 'include', ids: new Set([row._id]) });
                              setCardDialog(null);
                              setSnackbar({ open: true, severity: 'info', message: `JOB ${row.jobNumber} sélectionné pour le BL.` });
                            }}
                            sx={{ fontWeight: 850, textTransform: 'none' }}
                          >
                            Mettre en BL
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                )}
                {!(cardDialog?.type === 'dispatchLists' ? cardDialog?.rows?.length : filteredCardDialogRows.length) && (
                  <TableRow>
                      <TableCell colSpan={cardDialog?.type === 'dispatchLists' ? 7 : 7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Aucun élément pour le moment.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCardDialog(null)} sx={{ fontWeight: 800 }}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {!dispatchOnly && canBulkWorkshopScan && (
        <Card elevation={0} sx={{ order: !dispatchOnly && (siteType === 'workshop' || isAdmin) ? 4 : 0, border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Box component="form" onSubmit={handleReceiveDispatchListByNumber}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocalShippingRoundedIcon color="primary" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    Réception BL atelier
                  </Typography>
                </Stack>
                <TextField
                  label="Scanner ou saisir un numéro de BL"
                  placeholder="Ex: AA21-0001"
                  value={dispatchListNumberInput}
                  onChange={(event) => setDispatchListNumberInput(event.target.value.toUpperCase())}
                  disabled={dispatchListSaving}
                  fullWidth
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<MoveToInboxRoundedIcon />}
                    disabled={!dispatchListNumberInput.trim() || dispatchListSaving}
                    sx={{ fontWeight: 900 }}
                  >
                    {dispatchListSaving ? 'Réception...' : 'Réceptionner le lot'}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Le BL valide en une fois la réception dossier de tous les jobs du lot.
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      )}

      {!dispatchOnly && isWorkshopFollowupView && (
        <Card elevation={0} sx={{ order: !dispatchOnly && (siteType === 'workshop' || isAdmin) ? 5 : 0, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', borderRadius: 2 }}>
          <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
            <Stack spacing={1.25}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FilterAltRoundedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    Filtres
                  </Typography>
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setSearch('');
                    setWorkshopKpiFilter('');
                    setColumnFilters({ job: '', category: '', store: '', mountingType: '', teleEdging: '', mountingLocation: '', frameType: '', status: '', nca: [] });
                    setUpdatedAtRange({ from: '', to: '' });
                  }}
                >
                  Réinitialiser
                </Button>
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    lg: 'repeat(auto-fit, minmax(150px, 1fr))',
                  },
                  gap: 1.5,
                }}
              >
                <TextField
                  size="small"
                  label="Recherche globale"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                  />
                {[
                  ['job', 'JOB', workshopFilterOptions.jobs],
                  ['category', 'Catégorie', workshopFilterOptions.categories],
                  ['store', 'Magasin', workshopFilterOptions.stores],
                  ['mountingType', 'Type de montage', workshopFilterOptions.mountingTypes],
                  ['teleEdging', 'Taillage verres par AMOI', workshopFilterOptions.teleEdging],
                  ['mountingLocation', 'Lieu de montage', workshopFilterOptions.mountingLocations],
                  ['frameType', 'Type de monture', workshopFilterOptions.frameTypes],
                  ['status', 'État', workshopFilterOptions.statuses],
                ].map(([key, label, options]) => (
                  <Autocomplete
                    key={key}
                    freeSolo
                    size="small"
                    options={options}
                    value={columnFilters[key] || ''}
                    onInputChange={(_event, value) =>
                      setColumnFilters((current) => ({ ...current, [key]: value || '' }))
                    }
                    onChange={(_event, value) =>
                      setColumnFilters((current) => ({ ...current, [key]: value || '' }))
                    }
                    renderInput={(params) => <TextField {...params} label={label} />}
                    fullWidth
                  />
                ))}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ minWidth: 0 }}>
                  <TextField
                    type="date"
                    size="small"
                    label="Mise à jour du"
                    value={updatedAtRange.from}
                    onChange={(event) => setUpdatedAtRange((current) => ({ ...current, from: event.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    type="date"
                    size="small"
                    label="Mise à jour au"
                    value={updatedAtRange.to}
                    onChange={(event) => setUpdatedAtRange((current) => ({ ...current, to: event.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Stack>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  size="small"
                  options={workshopFilterOptions.ncas}
                  value={Array.isArray(columnFilters.nca) ? columnFilters.nca : []}
                  onChange={(_event, value) => setColumnFilters((current) => ({ ...current, nca: value }))}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox size="small" checked={selected} sx={{ mr: 0.75 }} />
                      {option}
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} label="Non-conformité / traçabilité" />}
                  fullWidth
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {!dispatchOnly && (
      <Card elevation={0} sx={{ order: !dispatchOnly && (siteType === 'workshop' || isAdmin) ? 6 : 0, border: '1px solid', borderColor: 'divider' }}>
        <CardContent
          sx={{
            p: 2,
            '&:last-child': { pb: 2 },
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ mb: isWorkshopFollowupView ? 1.5 : 2 }}
          >
            {!isWorkshopFollowupView && (
              <TextField
                size="small"
                label="Rechercher"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ maxWidth: { sm: 360 } }}
                fullWidth
              />
            )}

            {isWorkshopFollowupView && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ flex: 1 }}
              >
                <Chip
                  label={`${selectedCommandeRows.length} sélectionné(s)`}
                  color={selectedCommandeRows.length ? 'primary' : 'default'}
                  variant={selectedCommandeRows.length ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 900, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                />
                {(siteType === 'store' || siteType === 'workshop' || isAdmin) && (
                  <Tooltip
                    title={
                      selectedNonDispatchableCount
                        ? `${selectedNonDispatchableCount} dossier(s) sélectionné(s) ne sont pas éligibles au BL.`
                        : ''
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        startIcon={<LocalShippingRoundedIcon />}
                        onClick={handleCreateDispatchListFromSelection}
                        disabled={!canCreateDispatchListFromSelection || dispatchListSaving}
                        sx={{ fontWeight: 900 }}
                      >
                        {siteType === 'workshop' ? 'Créer BL retour' : 'Créer BL'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {!!selectedCommandeRows.length && (
                  <>
                    {selectedNonDispatchableCount > 0 && (
                      <Chip
                        size="small"
                        color="warning"
                        label={`${selectedNonDispatchableCount} non éligible(s) BL`}
                        sx={{ fontWeight: 850 }}
                      />
                    )}
                    <Button variant="text" onClick={clearCommandeSelection} sx={{ fontWeight: 800 }}>
                      Désélectionner
                    </Button>
                  </>
                )}
              </Stack>
            )}

            <Button
              variant="outlined"
              startIcon={<FileDownloadRoundedIcon />}
              onClick={handleExportWorkshopCsv}
              disabled={!filteredRows.length}
              sx={{ fontWeight: 800, alignSelf: { xs: 'stretch', sm: 'center' } }}
            >
              {useServerPagination ? 'Exporter la page' : 'Export CSV'}
            </Button>
          </Stack>

          {loading ? (
            <Stack alignItems="center" sx={{ py: 8 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <Box sx={{ height: 620, width: '100%' }}>
              <DataGrid
                rows={commandeGridRows}
                columns={
                  isWorkshopFollowupView
                    ? [
                        commandeGridColumns[commandeGridColumns.length - 2],
                        commandeGridColumns[commandeGridColumns.length - 1],
                        ...commandeGridColumns.slice(0, -2),
                      ]
                    : commandeGridColumns
                }
                checkboxSelection={isWorkshopFollowupView}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={(model) => setRowSelectionModel(model)}
                {...(useServerPagination
                  ? {
                      paginationMode: 'server',
                      paginationModel,
                      onPaginationModelChange: setPaginationModel,
                      rowCount: serverRowCount,
                      loading,
                    }
                  : {})}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                pageSizeOptions={[50, 100]}
                disableRowSelectionOnClick
                sx={{
                  border: 0,
                  bgcolor: 'transparent',
                  color: 'text.primary',
                  '--DataGrid-containerBackground': 'transparent',
                  '& .MuiDataGrid-main': {
                    bgcolor: 'transparent',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    bgcolor: 'rgba(100, 116, 139, 0.10)',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    bgcolor: 'transparent',
                  },
                  '& .MuiDataGrid-row': {
                    bgcolor: 'transparent',
                    borderColor: 'divider',
                  },
                  '& .MuiDataGrid-row:hover': {
                    bgcolor: 'rgba(102, 178, 255, 0.06)',
                  },
                  '& .MuiDataGrid-cell': {
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    bgcolor: 'transparent',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  },
                  '& .MuiDataGrid-filler': {
                    bgcolor: 'transparent',
                  },
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>
      )}

      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
      >
        {actionMenuRow && (
          <MenuItem disabled>
            JOB {actionMenuRow.jobNumber || actionMenuRow.workshopSheetNumber}
          </MenuItem>
        )}
        {actionMenuRow && canActOnCommande(actionMenuRow) && (
          <MenuItem
            disabled={saving}
            onClick={() => {
              const row = actionMenuRow;
              handleCloseActionMenu();
              handleCompleteRowStep(row);
            }}
          >
            <ListItemIcon>
              <CheckCircleOutlineIcon fontSize="small" color="primary" />
            </ListItemIcon>
            {getRowWorkflowActionLabel(actionMenuRow)}
          </MenuItem>
        )}
        {actionMenuRow && canDeclareNonConformity && (
          <MenuItem
            onClick={() => {
              const row = actionMenuRow;
              handleCloseActionMenu();
              handleOpenNonConformity(row, siteType === 'store' ? 'NCM' : 'NCA');
            }}
          >
            <ListItemIcon>
              <WarningAmberIcon fontSize="small" color="warning" />
            </ListItemIcon>
            Non-conformité {siteType === 'store' ? 'magasin' : 'atelier'}
          </MenuItem>
        )}
        {actionMenuRow && canDeclareTraceability(actionMenuRow) && (
          <MenuItem
            disabled={traceabilitySaving}
            onClick={() => {
              const row = actionMenuRow;
              handleCloseActionMenu();
              handleOpenTraceability(row);
            }}
          >
            <ListItemIcon>
              <FactCheckRoundedIcon fontSize="small" color="success" />
            </ListItemIcon>
            Traçabilité
          </MenuItem>
        )}
        {actionMenuRow && !canActOnCommande(actionMenuRow) && !isWorkshopFollowupView && (
          <MenuItem disabled>Aucune action métier disponible</MenuItem>
        )}
      </Menu>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle>{isStoreProcessOnlyEdit ? 'Modifier le process de la commande' : editingId ? 'Modifier une commande' : 'Ajouter une commande'}</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    lg: isStoreProcessOnlyEdit ? 'minmax(0, 1fr)' : 'minmax(420px, 0.95fr) minmax(500px, 1.05fr)',
                  },
                  gap: 3,
                  alignItems: 'start',
                }}
              >
                <Stack spacing={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Informations suivi
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: isStockLensProcess ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 2,
                }}
              >
                <TextField name="jobNumber" label="Numéro de job" value={form.jobNumber} onChange={handleChange} required disabled={lockFollowupIdentityFields || isStoreProcessOnlyEdit} fullWidth />
                {!isStockLensProcess && !isWorkshopFollowupView && (
                  <TextField name="workshopSheetNumber" label="Fiche atelier" value={form.workshopSheetNumber} onChange={handleChange} disabled={lockFollowupIdentityFields} fullWidth />
                )}
              </Box>
              {isWorkshopFollowupView ? (
                <>
                  <TextField
                    label="Magasin"
                    value={
                      selectedStoreSite
                        ? getSiteLabel(selectedStoreSite)
                        : !isAdmin && siteType === 'store'
                          ? getSiteLabel(site)
                          : '—'
                    }
                    disabled
                    fullWidth
                  />
                  <TextField
                    name="customerFullName"
                    label="Nom et prénom du client"
                    value={form.customerFullName}
                    onChange={handleChange}
                    required
                    disabled={lockFollowupIdentityFields}
                    helperText={lockFollowupIdentityFields ? 'Le client est verrouillé après l’envoi du JOB.' : 'Modifiable tant que le JOB n’a pas été envoyé.'}
                    fullWidth
                  />
                  {isStoreProcessOnlyEdit ? (
                    <ProcessSelectionCards
                      frameType={form.frameType}
                      mountingLocation={form.mountingLocation}
                      onFrameTypeChange={(value) => setFieldValue('frameType', value)}
                      onMountingLocationChange={(value) => setFieldValue('mountingLocation', value)}
                    />
                  ) : (
                    <>
                      <TextField
                        name="frameType"
                        label="Type de monture"
                        select
                        SelectProps={{ native: true }}
                        value={form.frameType}
                        onChange={handleChange}
                        required
                        disabled={lockFollowupIdentityFields}
                        fullWidth
                      >
                        <option value="new">Simple Boucle</option>
                        <option value="existing">Deuxième Boucle</option>
                      </TextField>
                      <TextField
                        name="mountingLocation"
                        label="Montage réalisé par"
                        select
                        SelectProps={{ native: true }}
                        value={form.mountingLocation}
                        onChange={handleChange}
                        required
                        disabled={lockFollowupIdentityFields}
                        fullWidth
                      >
                        <option value="workshop">Atelier / AMOI</option>
                        <option value="store">Magasin</option>
                      </TextField>
                      <Alert severity="info">
                        <strong>{getFrameTypeLabel(form.frameType)}</strong> + Montage réalisé par{' '}
                        <strong>{getMountingLocationLabel(form.mountingLocation)}</strong>
                      </Alert>
                    </>
                  )}
                  {editingId && lockFollowupIdentityFields && (
                    <Alert severity="info">
                      Les choix de monture et de montage sont verrouillés après création pour préserver les statistiques. Un administrateur peut les corriger avant l’envoi.
                    </Alert>
                  )}
                </>
              ) : (
                <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: isStockLensProcess ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 2,
                }}
              >
                <Autocomplete
                  options={customers}
                  value={selectedCustomer}
                  onChange={(_event, value) => {
                    setForm((current) => ({
                      ...current,
                      customerId: value?._id || '',
                      customerFullName: value ? getCustomerLabel(value) : '',
                    }));
                  }}
                  getOptionLabel={getCustomerLabel}
                  renderInput={(params) => <TextField {...params} label="Client" required />}
                  fullWidth
                />
                <Autocomplete
                  options={advisorOptions}
                  value={selectedAdvisor}
                  onChange={(_event, value) => {
                    setForm((current) => ({
                      ...current,
                      advisorId: value?._id || '',
                      advisorFullName: value ? getUserLabel(value) : '',
                    }));
                  }}
                  getOptionLabel={getUserLabel}
                  renderInput={(params) => <TextField {...params} label="Conseiller" required />}
                  fullWidth
                />
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: isStockLensProcess ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 2,
                }}
              >
                <Autocomplete
                  options={storeSites}
                  value={selectedStoreSite}
                  onChange={(_event, value) => setFieldValue('storeSiteId', value?._id || '')}
                  getOptionLabel={getSiteLabel}
                  renderInput={(params) => <TextField {...params} label="Magasin" required />}
                  disabled={lockFollowupIdentityFields || (!isAdmin && siteType === 'store')}
                  fullWidth
                />
                <Autocomplete
                  options={workshopSites}
                  value={selectedWorkshopSite}
                  onChange={(_event, value) => setFieldValue('workshopSiteId', value?._id || '')}
                  getOptionLabel={getSiteLabel}
                  renderInput={(params) => <TextField {...params} label="Atelier" required />}
                  disabled={lockFollowupIdentityFields}
                  fullWidth
                />
              </Box>

              <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 1 }}>
                Choix du process
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: isStockLensProcess ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 2,
                }}
              >
                <TextField
                  name="lensMode"
                  label="Type de verre"
                  select
                  SelectProps={{ native: true }}
                  value={form.lensMode}
                  disabled={viewMode !== 'all' || lockFollowupIdentityFields}
                  onChange={(event) => {
                    const value = event.target.value;
                    setForm((current) => ({
                      ...current,
                      lensMode: value,
                      stockItemId: value === 'stock' ? current.stockItemId : '',
                      mountingLocation: value === 'stock' ? current.mountingLocation : 'workshop',
                    }));
                  }}
                  required
                  fullWidth
                >
                  <option value="stock">Verre de stock</option>
                  <option value="supplier">Verre non stock</option>
                </TextField>
                <TextField
                  name="frameType"
                  label="Type de monture"
                  select
                  SelectProps={{ native: true }}
                  value={form.frameType}
                  onChange={handleChange}
                  required
                  disabled={lockFollowupIdentityFields}
                  fullWidth
                >
                  <option value="new">Simple Boucle</option>
                  <option value="existing">Deuxième Boucle</option>
                </TextField>
                {isStockLensProcess && (
                  <TextField
                    name="mountingLocation"
                    label="Montage réalisé par"
                    select
                    SelectProps={{ native: true }}
                    value={form.mountingLocation}
                    onChange={handleChange}
                    required
                    disabled={lockFollowupIdentityFields}
                    fullWidth
                  >
                    <option value="store">Magasin</option>
                    <option value="workshop">Atelier / AMOI</option>
                  </TextField>
                )}
                {!isStockLensProcess && (
                  <TextField
                    name="mountingLocation"
                    label="Montage réalisé par"
                    select
                    SelectProps={{ native: true }}
                    value={form.mountingLocation}
                    onChange={handleChange}
                    required
                    disabled={lockFollowupIdentityFields}
                    fullWidth
                  >
                    <option value="workshop">Atelier / AMOI</option>
                    <option value="store">Magasin</option>
                  </TextField>
                )}
              </Box>
              <Alert severity="info">
                <strong>{getFrameTypeLabel(form.frameType)}</strong> + Montage réalisé par{' '}
                <strong>{getMountingLocationLabel(form.mountingLocation)}</strong>
              </Alert>

              {!isStockLensProcess && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 1 }}>
                    Verre non stock
                  </Typography>
                  <Alert severity="info">
                    Aucun verre de stock à sélectionner : le process passera par la commande/réception du verre
                    hors stock côté atelier.
                  </Alert>
                </>
              )}
              <TextField name="frameDisplayLabel" label="Monture / note affichée" value={form.frameDisplayLabel} onChange={handleChange} />
                </>
              )}

              {isStockLensProcess && (
                <Card variant="outlined" sx={{ overflow: 'hidden' }}>
                  <CardContent>
                    <Stack spacing={2}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        Verres en stock
                      </Typography>
                      {selectedStockItem && (
                        <Alert
                          severity="success"
                          action={
                            !editingId ? (
                              <Button color="inherit" size="small" onClick={() => setFieldValue('stockItemId', '')}>
                                Retirer
                              </Button>
                            ) : null
                          }
                        >
                          Verre sélectionné : <strong>{getStockItemLabel(selectedStockItem)}</strong>
                          {' '}— sphère {formatDiopter(selectedStockItem.sphere)}, cylindre {formatDiopter(selectedStockItem.cylinder)}
                          {' '}— disponible : {selectedStockEffectiveAvailable}
                        </Alert>
                      )}
                      <TextField
                        name="lensQuantity"
                        label="Quantité souhaitée"
                        type="number"
                        value={form.lensQuantity}
                        onChange={handleChange}
                        disabled={Boolean(editingId)}
                        inputProps={{
                          min: 1,
                          max: selectedStockItem ? selectedStockEffectiveAvailable : undefined,
                        }}
                        error={Boolean(selectedStockItem) && Number(form.lensQuantity || 0) > selectedStockEffectiveAvailable}
                        helperText={
                          selectedStockItem
                            ? editingId
                              ? 'Verre et quantité verrouillés après création du suivi.'
                              : `Maximum disponible : ${selectedStockEffectiveAvailable}`
                            : 'Sélectionne un verre dans le tableau.'
                        }
                        fullWidth
                      />
                      {!editingId && (
                        <>
                          <TextField
                            size="small"
                            label="Rechercher un verre"
                            placeholder="Code barre, fournisseur, libellé, sphère, cylindre…"
                            value={lensSearch}
                            onChange={(event) => setLensSearch(event.target.value)}
                            fullWidth
                          />
                          <TableContainer
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              maxHeight: 390,
                            }}
                          >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Code barre</TableCell>
                              <TableCell>Fournisseur</TableCell>
                              <TableCell>Libellé</TableCell>
                              <TableCell align="right">Sphère</TableCell>
                              <TableCell align="right">Cylindre</TableCell>
                              <TableCell align="right">Dispo.</TableCell>
                              <TableCell align="right">Réservée</TableCell>
                              <TableCell align="right">Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredStockItems.map((item) => {
                              const isSelected = getId(item) === form.stockItemId;

                              return (
                                <TableRow key={getId(item)} hover selected={isSelected}>
                                  <TableCell sx={{ fontWeight: 700 }}>{item.barcode || '—'}</TableCell>
                                  <TableCell>{item.supplier || '—'}</TableCell>
                                  <TableCell>{item.label || '—'}</TableCell>
                                  <TableCell align="right">{formatDiopter(item.sphere)}</TableCell>
                                  <TableCell align="right">{formatDiopter(item.cylinder)}</TableCell>
                                  <TableCell align="right">{item.availableQty ?? 0}</TableCell>
                                  <TableCell align="right">{item.reservedQty ?? 0}</TableCell>
                                  <TableCell align="right">
                                    <Button
                                      size="small"
                                      variant={isSelected ? 'contained' : 'outlined'}
                                      onClick={() => handleSelectStockItem(item)}
                                    >
                                      {isSelected ? 'Sélectionné' : 'Ajouter'}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            {!filteredStockItems.length && (
                              <TableRow>
                                <TableCell colSpan={8}>
                                  <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                                    Aucun verre disponible.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                          </TableContainer>
                        </>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              )}
                </Stack>

                {!isStoreProcessOnlyEdit && (
                <Stack spacing={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 1 }}>
                Jalons du workflow
              </Typography>
              {isAdmin && (
                <Autocomplete
                  options={users}
                  value={selectedWorkflowOperator}
                  onChange={(_event, value) => setWorkflowOperatorId(value?._id || '')}
                  getOptionLabel={(user) => {
                    if (!user) return '';
                    const userSites = user.siteIds?.length ? user.siteIds : user.siteId ? [user.siteId] : [];
                    return `${getUserLabel(user)} — ${userSites.map(getSiteLabel).filter(Boolean).join(', ')}`;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Utilisateur qui bippe (mode admin)"
                      helperText="L’utilisateur sélectionné doit appartenir au site responsable de l’étape."
                    />
                  )}
                  fullWidth
                />
              )}
              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Étape</TableCell>
                      <TableCell>Responsable</TableCell>
                      <TableCell>Date bip</TableCell>
                      <TableCell>Bippé par</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workflowSteps.map((step) => {
                      const isCurrentStep = currentWorkflowStep?.code === step.code;
                      const expectedSiteId =
                        step.ownerType === 'store' ? form.storeSiteId : form.workshopSiteId;
                      const canOperatorBip = isAdmin
                        ? Boolean(selectedWorkflowOperator) &&
                          userHasSite(selectedWorkflowOperator, expectedSiteId)
                        : siteType === step.ownerType && site?._id === expectedSiteId;

                      return (
                        <TableRow key={step.code} hover selected={isCurrentStep}>
                          <TableCell sx={{ fontWeight: 700 }}>{getStepLabel(step)}</TableCell>
                          <TableCell>{getOwnerLabel(step.ownerType)}</TableCell>
                          <TableCell>{formatDateTime(step.completedAt)}</TableCell>
                          <TableCell>
                            {step.completedBy
                              ? getUserLabel(step.completedBy)
                              : getSiteLabel(step.completedSiteId) || '—'}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              type="button"
                              size="small"
                              variant={isCurrentStep ? 'contained' : 'outlined'}
                              disabled={!editingId || saving || !isCurrentStep || !canOperatorBip}
                              onClick={() => handleCompleteWorkflowStep(step.code)}
                            >
                              {step.completedAt ? 'Déjà bippé' : isCurrentStep ? 'Bipper' : 'En attente'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TextField name="printLabel" label="Impression" value={form.printLabel} onChange={handleChange} />
                </Stack>
                )}
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Enregistrement…' : isStoreProcessOnlyEdit ? 'Enregistrer le process' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={pickingOpen} onClose={() => setPickingOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={handlePickingSubmit}>
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <QrCodeScannerIcon color="primary" />
              <span>Picking de verre</span>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <Alert severity="info">
                Scanne le numéro de JOB de la commande, ou la fiche atelier si elle est différente,
                puis les codes-barres qui existent dans <strong>Verres de stock</strong>. La commande
                doit être à l’étape <strong>Picking verre</strong>.
              </Alert>
              {isAdmin ? (
                <Autocomplete
                  options={workshopUsers}
                  value={selectedPickingOperator}
                  onChange={(_event, value) => setPickingOperatorId(value?._id || '')}
                  getOptionLabel={(user) =>
                    user ? `${getUserLabel(user)} — ${(user.siteIds?.length ? user.siteIds : [user.siteId]).map(getSiteLabel).filter(Boolean).join(', ')}` : ''
                  }
                  renderInput={(params) => <TextField {...params} label="Opérateur atelier" required />}
                  fullWidth
                />
              ) : (
                <Alert severity="success" variant="outlined">
                  Picking affecté à l’atelier connecté : <strong>{site?.name}</strong>.
                </Alert>
              )}
              <TextField
                autoFocus
                label="Numéro de JOB / fiche atelier"
                value={pickingJobNumber}
                onChange={(event) => setPickingJobNumber(event.target.value)}
                required
                fullWidth
              />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: 2,
                }}
              >
                <TextField
                  label="Verre droit"
                  value={pickingRightBarcode}
                  onChange={(event) => setPickingRightBarcode(event.target.value)}
                  fullWidth
                />
                <TextField
                  label="Verre gauche"
                  value={pickingLeftBarcode}
                  onChange={(event) => setPickingLeftBarcode(event.target.value)}
                  fullWidth
                />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPickingOpen(false)}>Fermer</Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<QrCodeScannerIcon />}
              disabled={pickingSaving}
            >
              {pickingSaving ? 'Validation…' : 'Valider le prélèvement'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={traceabilityOpen}
        onClose={() => {
          if (!traceabilitySaving) {
            setTraceabilityOpen(false);
            setTraceabilityRow(null);
          }
        }}
        fullWidth
        maxWidth="md"
      >
        <Box component="form" onSubmit={handleTraceabilitySubmit}>
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FactCheckRoundedIcon color="success" />
              <span>Déclarer une Traçabilité</span>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              {traceabilityRow && (
                <Alert severity="info">
                  JOB {traceabilityRow.jobNumber} · {getSiteLabel(traceabilityRow.storeSiteId)} · Atelier {getSiteLabel(traceabilityRow.workshopSiteId)}
                </Alert>
              )}

              <Autocomplete
                options={workshopUsers}
                value={selectedTraceabilityTechnician}
                onChange={(_event, value) => setTraceabilityTechnicianId(value?._id || '')}
                getOptionLabel={(user) => (user ? getUserLabel(user) : '')}
                isOptionEqualToValue={(option, value) => option._id === value?._id}
                renderInput={(params) => <TextField {...params} label="Technicien" required />}
                fullWidth
              />

              <TextField
                select
                label="Montage réalisé"
                value={traceabilityForm.mountingCompleted}
                onChange={(event) => setTraceabilityForm((current) => ({ ...current, mountingCompleted: event.target.value }))}
                required
                fullWidth
              >
                <MenuItem value="yes">Oui</MenuItem>
                <MenuItem value="no">Non</MenuItem>
              </TextField>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
                {Object.entries(traceabilityCheckLabels).map(([key, label]) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={Boolean(traceabilityForm.checks[key])}
                        onChange={(event) => handleTraceabilityCheckChange(key, event.target.checked)}
                      />
                    }
                    label={label}
                  />
                ))}
              </Box>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1.5 }}>
                    Écarts pupillaires
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
                    <TextField label="Commande droit" value={traceabilityForm.pupillaryDifferences.orderRight} onChange={(event) => handleTraceabilityFieldChange('pupillaryDifferences', 'orderRight', event.target.value)} size="small" />
                    <TextField label="Commande gauche" value={traceabilityForm.pupillaryDifferences.orderLeft} onChange={(event) => handleTraceabilityFieldChange('pupillaryDifferences', 'orderLeft', event.target.value)} size="small" />
                    <TextField label="Réalisation droit" value={traceabilityForm.pupillaryDifferences.realizationRight} onChange={(event) => handleTraceabilityFieldChange('pupillaryDifferences', 'realizationRight', event.target.value)} size="small" />
                    <TextField label="Réalisation gauche" value={traceabilityForm.pupillaryDifferences.realizationLeft} onChange={(event) => handleTraceabilityFieldChange('pupillaryDifferences', 'realizationLeft', event.target.value)} size="small" />
                  </Box>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1.5 }}>
                    Hauteurs
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
                    <TextField label="Commande droit" value={traceabilityForm.heights.orderRight} onChange={(event) => handleTraceabilityFieldChange('heights', 'orderRight', event.target.value)} size="small" />
                    <TextField label="Commande gauche" value={traceabilityForm.heights.orderLeft} onChange={(event) => handleTraceabilityFieldChange('heights', 'orderLeft', event.target.value)} size="small" />
                    <TextField label="Réalisation droit" value={traceabilityForm.heights.realizationRight} onChange={(event) => handleTraceabilityFieldChange('heights', 'realizationRight', event.target.value)} size="small" />
                    <TextField label="Réalisation gauche" value={traceabilityForm.heights.realizationLeft} onChange={(event) => handleTraceabilityFieldChange('heights', 'realizationLeft', event.target.value)} size="small" />
                  </Box>
                </CardContent>
              </Card>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  label="Problème de forme"
                  value={traceabilityForm.shapeProblem}
                  onChange={(event) => setTraceabilityForm((current) => ({ ...current, shapeProblem: event.target.value }))}
                  multiline
                  minRows={2}
                />
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    Inadéquation verres / monture
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
                    <TextField label="Matériau" value={traceabilityForm.lensFrameMismatchDetails.material} onChange={(event) => handleTraceabilityFieldChange('lensFrameMismatchDetails', 'material', event.target.value)} size="small" />
                    <TextField label="Base" value={traceabilityForm.lensFrameMismatchDetails.base} onChange={(event) => handleTraceabilityFieldChange('lensFrameMismatchDetails', 'base', event.target.value)} size="small" />
                    <TextField label="Épaisseur" value={traceabilityForm.lensFrameMismatchDetails.thickness} onChange={(event) => handleTraceabilityFieldChange('lensFrameMismatchDetails', 'thickness', event.target.value)} size="small" />
                  </Box>
                </Stack>
              </Box>

              <TextField
                label="Autres / commentaire"
                value={traceabilityForm.otherComment}
                onChange={(event) => setTraceabilityForm((current) => ({ ...current, otherComment: event.target.value }))}
                multiline
                minRows={2}
              />
              <TextField
                label="Commentaire atelier"
                value={traceabilityForm.comment}
                onChange={(event) => setTraceabilityForm((current) => ({ ...current, comment: event.target.value }))}
                multiline
                minRows={2}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setTraceabilityOpen(false);
                setTraceabilityRow(null);
              }}
              disabled={traceabilitySaving}
            >
              Annuler
            </Button>
            <Button type="submit" variant="contained" color="success" disabled={traceabilitySaving}>
              {traceabilitySaving ? 'Déclaration…' : 'Déclarer'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={nonConformityOpen} onClose={() => setNonConformityOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={handleNonConformitySubmit}>
          <DialogTitle sx={{ pb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.dark', width: 40, height: 40 }}>
                <WarningAmberIcon />
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                  Non-conformité {nonConformityForm.type === 'NCM' ? 'magasin' : 'atelier'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {nonConformityRow ? 'Déclaration pour le JOB' : 'Déclaration liée à un numéro de JOB'}
                  {nonConformityRow && (
                    <Box component="span" sx={{ ml: 0.75, px: 1, py: 0.25, borderRadius: 999, bgcolor: 'action.hover', fontFamily: 'monospace', fontWeight: 800, color: 'text.primary' }}>
                      {nonConformityJobNumber}
                    </Box>
                  )}
                </Typography>
              </Box>
              <IconButton aria-label="Fermer" onClick={() => setNonConformityOpen(false)} size="small">
                <CloseRoundedIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2.25}>
              {!nonConformityRow && (
                <TextField
                  label="Numéro de JOB"
                  value={nonConformityJobNumber}
                  onChange={(event) => setNonConformityJobNumber(event.target.value)}
                  required
                  autoFocus
                  placeholder="Scanne ou saisis le JOB / fiche atelier"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"><QrCodeScannerRoundedIcon color="action" /></InputAdornment>
                    ),
                  }}
                  fullWidth
                />
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                <Autocomplete
                  options={nonConformityReasonOptions}
                  value={nonConformityReasonOptions.find((option) => option.value === nonConformityForm.reason) || null}
                  onChange={(_event, value) =>
                    setNonConformityForm((current) => ({ ...current, reason: value?.value || '', scope: '' }))
                  }
                  getOptionLabel={(option) => option.label || ''}
                  renderInput={(params) => <TextField {...params} label="Motif" required />}
                />
                <Autocomplete
                  options={availableNonConformityScopeOptions}
                  value={availableNonConformityScopeOptions.find((option) => option.value === nonConformityForm.scope) || null}
                  onChange={(_event, value) =>
                    setNonConformityForm((current) => ({ ...current, scope: value?.value || '' }))
                  }
                  getOptionLabel={(option) => option.label || ''}
                  disabled={!nonConformityForm.reason}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Zone concernée"
                      required
                      placeholder={!nonConformityForm.reason ? 'Choisis d’abord un motif' : undefined}
                    />
                  )}
                />
              </Box>
              <TextField
                label="Commentaire"
                value={nonConformityForm.comment}
                onChange={(event) =>
                  setNonConformityForm((current) => ({ ...current, comment: event.target.value }))
                }
                multiline
                minRows={3}
                required
                placeholder="Ex : verre droit rayé, défaut visible au contrôle, monture à recommander…"
              />
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>Photos</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Facultatif · {NC_PHOTO_MAX} maximum
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.25} alignItems="stretch" flexWrap="wrap" useFlexGap>
                  {nonConformityPhotos.map((photo, index) => (
                    <Box key={photo.url} sx={{ position: 'relative', width: 92, height: 92, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                      <Box component="img" src={photo.url} alt={`Photo ${index + 1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <IconButton
                        size="small"
                        aria-label={`Retirer la photo ${index + 1}`}
                        onClick={() => removeNonConformityPhoto(index)}
                        sx={{ position: 'absolute', top: 4, right: 4, p: 0.25, bgcolor: 'rgba(15, 23, 42, 0.65)', color: '#fff', '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.85)' } }}
                      >
                        <CloseRoundedIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ))}
                  {nonConformityPhotos.length < NC_PHOTO_MAX && (
                    <Box
                      component="label"
                      sx={{
                        width: 92,
                        height: 92,
                        borderRadius: 2,
                        border: '2px dashed',
                        borderColor: 'divider',
                        color: 'text.secondary',
                        display: 'grid',
                        placeItems: 'center',
                        alignContent: 'center',
                        gap: 0.5,
                        textAlign: 'center',
                        cursor: nonConformityPhotoBusy ? 'progress' : 'pointer',
                        transition: 'border-color 120ms, color 120ms',
                        '&:hover': { borderColor: 'warning.main', color: 'warning.dark' },
                        '&:focus-within': { borderColor: 'warning.main' },
                      }}
                    >
                      {nonConformityPhotoBusy ? <CircularProgress size={22} /> : <AddAPhotoRoundedIcon />}
                      <Typography variant="caption" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                        {nonConformityPhotos.length ? 'Ajouter' : 'Ajouter une photo'}
                      </Typography>
                      <input
                        hidden
                        type="file"
                        accept="image/*"
                        multiple
                        disabled={nonConformityPhotoBusy}
                        onChange={handleNonConformityPhotosSelected}
                      />
                    </Box>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button onClick={() => setNonConformityOpen(false)}>Annuler</Button>
            <Button
              type="submit"
              variant="contained"
              color="warning"
              disabled={
                nonConformitySaving ||
                nonConformityPhotoBusy ||
                !nonConformityJobNumber.trim() ||
                !nonConformityForm.scope ||
                !nonConformityForm.reason ||
                !String(nonConformityForm.comment || '').trim()
              }
              sx={{ fontWeight: 900 }}
            >
              {nonConformitySaving ? 'Déclaration…' : 'Déclarer la non-conformité'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={Boolean(cancelNonConformityTarget)}
        onClose={() => {
          if (!cancelNonConformitySaving) {
            setCancelNonConformityTarget(null);
            setCancelNonConformityReason('');
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <Box component="form" onSubmit={handleCancelNonConformity}>
          <DialogTitle>Annuler une NCA</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning">
                La NCA restera visible dans l'historique, mais elle ne sera plus comptée comme non-conformité active.
              </Alert>
              <TextField
                label="Raison d'annulation"
                value={cancelNonConformityReason}
                onChange={(event) => setCancelNonConformityReason(event.target.value)}
                multiline
                minRows={3}
                required
                autoFocus
                placeholder="Ex : contrôle magasin OK, déclaration atelier à annuler, erreur de JOB..."
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setCancelNonConformityTarget(null);
                setCancelNonConformityReason('');
              }}
              disabled={cancelNonConformitySaving}
            >
              Fermer
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="warning"
              disabled={cancelNonConformitySaving || !cancelNonConformityReason.trim()}
            >
              {cancelNonConformitySaving ? 'Annulation...' : 'Annuler la NCA'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(viewingRow)} onClose={() => setViewingRow(null)} fullScreen>
        <AppBar sx={{ position: 'relative', bgcolor: 'background.paper', backgroundImage: 'none', color: 'text.primary' }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={() => setViewingRow(null)} aria-label="fermer">
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1, fontWeight: 800 }} variant="h6" component="div">
              Suivi de la commande {viewingRow?.jobNumber}
            </Typography>
            {viewingRow && (isAdmin || canStoreEditInitialCommande(viewingRow)) && (
              <Stack direction="row" spacing={1}>
                {isAdmin && (
                  <Button
                    variant="outlined"
                    startIcon={<AccountTreeRoundedIcon />}
                    onClick={() => handleOpenWorkflowCorrection(viewingRow)}
                  >
                    Corriger le workflow
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    const row = viewingRow;
                    setViewingRow(null);
                    setDeleteTarget(row);
                  }}
                >
                  Supprimer
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    const row = viewingRow;
                    setViewingRow(null);
                    handleOpenEdit(row);
                  }}
                >
                  Modifier
                </Button>
              </Stack>
            )}
          </Toolbar>
        </AppBar>
        <DialogContent sx={{ px: { xs: 2, md: 5 }, py: 4 }}>
          {viewingRow && (
            <Stack spacing={3} sx={{ maxWidth: 1440, mx: 'auto' }}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={getSimpleCommandeStatus(viewingRow, viewMode)} color="primary" />
                <Chip label={`Job ${viewingRow.jobNumber}`} variant="outlined" />
                <Chip
                  label={workflowOptions[viewingRow.process]?.label || viewingRow.process || 'Workflow non défini'}
                  variant="outlined"
                />
                {getActiveNonConformitiesByType(viewingRow, 'NCA').length > 0 && (
                  <Chip
                    icon={<BuildCircleRoundedIcon />}
                    label={`${getActiveNonConformitiesByType(viewingRow, 'NCA').length} N.C.A atelier`}
                    color="warning"
                    variant="filled"
                  />
                )}
                {getActiveNonConformitiesByType(viewingRow, 'NCM').length > 0 && (
                  <Chip
                    icon={<StorefrontRoundedIcon />}
                    label={`${getActiveNonConformitiesByType(viewingRow, 'NCM').length} N.C.M magasin`}
                    color="error"
                    variant="filled"
                  />
                )}
                {hasMountingImpossibleAlert(viewingRow) && (
                  <Chip
                    icon={<WarningAmberRoundedIcon />}
                    label="Montage impossible"
                    color="error"
                    variant="filled"
                  />
                )}
                {hasTraceabilityReport(viewingRow) && (
                  <Chip
                    icon={<FactCheckRoundedIcon />}
                    label={`${viewingRow.traceabilityReports.length} Traçabilité`}
                    color="success"
                    variant="filled"
                  />
                )}
              </Stack>

              <Stack direction="row" spacing={1.25} flexWrap="wrap" sx={{ rowGap: 1.25 }}>
                {[
                  {
                    label: getFrameTypeLabel(viewingRow.frame?.type, viewingRow.process, viewingRow.dailyOrder),
                    icon: <TbEyeglass2 size={29} />,
                    accent: 'primary.main',
                  },
                  viewingRow.frame?.origin && {
                    label: getFrameOriginLabel(viewingRow.frame),
                    icon: viewingRow.frame.origin === 'stock' ? <StorefrontRoundedIcon /> : <PersonRoundedIcon />,
                    accent: 'secondary.main',
                  },
                  {
                    label: viewingRow.mountingLocation === 'store' ? 'Montage magasin' : 'Montage atelier',
                    icon: viewingRow.mountingLocation === 'store' ? <StorefrontRoundedIcon /> : <BuildCircleRoundedIcon />,
                    accent: viewingRow.mountingLocation === 'store' ? 'info.main' : 'success.main',
                  },
                ].filter(Boolean).map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      minWidth: 146,
                      minHeight: 76,
                      px: 1.5,
                      py: 1.25,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 0.5,
                      color: item.accent,
                      bgcolor: 'background.paper',
                    }}
                  >
                    {item.icon}
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 900 }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              {canActOnCommande(viewingRow) && (
                <Button
                  size="large"
                  variant="contained"
                  onClick={() => handleCompleteRowStep(viewingRow)}
                  disabled={saving}
                  sx={{ alignSelf: 'flex-start', px: 4, py: 1.4, fontWeight: 900 }}
                >
                  {getRowWorkflowActionLabel(viewingRow)}
                </Button>
              )}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.45fr) minmax(360px, 0.75fr)' },
                  gap: 3,
                  alignItems: 'start',
                }}
              >
                <Stack spacing={2.5}>
                  <Card variant="outlined">
                    <CardContent>
                      <CardSectionTitle icon={<PersonRoundedIcon />}>
                        Client
                      </CardSectionTitle>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                        {[
                          ['Nom', viewingRow.customerSnapshot?.fullName || getLabel(viewingRow.customerId)],
                          ['Conseiller', viewingRow.advisorSnapshot?.fullName || getLabel(viewingRow.advisorId)],
                          ['Numéro de job', viewingRow.jobNumber],
                          ['Fiche atelier', viewingRow.workshopSheetNumber || viewingRow.jobNumber],
                        ].map(([label, value]) => (
                          <Box key={label}>
                            <Typography variant="caption" color="text.secondary">
                              {label}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                              {value || '—'}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2.5 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <CardSectionTitle icon={<StorefrontRoundedIcon />}>
                          Magasin
                        </CardSectionTitle>
                        {[
                          ['Site', getSiteLabel(viewingRow.storeSiteId)],
                          ['Compte fournisseur', getStoreSupplierAccount(viewingRow)],
                          ['Monture envoyée atelier', viewingRow.frame?.sentToWorkshop ? 'Oui' : 'Non'],
                          ...(NON_STOCK_STORE_MOUNT_PROCESS_CODES.includes(viewingRow.process)
                            ? [['Taillage verres par AMOI', viewingRow.frame?.teleEdging ? 'Oui' : 'Non']]
                            : []),
                        ].map(([label, value]) => (
                          <Box key={label} sx={{ mb: 1.6 }}>
                            <Typography variant="caption" color="text.secondary">
                              {label}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                              {value || '—'}
                            </Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>

                    <Card variant="outlined">
                      <CardContent>
                        <CardSectionTitle icon={<BuildCircleRoundedIcon />} color="success.main">
                          Atelier
                        </CardSectionTitle>
                        {[
                          ['Site', getSiteLabel(viewingRow.workshopSiteId)],
                          ...(viewingRow.lens?.mode === 'stock'
                            ? [
                                ['Verre droit scanné', viewingRow.lens?.picking?.rightBarcode],
                                ['Verre gauche scanné', viewingRow.lens?.picking?.leftBarcode],
                                ['Picking réalisé le', formatDateTime(viewingRow.lens?.picking?.pickedAt)],
                              ]
                            : [['Type de montage', viewingRow.frame?.mountingType]]),
                        ].map(([label, value]) => (
                          <Box key={label} sx={{ mb: 1.6 }}>
                            <Typography variant="caption" color="text.secondary">
                              {label}
                            </Typography>
                            {label === 'Type de montage' ? (
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                  {value || '—'}
                                </Typography>
                                {viewingRow.frame?.mountingTypeHistory?.length > 0 && (
                                  <Tooltip title={`${viewingRow.frame.mountingTypeHistory.length} modification(s)`}>
                                    <HistoryRoundedIcon fontSize="small" color="warning" />
                                  </Tooltip>
                                )}
                                {canEditMountingType(viewingRow) && (
                                  <Tooltip title="Modifier le type de montage">
                                    <IconButton size="small" onClick={() => handleOpenMountingTypeEdit(viewingRow)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                {value || '—'}
                              </Typography>
                            )}
                          </Box>
                        ))}
                        {viewingRow.frame?.mountingTypeHistory?.length > 0 && (
                          <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                              Historique type de montage
                            </Typography>
                            <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                              {[...(viewingRow.frame.mountingTypeHistory || [])].reverse().map((entry) => (
                                <Box key={entry._id || `${entry.changedAt}-${entry.nextValue}`} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 850 }}>
                                    {entry.previousValue || '—'} → {entry.nextValue || '—'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDateTime(entry.changedAt)} · {getLabel(entry.changedSiteId) || getLabel(entry.changedBy) || '—'}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Box>

                  {viewingRow.lens?.mode === 'stock' && (
                    <Card variant="outlined">
                      <CardContent>
                        <CardSectionTitle icon={<Inventory2RoundedIcon />} color="info.main">
                          Articles
                        </CardSectionTitle>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                          {getStockLensSideLines(viewingRow).map((line, index) => {
                            const stockItem = line.stockItemId && typeof line.stockItemId === 'object' ? line.stockItemId : {};
                            const eyeLabel = line.side === 'right' ? 'OD — Œil droit' : line.side === 'left' ? 'OG — Œil gauche' : `Verre ${index + 1}`;
                            return (
                              <Box key={`${line.reservationId || getId(line.stockItemId) || index}-${line.side || 'lens'}`} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                  <LensBlurRoundedIcon color="info" />
                                  <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>{eyeLabel}</Typography>
                                </Stack>
                                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                  {getStockItemLabel(line.stockItemId) || [line.barcode, line.supplier, line.label].filter(Boolean).join(' - ') || '—'}
                                </Typography>
                                <Typography variant="h6" sx={{ mt: 1, fontWeight: 950 }}>
                                  {formatOpticalCorrection(stockItem.sphere ?? line.sphere, stockItem.cylinder ?? line.cylinder)}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  {(viewingRow.nonConformities || []).length > 0 && (
                    <Card variant="outlined" sx={{ borderColor: 'warning.main' }}>
                      <CardContent>
                        <CardSectionTitle icon={<WarningAmberIcon />} color="warning.main">
                          Non-conformités
                        </CardSectionTitle>
                        <Stack spacing={1.2}>
                          {viewingRow.nonConformities.map((item) => (
                            <Alert
                              key={item._id || `${item.scope}-${item.reason}-${item.declaredAt}`}
                              severity={item.status === 'cancelled' ? 'info' : 'warning'}
                            >
                              <Stack spacing={0.4}>
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                    {item.type || 'NCA'} • {getOptionLabelByValue(nonConformityReasonOptions, item.reason)} •{' '}
                                    {getOptionLabelByValue(nonConformityScopeOptions, item.scope)}
                                  </Typography>
                                  <Stack direction="row" spacing={0.75} alignItems="center">
                                    {siteType === 'store' && (item.type || 'NCA') === 'NCA' && item.status !== 'cancelled' && (
                                      <Button
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        onClick={() => {
                                          setCancelNonConformityTarget({ commandeId: viewingRow._id, item });
                                          setCancelNonConformityReason('');
                                        }}
                                        sx={{ fontWeight: 900, flex: '0 0 auto' }}
                                      >
                                        Annuler NCA
                                      </Button>
                                    )}
                                    {isAdmin && (
                                      <IconButton
                                        size="small"
                                        color="error"
                                        aria-label="Supprimer la non-conformité"
                                        onClick={() => handleDeleteEmbeddedCommandeItem('non-conformity', item)}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                  </Stack>
                                </Stack>
                                <Typography variant="caption">
                                  {item.declaredAt ? `Déclarée le ${formatDateTime(item.declaredAt)}` : 'Date non renseignée'}
                                  {item.declaredSiteId ? ` • ${getSiteLabel(item.declaredSiteId)}` : ''}
                                </Typography>
                                {item.status === 'cancelled' && (
                                  <Typography variant="caption" color="info.main" sx={{ fontWeight: 900 }}>
                                    Annulée le {formatDateTime(item.cancelledAt)}
                                    {item.cancelledSiteId ? ` • ${getSiteLabel(item.cancelledSiteId)}` : ''}
                                  </Typography>
                                )}
                                {item.comment && (
                                  <Typography variant="body2" color="text.secondary">
                                    {item.comment}
                                  </Typography>
                                )}
                                {item.cancellationReason && (
                                  <Typography variant="body2" color="text.secondary">
                                    Raison d'annulation : {item.cancellationReason}
                                  </Typography>
                                )}
                                {item._id && (
                                  <NonConformityPhotos
                                    commandeId={viewingRow._id}
                                    nonConformity={item}
                                    canAdd={item.status !== 'cancelled' && (isAdmin || ['store', 'workshop'].includes(siteType))}
                                    onUpdated={(updatedCommande) => {
                                      setRows((current) => current.map((row) => (row._id === updatedCommande._id ? updatedCommande : row)));
                                      setViewingRow(updatedCommande);
                                      setSnackbar({ open: true, severity: 'success', message: 'Photo(s) ajoutée(s) à la non-conformité.' });
                                    }}
                                    onError={(message) => setSnackbar({ open: true, severity: 'error', message })}
                                  />
                                )}
                              </Stack>
                            </Alert>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  )}

                  {((viewingRow.traceabilityReports || []).length > 0 || (viewingRow.workflowAlerts || []).some((item) => item.type === 'mounting_impossible')) && (
                    <Card variant="outlined" sx={{ borderColor: 'success.main' }}>
                      <CardContent>
                        <CardSectionTitle icon={<FactCheckRoundedIcon />} color="success.main">
                          Traçabilité
                        </CardSectionTitle>
                        <Stack spacing={1.5}>
                          {[...(viewingRow.workflowAlerts || [])]
                            .filter((item) => item.type === 'mounting_impossible')
                            .reverse()
                            .map((item) => (
                              <Alert key={item._id || `${item.type}-${item.declaredAt}`} severity="error" variant="outlined">
                                <Stack spacing={0.5}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                      Montage impossible
                                    </Typography>
                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                      <Typography variant="caption" color="text.secondary">
                                        {formatDateTime(item.declaredAt)}
                                        {item.declaredSiteId ? ` • ${getSiteLabel(item.declaredSiteId)}` : ''}
                                      </Typography>
                                      {isAdmin && (
                                        <IconButton
                                          size="small"
                                          color="error"
                                          aria-label="Supprimer l’alerte de workflow"
                                          onClick={() => handleDeleteWorkflowAlert(item)}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                    </Stack>
                                  </Stack>
                                  {item.comment && (
                                    <Typography variant="body2">
                                      {item.comment}
                                    </Typography>
                                  )}
                                </Stack>
                              </Alert>
                            ))}
                          {[...(viewingRow.traceabilityReports || [])].reverse().map((item) => {
                            const checkedLabels = Object.entries(traceabilityCheckLabels)
                              .filter(([key]) => item.checks?.[key])
                              .map(([, label]) => label);
                            return (
                              <Alert key={item._id || `${item.declaredAt}-${item.comment}`} severity="success" variant="outlined">
                                <Stack spacing={1}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                      Fiche Traçabilité
                                    </Typography>
                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                      <Typography variant="caption" color="text.secondary">
                                        {formatDateTime(item.declaredAt)}
                                        {item.declaredSiteId ? ` • ${getSiteLabel(item.declaredSiteId)}` : ''}
                                      </Typography>
                                      {isAdmin && (
                                        <IconButton
                                          size="small"
                                          color="error"
                                          aria-label="Supprimer la fiche de traçabilité"
                                          onClick={() => handleDeleteEmbeddedCommandeItem('traceability', item)}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                    </Stack>
                                  </Stack>
                                  {checkedLabels.length > 0 && (
                                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                      {checkedLabels.map((label) => (
                                        <Chip
                                          key={label}
                                          size="small"
                                          color={label === 'Montage impossible' ? 'error' : 'success'}
                                          variant="outlined"
                                          label={label}
                                        />
                                      ))}
                                    </Stack>
                                  )}
                                  {typeof item.mountingCompleted === 'boolean' && (
                                    <Typography variant="body2" sx={{ fontWeight: 850 }}>
                                      Montage réalisé : {item.mountingCompleted ? 'Oui' : 'Non'}
                                      {item.declaredBy ? ` • Technicien : ${getUserLabel(item.declaredBy)}` : ''}
                                    </Typography>
                                  )}
                                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                                        Écarts pupillaires
                                      </Typography>
                                      <Typography variant="body2">
                                        Cde D {item.pupillaryDifferences?.orderRight || '—'} / G {item.pupillaryDifferences?.orderLeft || '—'} · Réal. D {item.pupillaryDifferences?.realizationRight || '—'} / G {item.pupillaryDifferences?.realizationLeft || '—'}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                                        Hauteurs
                                      </Typography>
                                      <Typography variant="body2">
                                        Cde D {item.heights?.orderRight || '—'} / G {item.heights?.orderLeft || '—'} · Réal. D {item.heights?.realizationRight || '—'} / G {item.heights?.realizationLeft || '—'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  {(item.shapeProblem || item.lensFrameMismatchDetails?.material || item.lensFrameMismatchDetails?.base || item.lensFrameMismatchDetails?.thickness) && (
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                                          Problème de forme
                                        </Typography>
                                        <Typography variant="body2">{item.shapeProblem || '—'}</Typography>
                                      </Box>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                                          Inadéquation verres / monture
                                        </Typography>
                                        <Typography variant="body2">
                                          Matière {item.lensFrameMismatchDetails?.material || '—'} · Base {item.lensFrameMismatchDetails?.base || '—'} · Épaisseur {item.lensFrameMismatchDetails?.thickness || '—'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  )}
                                  {(item.otherComment || item.comment) && (
                                    <Typography variant="body2" color="text.secondary">
                                      {[item.otherComment, item.comment].filter(Boolean).join(' · ')}
                                    </Typography>
                                  )}
                                </Stack>
                              </Alert>
                            );
                          })}
                        </Stack>
                      </CardContent>
                    </Card>
                  )}

                  {viewingRow.missingInitialFile?.detectedAt && (
                    <Card variant="outlined" sx={{ borderColor: viewingRow.missingInitialFile.resolvedAt ? 'warning.main' : 'error.main' }}>
                      <CardContent>
                        <CardSectionTitle icon={<WarningAmberRoundedIcon />} color={viewingRow.missingInitialFile.resolvedAt ? 'warning.main' : 'error.main'}>
                          Fiche dossier initialement absente
                        </CardSectionTitle>
                        <Stack spacing={0.75}>
                          <Typography variant="body2">
                            Verre réceptionné à l’atelier le {formatDateTime(viewingRow.missingInitialFile.detectedAt)} sans fiche dossier associée.
                          </Typography>
                          <Typography variant="body2" color={viewingRow.missingInitialFile.resolvedAt ? 'success.main' : 'error.main'} sx={{ fontWeight: 800 }}>
                            {viewingRow.missingInitialFile.resolvedAt
                              ? `Dossier régularisé par le magasin le ${formatDateTime(viewingRow.missingInitialFile.resolvedAt)}.`
                              : 'Informations attendues du magasin.'}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}

                  {isAdmin && viewingTraceabilityMails.length > 0 && (
                    <Card variant="outlined" sx={{ borderColor: 'success.main' }}>
                      <CardContent>
                        <CardSectionTitle icon={<MailOutlineRoundedIcon />} color="success.main">
                          Mails de traçabilité
                        </CardSectionTitle>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          Destinataires et résultat de chaque envoi lors d’une déclaration de traçabilité.
                        </Typography>
                        <Stack spacing={1.2}>
                          {[...viewingTraceabilityMails].reverse().map((item) => (
                            <Alert
                              key={item._id || `${item.to}-${item.sentAt}-${item.subject}`}
                              severity={getMailStatusSeverity(item.status)}
                              variant="outlined"
                            >
                              <Stack spacing={0.35}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                    Traçabilité • {getMailStatusLabel(item.status)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDateTime(item.sentAt)}
                                  </Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                  À : {item.to || '—'}
                                  {item.triggeredSiteId ? ` • ${getSiteLabel(item.triggeredSiteId)}` : ''}
                                </Typography>
                                {item.messageId && (
                                  <Typography variant="caption" color="text.secondary">
                                    Identifiant SMTP : {item.messageId}
                                  </Typography>
                                )}
                                {item.subject && <Typography variant="body2">{item.subject}</Typography>}
                                {item.error && (
                                  <Typography variant="body2" color="error.main">
                                    {item.error}
                                  </Typography>
                                )}
                              </Stack>
                            </Alert>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  )}

                  {viewingOtherMails.length > 0 && (
                    <Card variant="outlined">
                      <CardContent>
                        <CardSectionTitle icon={<MailOutlineRoundedIcon />} color="info.main">
                          Mails envoyés
                        </CardSectionTitle>
                        <Stack spacing={1.2}>
                          {[...viewingOtherMails].reverse().map((item) => (
                            <Alert
                              key={item._id || `${item.to}-${item.sentAt}-${item.subject}`}
                              severity={getMailStatusSeverity(item.status)}
                            >
                              <Stack spacing={0.35}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                    {item.type || item.category || 'Mail'} • {getMailStatusLabel(item.status)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDateTime(item.sentAt)}
                                  </Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                  À : {item.to || '—'}
                                  {item.cc ? ` • Copie : ${item.cc}` : ''}
                                  {item.triggeredSiteId ? ` • ${getSiteLabel(item.triggeredSiteId)}` : ''}
                                </Typography>
                                {item.subject && (
                                  <Typography variant="body2">
                                    {item.subject}
                                  </Typography>
                                )}
                                {item.error && (
                                  <Typography variant="body2" color="error.main">
                                    {item.error}
                                  </Typography>
                                )}
                              </Stack>
                            </Alert>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  )}

                  <Card variant="outlined">
                    <CardContent>
                      <CardSectionTitle icon={<HistoryRoundedIcon />} color="secondary.main">
                        Journal des actions
                      </CardSectionTitle>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Historique complet du JOB, conservé même après une correction ou une remise à zéro du workflow.
                      </Typography>
                      <Stack spacing={1.1}>
                        {getCommandeActivityHistory(viewingRow).map((entry, index) => {
                          const actorUser = entry.performedByUserId && typeof entry.performedByUserId === 'object'
                            ? entry.performedByUserId
                            : users.find((candidate) => String(candidate._id) === String(entry.performedByUserId));
                          const actorSite = entry.performedBySiteId && typeof entry.performedBySiteId === 'object'
                            ? entry.performedBySiteId
                            : sites.find((candidate) => String(candidate._id) === String(entry.performedBySiteId));
                          const actorName = entry.performedByUserName || getUserLabel(actorUser) || 'Application';
                          const actorSiteLabel = getSiteLabel(actorSite);
                          const beforeAfter = entry.before || entry.after;
                          return (
                            <Box
                              key={entry._id || `${entry.action}-${entry.occurredAt}-${index}`}
                              sx={{
                                border: '1px solid',
                                borderColor: entry.metadata?.legacy ? 'divider' : 'secondary.light',
                                borderRadius: 1.5,
                                p: 1.25,
                                bgcolor: entry.metadata?.legacy ? 'transparent' : 'rgba(156, 39, 176, 0.035)',
                              }}
                            >
                              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                  {entry.label || entry.action || 'Action'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                  {formatDateTime(entry.occurredAt)}
                                </Typography>
                              </Stack>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {actorName}
                                {actorSiteLabel ? ` • ${actorSiteLabel}` : ''}
                                {entry.metadata?.legacy ? ' • historique existant' : ''}
                              </Typography>
                              {entry.detail && (
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  {entry.detail}
                                </Typography>
                              )}
                              {beforeAfter && (
                                <Box component="details" sx={{ mt: 0.75 }}>
                                  <Box component="summary" sx={{ cursor: 'pointer', color: 'secondary.main', fontSize: 12, fontWeight: 800 }}>
                                    Voir les valeurs avant / après
                                  </Box>
                                  <Typography component="pre" variant="caption" sx={{ whiteSpace: 'pre-wrap', maxHeight: 180, overflow: 'auto', mt: 0.75 }}>
                                    {JSON.stringify({ avant: entry.before || null, après: entry.after || null }, null, 2)}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                        {!getCommandeActivityHistory(viewingRow).length && (
                          <Typography variant="body2" color="text.secondary">
                            Aucune action historisée pour ce JOB.
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>

                </Stack>

                <Card
                  variant="outlined"
                  sx={{
                    position: { lg: 'sticky' },
                    top: { lg: 24 },
                    maxHeight: { lg: 'calc(100vh - 150px)' },
                    overflow: 'auto',
                  }}
                >
                  <CardContent>
                    <CardSectionTitle icon={<AccountTreeRoundedIcon />} color="secondary.main">
                      Workflow
                    </CardSectionTitle>
                    {(viewingRow.dispatchLists || []).length > 0 && (
                      <Stack spacing={1} sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900, textTransform: 'uppercase' }}>
                          Bons de livraison associés
                        </Typography>
                        {viewingRow.dispatchLists.map((dispatchList) => {
                          const directionLabel = dispatchList.direction === 'workshop_to_store'
                            ? 'Atelier → Magasin'
                            : 'Magasin → Atelier';
                          const statusLabel = {
                            draft: 'Brouillon', sent: 'Envoyé', pending: 'En attente de réception',
                            partial: 'Partiel', received: 'Réceptionné',
                          }[dispatchList.status] || dispatchList.status || '—';
                          const item = dispatchList.items?.[0];
                          return (
                            <Box key={dispatchList._id || dispatchList.listNumber} sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                <Typography variant="body2" sx={{ fontWeight: 900 }}>{dispatchList.listNumber || 'BL —'}</Typography>
                                <Chip size="small" label={statusLabel} color={dispatchList.status === 'received' ? 'success' : dispatchList.status === 'partial' ? 'warning' : 'default'} />
                              </Stack>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {directionLabel}
                                {dispatchList.createdAt ? ` • Créé le ${formatDateTime(dispatchList.createdAt)}` : ''}
                              </Typography>
                              {item?.sentAt && <Typography variant="caption" color="text.secondary" display="block">Envoyé le {formatDateTime(item.sentAt)}</Typography>}
                              {item?.receivedAt && <Typography variant="caption" color="text.secondary" display="block">Réceptionné le {formatDateTime(item.receivedAt)}</Typography>}
                              {item?.message && <Typography variant="caption" color="text.secondary" display="block">{item.message}</Typography>}
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                    <Stepper activeStep={normalizedViewingActiveStep} orientation="vertical">
                      {viewingWorkflowSteps.map((step, index) => {
                        const completed = Boolean(step.completedAt);
                        const isCurrent = index === normalizedViewingActiveStep;

                        return (
                          <Step key={step.code} completed={completed}>
                            <StepLabel
                              optional={
                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                                  <Box
                                    sx={{
                                      width: 18,
                                      height: 18,
                                      borderRadius: '50%',
                                      display: 'grid',
                                      placeItems: 'center',
                                      color: getOwnerIconColor(step.ownerType),
                                      bgcolor: 'rgba(100, 116, 139, 0.12)',
                                      fontSize: 12,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {getOwnerIcon(step.ownerType)}
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {getOwnerLabel(step.ownerType)}
                                    {completed ? ` • Bippé le ${formatDateTime(step.completedAt)}` : ''}
                                    {completed && step.completedBy
                                      ? (() => {
                                          const user = typeof step.completedBy === 'object'
                                            ? step.completedBy
                                            : users.find((candidate) => String(candidate._id) === String(step.completedBy));
                                          return user ? ` • ${getUserLabel(user)}` : '';
                                        })()
                                      : completed && step.completedSiteId
                                        ? (() => {
                                            const workflowSite = typeof step.completedSiteId === 'object'
                                              ? step.completedSiteId
                                              : sites.find((candidate) => String(candidate._id) === String(step.completedSiteId));
                                            return workflowSite ? ` • ${getSiteLabel(workflowSite)}` : '';
                                          })()
                                        : ''}
                                  </Typography>
                                </Stack>
                              }
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: isCurrent || completed ? 800 : 500 }}
                              >
                                {getStepLabel(step)}
                              </Typography>
                            </StepLabel>
                          </Step>
                        );
                      })}
                    </Stepper>
                    {!viewingWorkflowSteps.length && (
                      <Typography color="text.secondary">Aucune étape définie pour cette commande.</Typography>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={workflowCorrectionOpen}
        onClose={() => !workflowCorrectionSaving && setWorkflowCorrectionOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Corriger le workflow — JOB {viewingRow?.jobNumber}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              Décocher une étape efface automatiquement sa validation et celles de toutes les étapes suivantes. Cette correction ne modifie ni les stocks ni les bons de livraison.
            </Alert>
            {workflowCorrectionError && <Alert severity="error">{workflowCorrectionError}</Alert>}
            <Stack spacing={1}>
              {workflowCorrectionSteps.map((step, index) => {
                const siteOptions = sites.filter((candidate) => candidate.active !== false && candidate.type === step.ownerType);
                return (
                  <Paper key={step.code} variant="outlined" sx={{ p: 1.5 }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'minmax(210px, 1fr) minmax(210px, 0.8fr) minmax(210px, 0.9fr)' },
                        gap: 1.5,
                        alignItems: 'center',
                      }}
                    >
                      <FormControlLabel
                        control={(
                          <Checkbox
                            checked={step.completed}
                            onChange={(event) => handleWorkflowCorrectionToggle(index, event.target.checked)}
                            disabled={workflowCorrectionSaving}
                          />
                        )}
                        label={(
                          <Stack spacing={0.2}>
                            <Typography variant="body2" sx={{ fontWeight: 900 }}>{index + 1}. {step.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{getOwnerLabel(step.ownerType)}</Typography>
                          </Stack>
                        )}
                      />
                      <TextField
                        type="datetime-local"
                        label="Date et heure"
                        value={step.completedAt}
                        onChange={(event) => handleWorkflowCorrectionField(index, 'completedAt', event.target.value)}
                        disabled={!step.completed || workflowCorrectionSaving}
                        required={step.completed}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                      <TextField
                        select
                        label={step.ownerType === 'store' ? 'Magasin' : 'Atelier'}
                        value={step.completedSiteId}
                        onChange={(event) => handleWorkflowCorrectionField(index, 'completedSiteId', event.target.value)}
                        disabled={!step.completed || workflowCorrectionSaving}
                        required={step.completed}
                        fullWidth
                      >
                        {siteOptions.map((candidate) => (
                          <MenuItem key={candidate._id} value={candidate._id}>
                            {[candidate.code, getSiteLabel(candidate)].filter(Boolean).join(' - ')}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWorkflowCorrectionOpen(false)} disabled={workflowCorrectionSaving}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleSaveWorkflowCorrection}
            disabled={workflowCorrectionSaving || workflowCorrectionSteps.some((step) => step.completed && (!step.completedAt || !step.completedSiteId))}
          >
            {workflowCorrectionSaving ? 'Enregistrement...' : 'Enregistrer la correction'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={mountingTypeOpen} onClose={() => setMountingTypeOpen(false)} fullWidth maxWidth="xs">
        <Box
          component="form"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!mountingTypeOptions.includes(mountingTypeValue)) {
              setSnackbar({
                open: true,
                severity: 'error',
                message: 'Sélectionne un type de montage.',
              });
              return;
            }
            const row = mountingTypeRow;
            setMountingTypeOpen(false);
            setMountingTypeRow(null);
            if (row) {
              if (mountingTypeMode === 'edit') {
                try {
                  setSaving(true);
                  const updatedCommande = await requestJson(`/commandes/${row._id}/mounting-type`, {
                    method: 'PATCH',
                    body: JSON.stringify({ mountingType: mountingTypeValue.trim() }),
                  });
                  setRows((current) => current.map((item) => (item._id === row._id ? updatedCommande : item)));
                  if (viewingRow?._id === row._id) setViewingRow(updatedCommande);
                  setSnackbar({
                    open: true,
                    severity: 'success',
                    message: `Type de montage modifié pour le JOB ${updatedCommande.jobNumber}.`,
                  });
                } catch (err) {
                  setError(err.message);
                  setSnackbar({ open: true, severity: 'error', message: err.message });
                } finally {
                  setSaving(false);
                }
              } else {
                await completeRowStep(row, { mountingType: mountingTypeValue.trim() });
              }
            }
          }}
        >
          <DialogTitle>{mountingTypeMode === 'edit' ? 'Modifier le type de montage' : 'Préparer le retour magasin'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                {mountingTypeMode === 'edit'
                  ? 'La modification sera historisée avec l’ancienne et la nouvelle valeur.'
                  : 'Sélectionne le type de montage. Le dossier passera ensuite en attente de BL retour magasin.'}
              </Alert>
              <Autocomplete
                options={mountingTypeOptions}
                value={mountingTypeOptions.includes(mountingTypeValue) ? mountingTypeValue : null}
                onChange={(_event, value) => setMountingTypeValue(value || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Type de montage" required autoFocus />
                )}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMountingTypeOpen(false)}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {mountingTypeMode === 'edit' ? 'Enregistrer' : 'Mettre en attente BL'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={confirmDialog.open} onClose={closeConfirmDialog} fullWidth maxWidth="xs">
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1 }}>
            {confirmDialog.description}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog}>Annuler</Button>
          <Button
            variant="contained"
            color={confirmDialog.color}
            onClick={handleConfirmDialog}
            disabled={saving}
          >
            {confirmDialog.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        itemLabel={`la commande ${deleteTarget?.jobNumber || ''}`}
        description={`Confirmer la suppression de la commande ${
          deleteTarget?.jobNumber || deleteTarget?.workshopSheetNumber || ''
        } ? Le stock réservé sera libéré automatiquement.`}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => handleDelete(deleteTarget._id)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
